/* eslint-disable no-unused-vars -- delivered by the business team; unused helpers kept so later deliveries diff cleanly */
import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AspectRatio,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Link,
  List,
  ListIcon,
  ListItem,
  Modal,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  Tag,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiBarChart2,
  FiBook,
  FiCalendar,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiCamera,
  FiCheck,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiGlobe,
  FiGrid,
  FiImage,
  FiInfo,
  FiLink2,
  FiList,
  FiLock,
  FiMail,
  FiMapPin,
  FiMenu,
  FiMessageSquare,
  FiMic,
  FiMoon,
  FiPhone,
  FiCreditCard,
  FiEdit3,
  FiExternalLink,
  FiHelpCircle,
  FiPlay,
  FiX,
  FiSettings,
  FiShare2,
  FiShield,
  FiShoppingBag,
  FiSend,
  FiHeadphones,
  FiRepeat,
  FiRotateCcw,
  FiMaximize2,
  FiSearch,
  FiTarget,
  FiThumbsDown,
  FiThumbsUp,
  FiTool,
  FiTrendingUp,
  FiUser,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { landingContent } from './landingContent';
import { blogArticles } from './blogArticles';
import { readLang, writeLang } from './siteLang';
import { LangSwitch } from './LangSwitch';
import { allPosts, pageMeta, parsePath, pathFor } from './siteRoutes';
import { applyHead } from './siteHead';
import { submitInquiry, subscribeInsights } from './siteForms';
import VoiceTrialButton from './VoiceTrial';

const MotionBox = motion(Box);
const NORTH_AMERICA_MAP_SRC = `${process.env.PUBLIC_URL || ''}/north-america-coverage.png`;
const DEMO_URL = `${process.env.PUBLIC_URL || ''}/demo/index.html`;
const PARTNER_PAGE_URL = 'https://onesuitebusiness.com/zh/aiker-ai-assistant/';

// 占位视图的标题（中英）。导航中尚未开发的栏目，以及首页「尝试与我们 AI 来一场电话」
// 按钮（预约功能下架后暂落此处），都统一在页内切换到这里，Header 始终不卸载，语言等状态因此完整保留。
const PLACEHOLDER_META = {
  products: { en: 'Products', zh: '产品' },
  pricing: { en: 'Pricing', zh: '定价' },
  cases: { en: 'Call Demos', zh: '通话演示' },
  resources: { en: 'Resource Center', zh: '资源中心' },
  help: { en: 'Help Center', zh: '帮助中心' },
  blog: { en: 'Insights', zh: '资讯中心' },
  about: { en: 'About Us', zh: '关于我们' },
  contact: { en: 'Contact Us', zh: '联系我们' },
  'ai-call': { en: 'Try a live call with our AI', zh: '尝试与我们 AI 来一场电话' },
  agreement: { en: 'Customer Agreement', zh: '客户协议' },
};


const SMS_ICONS = { shoppingBag: FiShoppingBag, tool: FiTool };
// 产品界面改成 01/02/03 标签切换后，图标不再出现在页面上；保留映射以便恢复旧版卡片布局
const INTERFACE_ICONS = { list: FiList, fileText: FiFileText, message: FiMessageSquare, link: FiLink2, clipboard: FiClipboard, book: FiBook, check: FiCheckCircle };
const SCENARIO_ICONS = { settings: FiSettings, shoppingBag: FiShoppingBag, tool: FiTool };
// 上线指南图标：既给「五步流程」用，也给「费用 / 边界」两张卡用
const GUIDE_ICONS = {
  rocket: FiZap,
  user: FiUser,
  card: FiCreditCard,
  mic: FiMic,
  check: FiCheckCircle,
  dollar: FiDollarSign,
  shield: FiShield,
  lock: FiLock,
  chart: FiBarChart2,
  calendar: FiCalendar,
};
// 帮助中心主题图标（数据里只存字符串 key，渲染时在这里查表）
const HELP_ICONS = {
  rocket: FiZap,
  user: FiUser,
  card: FiCreditCard,
  mic: FiMic,
  check: FiCheckCircle,
  refund: FiRotateCcw,
  phone: FiPhone,
  message: FiMessageSquare,
  tool: FiTool,
  mail: FiMail,
  // 下面四个给「开通五步」与「服务边界」用：官方 SOP 里的图标名与上面那套不同
  dollar: FiDollarSign,
  shield: FiShield,
  lock: FiLock,
  chart: FiBarChart2,
};
// 联系页「也可以直接找我们」三个渠道的图标
const CHANNEL_ICONS = { mail: FiMail, phone: FiPhone, pin: FiMapPin };
const ABOUT_ICONS = { mic: FiMic, doc: FiFileText, shield: FiShield, clock: FiClock, users: FiUsers, phone: FiPhone };
// 当前未使用：产品页的痛点四卡已下架，恢复时配合 products.painPoints 一起接回
const PRODUCT_PAIN_ICONS = { moon: FiMoon, repeat: FiRepeat, clipboard: FiClipboard, globe: FiGlobe };
const PRODUCT_FLOW_ICONS = { phone: FiPhone, headphones: FiHeadphones, message: FiMessageSquare, send: FiSend, clipboard: FiClipboard };

/* 产品页：一组「✓ 文案」清单。label 传 null 时不渲染小标题（深色对照卡就是这种） */
const ProductCheckList = ({ label, items, mt = 6, onDark = false }) => (
  <Box mt={mt}>
    {label && (
      <Text fontSize="sm" fontWeight="bold" color={onDark ? 'white' : 'gray.900'} mb={3}>
        {label}
      </Text>
    )}
    <VStack align="stretch" spacing={2.5}>
      {items.map((f) => (
        <HStack key={f} align="flex-start" spacing={2.5}>
          <Icon as={FiCheck} color={onDark ? 'amber.400' : 'blue.500'} boxSize={4} mt="3px" flexShrink={0} />
          <Text fontSize="sm" color={onDark ? 'whiteAlpha.900' : 'gray.800'} lineHeight={1.7}>
            {f}
          </Text>
        </HStack>
      ))}
    </VStack>
  </Box>
);

/* 产品页：后台界面小样。模板里每张界面卡都挂一个假浏览器窗口，
   三种形态：table（表格）/ fields（字段）/ chat（对话气泡）。
   ⚠️ 2026-10-19 起**没有调用点**：产品页「产品界面」已换成三张后台真实截图（见 renderInterface）。
   组件与样式保留，要回到示意小样版本，把 items 里的 mock 数据接回来即可。 */
const MockWindow = ({ mock }) => {
  if (!mock) return null;
  const cols = mock.kind === 'table' ? mock.columns.length : 0;
  return (
    <Box mt={5} rounded="xl" overflow="hidden" border="1px solid" borderColor="gray.300" bg="white">
      <Flex align="center" gap={2.5} px={3} py={2} bg="gray.200" borderBottom="1px solid" borderColor="gray.300">
        <HStack spacing="3px" flexShrink={0}>
          {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
            <Box key={c} w="6px" h="6px" rounded="full" bg={c} />
          ))}
        </HStack>
        <Text fontSize="10px" color="gray.700" fontFamily="monospace" noOfLines={1}>
          {mock.url}
        </Text>
      </Flex>

      <Box p={3.5}>
        {mock.kind === 'table' && (
          <Box>
            <Grid
              templateColumns={`repeat(${cols}, minmax(0, 1fr))`}
              gap={2}
              pb={2}
              borderBottom="1px solid"
              borderColor="gray.300"
            >
              {mock.columns.map((col) => (
                <Text key={col} fontSize="10px" fontWeight="bold" color="gray.700" noOfLines={1}>
                  {col}
                </Text>
              ))}
            </Grid>
            {mock.rows.map((row, ri) => (
              <Grid
                key={ri}
                templateColumns={`repeat(${cols}, minmax(0, 1fr))`}
                gap={2}
                py={2.5}
                borderBottom={ri === mock.rows.length - 1 ? 'none' : '1px solid'}
                borderColor="gray.200"
              >
                {row.map((cell, ci) => (
                  <Text
                    key={ci}
                    fontSize="10px"
                    color={ci === cols - 1 ? 'blue.600' : 'gray.800'}
                    fontWeight={ci === cols - 1 ? 'semibold' : 'normal'}
                    noOfLines={1}
                  >
                    {cell}
                  </Text>
                ))}
              </Grid>
            ))}
          </Box>
        )}

        {mock.kind === 'fields' && (
          <VStack align="stretch" spacing={0}>
            {mock.fields.map((f, i) => (
              <Flex
                key={f.label}
                align="flex-start"
                gap={3}
                py={2.5}
                borderTop={i === 0 ? 'none' : '1px solid'}
                borderColor="gray.200"
              >
                <Text fontSize="10px" fontWeight="bold" color="gray.700" w="68px" flexShrink={0} noOfLines={1}>
                  {f.label}
                </Text>
                <Text fontSize="10px" color="gray.800" lineHeight={1.65}>
                  {f.value}
                </Text>
              </Flex>
            ))}
          </VStack>
        )}

        {mock.kind === 'chat' && (
          <VStack align="stretch" spacing={2}>
            {mock.rows.map((m, i) => (
              <Flex key={i} justify={m.side === 'out' ? 'flex-end' : 'flex-start'}>
                <Box
                  maxW="82%"
                  px={2.5}
                  py={1.5}
                  rounded="lg"
                  fontSize="10px"
                  lineHeight={1.65}
                  bg={m.side === 'out' ? 'brand.navy' : 'gray.200'}
                  color={m.side === 'out' ? 'white' : 'gray.800'}
                >
                  {m.text}
                </Box>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

const DIM_KEYS = ['fit', 'core', 'prep', 'mustKnow', 'notFit'];

/* ---------------- 产品页 / 关于我们页共用的「柔和」卡片语言 ----------------
   原来这两页到处是「高饱和实心圆 + 重阴影」，看久了累。
   这里统一成「淡色底 + 主题色图标 + 细边框 + 轻阴影」，
   并统一圆角与悬停反馈。只动视觉，不动任何文案与结构。 */
const SOFT_SECTION_PY = { base: 14, md: 20 };
const SOFT_CARD = {
  bg: 'white',
  rounded: '2xl',
  border: '1px solid',
  borderColor: 'gray.200',
  boxShadow: '0 1px 2px rgba(11,61,92,0.04)',
  sx: { transition: 'box-shadow 0.28s ease, border-color 0.28s ease' },
  _hover: { borderColor: 'blue.200', boxShadow: '0 16px 34px rgba(11,61,92,0.08)' },
};
/* 首页「核心价值」四张卡专用：比 SOFT_CARD 更轻。
   SOFT_CARD 是产品页 / 关于我们页 / 定价页共用的（13 处），不能动它。
   这一屏的卡片落在 hero 渐变带最下方，那里渐变已经收到接近纯白
   （实测卡片所在处背景是 rgb(253,253,254)，左下角还压着一点琥珀色光晕）。
   白底 + 1px 边框 + 投影三层叠在近白背景上，正是「四个方块太重」的来源，
   而单纯去掉边框又会让白卡在近白底上失去边缘（实测只剩约 3% 对比度）。
   所以改成「淡色填充 + 无边框 + 无投影」：靠色块本身立住，不靠边框和投影。
   配套把图标块从淡蓝改成白色 —— 淡蓝块在淡蓝底上会糊在一起。
   悬停时才浮起一层投影，静止态保持完全扁平。 */
const VALUE_CARD = {
  ...SOFT_CARD,
  bg: 'gray.200',
  border: '0',
  boxShadow: 'none',
  _hover: {},
};

// 柔和图标底：淡色方块 + 主题色图标（替代原来一圈实心圆）
// 尺寸按「刻度号 × 4px」换算成像素，不直接用刻度号——
// 本项目的 space 刻度是自定义的，缺 11 / 13 / 15 / 17 / 18 / 19 / 21 / 22 / 23，
// 写 w: 11 不会报错，但会静默退化成 11px（标准 Chakra 里 space[11] 是 44px），图标就溢出了。
const softIconBox = (tone = 'blue', size = 12) => ({
  w: `${size * 4}px`,
  h: `${size * 4}px`,
  rounded: 'xl',
  bg: `${tone}.50`,
  color: `${tone}.600`,
  align: 'center',
  justify: 'center',
  flexShrink: 0,
});

/* ---------------- shared bits ---------------- */

const SectionHeader = ({ eyebrow, title, subtitle, align = 'center', eyebrowStyle = 'badge' }) => {
  const textAlign = align === 'center' ? 'center' : 'left';
  const alignItems = align === 'center' ? 'center' : 'flex-start';
  return (
    <Stack spacing={4} textAlign={textAlign} align={alignItems}>
      {eyebrow &&
        (eyebrowStyle === 'text' ? (
          // 产品页模板用的是橙色小字眉标，不是胶囊 badge
          <Text fontSize="sm" fontWeight="bold" color="amber.600" letterSpacing="0.14em" textTransform="uppercase">
            {eyebrow}
          </Text>
        ) : (
          <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" fontSize="sm">
            {eyebrow}
          </Badge>
        ))}
      <Heading fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }} fontWeight="bold" color="gray.900" lineHeight={1.18}>
        {title}
      </Heading>
      {subtitle && (
        <Text fontSize="md" color="gray.800" maxW="3xl" lineHeight={1.8}>
          {subtitle}
        </Text>
      )}
    </Stack>
  );
};

const NumberedSteps = ({ steps, accent = 'blue' }) => (
  <VStack align="stretch" spacing={3}>
    {steps.map((step, i) => (
      <HStack key={step} align="flex-start">
        <Flex
          w={6}
          h={6}
          flexShrink={0}
          rounded="full"
          align="center"
          justify="center"
          fontSize="xs"
          fontWeight="bold"
          bg={`${accent}.500`}
          color="white"
        >
          {i + 1}
        </Flex>
        <Text fontSize="sm" lineHeight={1.6} color="gray.800">
          {step}
        </Text>
      </HStack>
    ))}
  </VStack>
);

/* 「说明与边界」折叠条。
   原来这两处是直接铺 2 大段灰字，字多、观感沉、还挡在正文后面。
   现在收成一条细条：默认只露一行标题，点开才看完整内容 —— 信息一条不少。 */
// 说明条：默认折叠，点标题展开。
//   items  = 平铺列表（旧用法）
//   groups = 分组列表，每组带小标题；与 items 二选一。
// 产品页底部的「服务范围与能力边界」用分组形态——原来散在页面上的三处折叠条
// 标题重复（两处都叫「说明与边界」）且内容互相重叠，已合并成这一处。
const NoteDisclosure = ({ label, items, groups, tone = 'blue', mt = 12 }) => {
  const [open, setOpen] = useState(false);
  const flat = groups ? groups.reduce((a, g) => a.concat(g.items), []) : items || [];
  if (!flat.length) return null;
  const row = (n, i) => (
    <HStack key={n} align="flex-start" spacing={3}>
      <Text fontSize="xs" fontWeight="bold" color={`${tone}.600`} mt="4px" flexShrink={0} letterSpacing="0.08em">
        {String(i + 1).padStart(2, '0')}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight={1.85}>
        {n}
      </Text>
    </HStack>
  );
  return (
    <Box mt={mt} maxW="5xl" mx="auto">
      <Box
        as="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        w="full"
        display="flex"
        alignItems="center"
        gap={3}
        px={{ base: 4, md: 5 }}
        py={3.5}
        rounded="2xl"
        textAlign="left"
        bg={open ? `${tone}.50` : 'gray.100'}
        border="1px solid"
        borderColor={open ? `${tone}.100` : 'transparent'}
        sx={{ transition: 'background 0.2s ease, border-color 0.2s ease' }}
        _hover={{ bg: `${tone}.50` }}
        _focusVisible={{ outline: 'none', boxShadow: '0 0 0 3px rgba(41,171,226,0.35)' }}
      >
        {/* 主题的 space/sizes 刻度里没有 4.5，写 boxSize={4.5} 会退化成 4.5px，必须用 px */}
        <Icon as={FiInfo} boxSize="18px" color={`${tone}.600`} flexShrink={0} />
        <Text flex="1" fontSize="sm" fontWeight="semibold" color="gray.800">
          {label}
        </Text>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          {flat.length}
        </Text>
        <Icon
          as={FiChevronDown}
          boxSize={4}
          color="gray.500"
          flexShrink={0}
          transform={open ? 'rotate(180deg)' : 'none'}
          sx={{ transition: 'transform 0.2s ease' }}
        />
      </Box>

      {open &&
        (groups ? (
          <VStack align="stretch" spacing={6} mt={5} px={{ base: 1, md: 2 }}>
            {groups.map((g, gi) => {
              const offset = groups.slice(0, gi).reduce((a, x) => a + x.items.length, 0);
              return (
                <Box key={g.label}>
                  <Text fontSize="xs" fontWeight="bold" color={`${tone}.600`} letterSpacing="wider" mb={3}>
                    {g.label}
                  </Text>
                  <VStack align="stretch" spacing={3.5}>
                    {g.items.map((n, i) => row(n, offset + i))}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        ) : (
          <VStack align="stretch" spacing={3.5} mt={4} px={{ base: 1, md: 2 }}>
            {flat.map(row)}
          </VStack>
        ))}
    </Box>
  );
};



/* ---------------- demo player row ---------------- */

// Pre-computed pseudo-random waveform heights (deterministic) so the player
// looks alive. 波形条本身再用 CSS 做起伏（见 index.css 的 .wave-live）。
const WAVE_HEIGHTS = [
  [42, 58, 34, 70, 52, 38, 64, 48, 72, 44, 60, 36, 68, 50, 42, 58, 70, 46, 38, 64, 52, 40, 72, 56, 44, 62, 36, 70, 48, 54],
  [54, 38, 66, 46, 60, 34, 72, 50, 40, 68, 44, 58, 36, 70, 48, 56, 42, 64, 38, 72, 50, 44, 60, 34, 66, 48, 58, 40, 70, 46],
  [36, 64, 48, 70, 42, 58, 38, 72, 46, 54, 40, 68, 50, 44, 62, 36, 70, 48, 56, 42, 64, 38, 72, 50, 40, 66, 46, 58, 34, 68],
  [60, 42, 72, 38, 56, 44, 68, 36, 64, 48, 50, 70, 42, 58, 36, 72, 46, 54, 40, 68, 50, 44, 62, 38, 70, 48, 56, 42, 64, 36],
];

const DemoPlayerRow = ({ item, index, c, onPlay }) => {
  const wave = WAVE_HEIGHTS[index % WAVE_HEIGHTS.length];
  // 演示进度：前 35% 已播放（深色），其余为未播放（浅色）
  const played = Math.floor(wave.length * 0.35);
  return (
    <MotionBox
      as="button"
      type="button"
      onClick={onPlay}
      textAlign="left"
      w="full"
      bg="white"
      border="1px solid"
      borderColor="blue.100"
      rounded="2xl"
      boxShadow="sm"
      overflow="hidden"
      cursor="pointer"
      position="relative"
      sx={{ transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease' }}
      _hover={{ boxShadow: 'lg', borderColor: 'blue.300', transform: 'translateY(-2px)' }}
      _focusVisible={{ outline: 'none', boxShadow: '0 0 0 3px rgba(41,171,226,0.35)' }}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.32, delay: index * 0.06 }}
    >
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align="stretch"
        gap={{ base: 4, md: 0 }}
      >
        {/* Left: number + title + category */}
        <Flex
          flex={{ base: 'none', md: '0 0 280px' }}
          direction="column"
          justify="center"
          p={{ base: 5, md: 6 }}
          borderRight={{ base: 'none', md: '1px solid' }}
          borderBottom={{ base: '1px solid', md: 'none' }}
          borderColor="gray.100"
          position="relative"
        >
          <Box position="absolute" top={0} left={0} bottom={0} w="4px" bg={item.accent} display={{ base: 'none', md: 'block' }} />
          <HStack spacing={2} mb={2}>
            <Text fontSize="xs" fontWeight="bold" color={item.accent} textTransform="uppercase" letterSpacing="0.1em">
              {c.demo.caseLabel} {String(index + 1).padStart(2, '0')}
            </Text>
          </HStack>
          <Heading as="h3" fontSize={{ base: 'lg', md: 'xl' }} lineHeight={1.3} color="gray.900" mb={1.5}>
            {item.title}
          </Heading>
          <Text fontSize="xs" color="gray.600" lineHeight={1.5} noOfLines={2}>
            {item.category}
          </Text>
        </Flex>

        {/* Middle: waveform + meta */}
        <Flex flex="1" direction="column" justify="center" p={{ base: 4, md: 6 }} minW={0}>
          <Flex align="center" gap={1.5} h="44px" mb={2}>
            {wave.map((h, i) => (
              <Box
                key={i}
                className="wave-live-bar"
                w="3px"
                flex="1"
                maxW="4px"
                bg={i < played ? item.accent : 'gray.200'}
                rounded="full"
                h={`${h}%`}
                sx={{
                  transition: 'background-color 0.2s ease',
                  // 错开延迟，形成从左到右的波峰，而不是整排一起闪
                  animationDelay: `${(i % 10) * 0.09}s`,
                }}
              />
            ))}
          </Flex>
          <Flex align="center" justify="space-between" fontSize="xs" color="gray.500">
            <HStack spacing={2}>
              <Icon as={FiClock} boxSize={3} />
              <Text>{c.demo.duration}</Text>
            </HStack>
            <Text fontWeight="medium" color={item.accent}>{c.demo.playCase} →</Text>
          </Flex>
        </Flex>

        {/* Right: big circular play button */}
        <Flex
          flex={{ base: 'none', md: '0 0 140px' }}
          align="center"
          justify="center"
          p={{ base: 4, md: 5 }}
          bgGradient={{ base: 'none', md: `linear(to-br, ${item.accent.split('.')[0]}.50, ${item.accent.split('.')[0]}.600)` }}
          color="white"
          position="relative"
        >
          {/* outer pulse ring */}
          <Box
            position="absolute"
            w="72px"
            h="72px"
            rounded="full"
            border="2px solid"
            borderColor="whiteAlpha.700"
            opacity={0.45}
            sx={{ animation: 'pulseRing 2.4s ease-out infinite' }}
          />
          <Flex
            w="56px"
            h="56px"
            rounded="full"
            bg="white"
            color={item.accent}
            align="center"
            justify="center"
            boxShadow="lg"
            className="play-btn-inner"
            sx={{ transition: 'transform 0.2s ease' }}
          >
            <Icon as={FiPlay} boxSize={5} ml={0.5} />
          </Flex>
        </Flex>
      </Flex>
    </MotionBox>
  );
};

/* ---------------- product column ---------------- */

const ProductColumn = ({ card, dims, onDemo, onBook }) => {
  const isAI = card.kind === 'ai';
  return (
    <MotionBox
      bg="white"
      rounded="2xl"
      border="1px solid"
      borderColor={isAI ? 'blue.400' : 'gray.300'}
      boxShadow={isAI ? 'lg' : 'base'}
      overflow="hidden"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -6, boxShadow: '0 24px 48px rgba(11,61,92,0.1)' }}
      _hover={{ borderColor: isAI ? 'blue.500' : 'gray.400' }}
    >
      {/* Header */}
      <Box p={7} bgGradient={isAI ? 'linear(to-r, blue.600, blue.800)' : 'none'} bg={isAI ? undefined : 'blue.50'}>
        <Flex justify="space-between" align="flex-start" gap={4}>
          <Box>
            <Heading fontSize="xl" color={isAI ? 'white' : 'gray.900'}>
              {card.name}
            </Heading>
            <Text mt={1.5} fontSize="sm" color={isAI ? 'whiteAlpha.900' : 'gray.700'} lineHeight={1.6}>
              {card.tagline}
            </Text>
          </Box>
          {isAI && (
            <Tag colorScheme="amber" variant="solid" rounded="full" fontWeight="bold" flexShrink={0}>
              AI
            </Tag>
          )}
        </Flex>
      </Box>

      {/* Dimensions */}
      <Stack divider={<Divider borderColor="gray.200" />} spacing={0}>
        {DIM_KEYS.map((key) => (
          <Flex key={key} gap={4} px={7} py={4} align="flex-start">
            <Text flex="0 0 88px" fontSize="xs" fontWeight="bold" color={key === 'notFit' ? 'amber.700' : 'blue.600'} pt="2px">
              {dims[key]}
            </Text>
            <Text fontSize="sm" color="gray.800" lineHeight={1.7}>
              {card[key]}
            </Text>
          </Flex>
        ))}
      </Stack>

      {/* Actions */}
      <Stack p={7} pt={5} spacing={3}>
        {isAI ? (
          <>
            <Button variant="primary" rightIcon={<FiPlay />} onClick={onDemo}>
              {card.cta}
            </Button>
            <Button variant="outline" rightIcon={<FiArrowRight />} onClick={onBook}>
              {card.ctaAlt}
            </Button>
          </>
        ) : (
          <Button variant="outline" rightIcon={<FiArrowRight />} onClick={onBook}>
            {card.cta}
          </Button>
        )}
      </Stack>
    </MotionBox>
  );
};

/* ---------------- demo modal ---------------- */

const DemoModal = ({ isOpen, onClose, title, hint, caseId, lang }) => {
  const src = `${DEMO_URL}?case=${encodeURIComponent(caseId)}&lang=${encodeURIComponent(lang)}`;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="white"
        borderRadius="2xl"
        overflow="hidden"
        mx={4}
        w={{ base: '100%', md: '92vw' }}
        maxW="1200px"
        maxH="92vh"
        display="flex"
        flexDirection="column"
      >
        {/* 顶部条：保留品牌与上下文，关闭按钮就在手边（不再漂到屏幕最右上角） */}
        <Flex
          align="center"
          justify="space-between"
          gap={4}
          px={{ base: 4, md: 5 }}
          py={3}
          bg="#0B3D5C"
          color="white"
          flexShrink={0}
        >
          <HStack spacing={3} minW={0}>
            <Flex w="30px" h="30px" rounded="lg" bg="whiteAlpha.200" align="center" justify="center" flexShrink={0}>
              <Icon as={FiPlay} boxSize="14px" />
            </Flex>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="bold" noOfLines={1}>{title}</Text>
              <Text fontSize="xs" color="blue.100" noOfLines={1}>{hint}</Text>
            </Box>
          </HStack>
          <HStack spacing={1} flexShrink={0}>
            <IconButton
              as="a"
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open demo in a new tab"
              icon={<Icon as={FiExternalLink} />}
              size="sm"
              variant="ghost"
              color="blue.100"
              _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
            />
            <IconButton
              aria-label="Close"
              icon={<Icon as={FiX} />}
              size="sm"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              onClick={onClose}
            />
          </HStack>
        </Flex>

        {/* iframe 只在打开时才挂载：避免首屏就加载整个 demo 页 */}
        <Box flex="1" minH={0} bg="gray.100">
          {isOpen && (
            <iframe
              title={title}
              src={src}
              style={{ width: '100%', height: 'calc(92vh - 58px)', border: 'none', display: 'block' }}
              allow="autoplay"
            />
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

/* ================= page ================= */

// ============ Products view ============
/* 产品页的后台截图：点击后打开灯箱，可按原始像素查看。
   背景：这三张截图原始宽度 2384–2461px，页面正文列只有 1246px，
   缩到 0.52 倍后中文从 10px 变成 5px，字会糊成一片。
   光靠「提高图片分辨率」没用（源图已经很清晰），只能改显示方式：
   ① 截图带比正文列更宽（见下方 #interface 的 1600px 容器）；
   ② 点开灯箱，在 96vw 的窗口里按适应屏幕或原始尺寸看。 */
const ZoomShot = ({ src, alt, caption, zh, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const [actual, setActual] = useState(false);
  const close = () => {
    setOpen(false);
    setActual(false);
    if (onOpenChange) onOpenChange(false);
  };
  const openZoom = () => {
    setOpen(true);
    // 灯箱打开时通知父级暂停自动轮播，否则背后那张图会在遮罩下面偷偷换掉
    if (onOpenChange) onOpenChange(true);
  };
  return (
    <>
      <Box
        position="relative"
        rounded="2xl"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        boxShadow="0 26px 60px -34px rgba(11,61,92,0.45)"
        cursor="zoom-in"
        role="button"
        tabIndex={0}
        aria-label={zh ? '放大查看截图' : 'Enlarge screenshot'}
        onClick={openZoom}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openZoom();
          }
        }}
        _hover={{ '& .shot-badge': { opacity: 1 } }}
      >
        <Image src={src} alt={alt} w="100%" display="block" loading="lazy" decoding="async" />
        <HStack
          className="shot-badge"
          position="absolute"
          bottom={4}
          right={4}
          bg="rgba(11,61,92,0.9)"
          color="white"
          px={3.5}
          py={2}
          rounded="full"
          spacing={2}
          fontSize="xs"
          fontWeight="semibold"
          opacity={{ base: 1, md: 0 }}
          transition="opacity .2s"
          pointerEvents="none"
        >
          <Icon as={FiMaximize2} boxSize={3.5} />
          <Text>{zh ? '放大查看' : 'Enlarge'}</Text>
        </HStack>
      </Box>

      <Modal isOpen={open} onClose={close} size="full" motionPreset="fadeIn">
        <ModalOverlay bg="rgba(6,18,28,0.96)" sx={{ backdropFilter: 'blur(6px)' }} />
        <ModalContent
          bg="transparent"
          boxShadow="none"
          maxW="100vw"
          maxH="100vh"
          p={{ base: 4, md: 8 }}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <ModalCloseButton
            color="white"
            top={{ base: 3, md: 5 }}
            right={{ base: 3, md: 5 }}
            bg="whiteAlpha.200"
            rounded="full"
            _hover={{ bg: 'whiteAlpha.300' }}
          />
          <Box
            overflow="auto"
            maxH="82vh"
            w="100%"
            textAlign="center"
            cursor={actual ? 'zoom-out' : 'zoom-in'}
            onClick={() => setActual((v) => !v)}
            sx={{
              // 原尺寸下图片会比窗口宽，把滚动条画出来，否则看不出右边还有内容
              '&::-webkit-scrollbar': { height: '10px', width: '10px' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.45)', borderRadius: '999px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
            }}
          >
            <Image
              src={src}
              alt={alt}
              display="block"
              mx={actual ? 0 : 'auto'}
              w="auto"
              maxW={actual ? 'none' : '100%'}
              maxH={actual ? 'none' : '82vh'}
            />
          </Box>
          <HStack justify="center" spacing={3} mt={5} flexWrap="wrap">
            <Text color="whiteAlpha.800" fontSize="sm">
              {caption}
            </Text>
            {/* 用主题里的 onDark 变体：outline 变体自带白底，放在深色遮罩上会变成白底白字 */}
            <Button size="sm" variant="onDark" onClick={() => setActual((v) => !v)}>
              {actual ? (zh ? '适应屏幕' : 'Fit to screen') : zh ? '查看原尺寸' : 'Actual size'}
            </Button>
          </HStack>
        </ModalContent>
      </Modal>
    </>
  );
};

// 短信链接卡片顶部「通话中」状态条里的声波：5 根细条循环起伏，
// 一眼看出这是一通正在进行的语音电话（不是网页聊天）。
const WaveBars = () => (
  <HStack spacing="2px" align="flex-end" h="14px" aria-hidden>
    {[0, 1, 2, 3, 4].map((b) => (
      <motion.span
        key={b}
        style={{
          display: 'inline-block',
          width: 2,
          height: 14,
          background: 'rgba(255,255,255,0.85)',
          borderRadius: 2,
          transformOrigin: 'bottom',
        }}
        animate={{ scaleY: [0.35, 1, 0.55, 0.9, 0.4] }}
        transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: b * 0.12 }}
      />
    ))}
  </HStack>
);

// AI「正在回复」的三个跳动点
const TypingDots = () => (
  <HStack spacing="4px" h="18px" align="center" aria-hidden>
    {[0, 1, 2].map((d) => (
      <motion.span
        key={d}
        style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '9999px', background: 'rgba(255,255,255,0.9)' }}
        animate={{ opacity: [0.35, 1, 0.35], y: [0, -2, 0] }}
        transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: d * 0.15 }}
      />
    ))}
  </HStack>
);

/* 短信链接的对话卡：模拟「一通电话里真实发生的一来一回」。
   逐条显示，AI 回复前先亮打字指示器；全部显示完停 2.6s 后从头重播 ——
   用户要的是「不是滑完就没了」，所以这里做成循环，而不是 whileInView 只播一次。
   暂停条件：鼠标悬停、滚出视口、系统开了「减少动态效果」（后者直接全显不播）。
   布局：AI 在左（品牌深蓝），客户在右（浅灰）。 */
const SmsChatCard = ({ item, zh, index }) => {
  const total = item.chat.length;
  const [step, setStep] = useState(0);        // 已显示的气泡条数
  const [typing, setTyping] = useState(false); // 当前这条是不是「AI 正在回复」
  const [paused, setPaused] = useState(false); // 鼠标悬停
  const [inView, setInView] = useState(false);
  const [reduce, setReduce] = useState(false);
  const [scrolled, setScrolled] = useState(false); // 内容是否已经溢出（决定顶部渐隐要不要出现）
  const [fadingOut, setFadingOut] = useState(false); // 一轮播完后先淡出，再从头开始
  const ref = useRef(null);

  // prefers-reduced-motion：直接全显，不做逐条动画
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduce(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  // 滚出视口就暂停，滚回来继续 —— 避免用户在别处读内容时对话已经播完
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // 逐条推进的状态机
  useEffect(() => {
    if (reduce) { setStep(total); setTyping(false); return undefined; }
    if (paused || !inView) return undefined;

    // 全部播完就停在终态，不再从头循环。刷新页面（或切语言）会从第 1 条重新开始。
    if (step >= total) return undefined;

    const next = item.chat[step];
    // 下一条是 AI：先留 0.75s 让上一条「读完」，再亮打字点
    if (next.side === 'out' && !typing) {
      const t = setTimeout(() => setTyping(true), 750);
      return () => clearTimeout(t);
    }
    // 慢一点发出：客户消息 1.8s、AI 整条 1.9s，跟得上阅读
    const delay = next.side === 'out' ? 1900 : 1800;
    const t = setTimeout(() => {
      setTyping(false);
      setStep((s) => s + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [step, typing, paused, inView, reduce, total, item.chat]);

  // 新消息进来时自动滚到底 —— 旧消息被往上顶，跟微信聊天一致。
  // 用 scrollTop 直接赋值而不是 smooth，避免连续更新时滚动动画互相追。
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // 内容高于视口才需要「上面还有」的渐隐提示
    setScrolled(el.scrollHeight > el.clientHeight + 2);
  }, [step, typing]);

  return (
    <MotionBox
      {...SOFT_CARD}
      p={{ base: 6, md: 7 }}
      display="flex"
      flexDirection="column"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.32, delay: index * 0.08 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* 「通话中」状态条：点明这是一通正在进行的语音电话，短信是在通话中发的 */}
      <Flex align="center" gap={2.5} bg="brand.navy" color="white" px={4} py={2.5} rounded="lg" mb={5}>
        <motion.span
          style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '9999px', background: '#F87171' }}
          animate={{ scale: [1, 1.35, 1], opacity: [1, 0.55, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <Icon as={FiPhone} boxSize={3.5} />
        <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.02em">
          {zh ? '通话中' : 'On a call'}
        </Text>
        <WaveBars />
        <Text fontSize="xs" fontFamily="mono" ml="auto" opacity={0.9}>00:42</Text>
      </Flex>

      <Flex align="center" gap={3} mb={4}>
        <Flex {...softIconBox('amber', 10)} rounded="lg">
          <Icon as={SMS_ICONS[item.icon] || FiShoppingBag} boxSize={5} />
        </Flex>
        <Heading fontSize="lg" color="gray.900">{item.scenario}</Heading>
      </Flex>
      <Text fontSize="sm" color="gray.700" lineHeight={1.8} mb={5}>{item.lead}</Text>

      {/* 固定高度的对话视口：新消息只往上顶，不再把卡片撑高。
          跟微信一样，内容超出就自动滚到底，旧消息往上走。
          高度取「四条消息刚好放下」附近 —— 中文一般刚好，英文长句会触发滚动。 */}
      <Box position="relative">
        <Box
          ref={ref}
          h={{ base: '320px', md: '300px' }}
          overflowY="auto"
          overflowX="hidden"
          opacity={fadingOut ? 0 : 1}
          transition="opacity .4s ease"
          sx={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <VStack align="stretch" spacing={2.5}>
          {item.chat.map((m, mi) => {
            const shown = mi < step;
            const typingHere = mi === step && typing;
            if (!shown && !typingHere) return null;
            const isAi = m.side === 'out';
            return (
            <MotionBox
              key={mi}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {/* AI 在左、客户在右 */}
              <Flex justify={isAi ? 'flex-start' : 'flex-end'}>
                <Box
                  maxW="82%"
                  px={3.5}
                  py={2.5}
                  rounded="2xl"
                  fontSize="sm"
                  lineHeight={1.7}
                  bg={isAi ? 'brand.navy' : 'gray.200'}
                  color={isAi ? 'white' : 'gray.900'}
                  borderBottomLeftRadius={isAi ? 'sm' : '2xl'}
                  borderBottomRightRadius={isAi ? '2xl' : 'sm'}
                >
                  {typingHere ? <TypingDots /> : m.text}
                </Box>
              </Flex>
            </MotionBox>
            );
          })}
          </VStack>
        </Box>

        {/* 内容溢出、被往上顶时，顶部渐隐一下，避免气泡像被硬切 */}
        <Box
          position="absolute" top={0} left={0} right={0} h="12px" pointerEvents="none"
          bgGradient="linear(to bottom, rgba(255,255,255,0.96), rgba(255,255,255,0))"
          opacity={scrolled ? 1 : 0} transition="opacity .2s"
        />
      </Box>

      {/* 状态行：全部气泡显示完才出现 */}
      <MotionBox animate={{ opacity: step >= total ? 1 : 0 }} transition={{ duration: 0.3 }}>
        <HStack mt={5} spacing={2} justify="center">
          <Icon as={FiArrowRight} boxSize={3.5} color="blue.500" />
          <Text fontSize="xs" fontWeight="semibold" color="blue.600">{item.status}</Text>
        </HStack>
      </MotionBox>

      <Box flex="1 1 auto" />
      <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.200">
        <Text fontSize="xs" color="amber.700" lineHeight={1.75}>{item.value}</Text>
      </Box>
    </MotionBox>
  );
};

const ProductsView = ({ lang, onCases, onPricing }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const p = c.products;

  /* 短信链接的演示语言：默认跟随站点语言，但允许在区块内单独切换。
     客户主要是美国客户、以英文交流，但官网上也要能演示中文，所以两种都备着。 */
  const [demoLang, setDemoLang] = useState(lang === 'zh' ? 'zh' : 'en');
  useEffect(() => { setDemoLang(lang === 'zh' ? 'zh' : 'en'); }, [lang]);
  const demoSms = (demoLang === 'zh' ? landingContent.zh : landingContent.en).flow.sms;

  // 产品界面区块的 01–04 标签切换（2026-10-19 由纵向堆叠改来，同日加自动轮播）
  const [activeShot, setActiveShot] = useState(0);
  const [shotPaused, setShotPaused] = useState(false); // 鼠标悬停
  const [shotZoomed, setShotZoomed] = useState(false); // 灯箱打开
  const [pageHidden, setPageHidden] = useState(false); // 浏览器标签页切到后台
  const [shotInView, setShotInView] = useState(false); // 这一块是否在视口里
  const shotRef = useRef(null);

  // 标签页切到后台时不要继续轮播，否则切回来已经跳过好几张
  useEffect(() => {
    const onVis = () => setPageHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // 滚出视口就停 —— 否则用户在首屏看别的，滚下来时已经跳到第 3 张了
  useEffect(() => {
    const el = shotRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShotInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setShotInView(entry.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* 自动轮播：每 3 秒切下一屏。
     暂停条件 —— 鼠标悬停、灯箱打开、页面在后台、不在视口内、系统开了「减少动态效果」。
     用 setTimeout 而不是 setInterval：依赖里有 activeShot，手动点标签会重置计时，
     不会出现「刚点完立刻又被自动切走」。 */
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (shotPaused || shotZoomed || pageHidden || !shotInView || reduce) return undefined;
    const total = c.interface.items.length;
    const timer = setTimeout(() => setActiveShot((i) => (i + 1) % total), 3000);
    return () => clearTimeout(timer);
  }, [shotPaused, shotZoomed, pageHidden, shotInView, activeShot, c.interface.items.length]);
  // 两张产品卡在「选型」和「对照」两处复用，先取出来
  const numCard = p.cards.find((x) => x.kind === 'number') || p.cards[0];
  const aiCard = p.cards.find((x) => x.kind === 'ai') || p.cards[1];
  return (
    // 末节（#interface）自带 py，这里不再重复加 pb，否则页脚前会空出一大段
    <Box pt="64px" className="gradient-mesh">
      <Container maxW="7xl">
        {/* Hero —— 与其他页面统一：居中（橙色眉标 + 两行标题 + 一行灰字） */}
        <Box pt={{ base: 8, md: 14 }} maxW="4xl" mx="auto" textAlign="center">
          <Text fontSize="sm" fontWeight="bold" color="amber.600" letterSpacing="0.14em" textTransform="uppercase">
            {p.eyebrow}
          </Text>
          <Heading as="h1" mt={5} fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }} lineHeight={1.22} color="gray.900">
            <Box as="span" display="block" fontWeight="600">{p.title}</Box>
            <Box as="span" display="block" fontWeight="800" mt={1}>{p.titleBold}</Box>
          </Heading>
          {/* 副标题放宽到 3xl：新文案 33 字，2xl 下会折出「系。」孤字 */}
          <Text mt={6} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.9} maxW="3xl" mx="auto">
            {p.subtitle}
          </Text>
        </Box>

        {/* 痛点四卡已按用户要求下架：产品页 hero 之后直接进两张产品卡。
            `products.painPoints` 的数据仍保留在 landingContent.js 里，要恢复把这段接回来即可。
        */}

        {/* 两种产品：左卡琥珀高亮 + 价格，右卡白底朴素（模板里这一段不带标题） */}
        <Box mt={{ base: 12, md: 16 }}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="stretch">
            {/* 号码 + AI —— 推荐款：琥珀描边、顶部「推荐」小标签、带月费 */}
            <MotionBox
              position="relative"
              {...SOFT_CARD}
              borderWidth="1.5px"
              borderColor="amber.300"
              boxShadow="0 18px 42px rgba(255,183,0,0.14)"
              _hover={{ borderColor: 'amber.400', boxShadow: '0 24px 52px rgba(255,183,0,0.20)' }}
              p={{ base: 7, md: 8 }}
              display="flex"
              flexDirection="column"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.4 }}
            >
              <Badge
                position="absolute"
                top="-12px"
                left={{ base: 7, md: 8 }}
                bg="amber.500"
                color="brand.navy"
                px={3}
                py={1}
                rounded="full"
                fontSize="xs"
                fontWeight="bold"
                textTransform="none"
                boxShadow="0 6px 16px -6px rgba(255,183,0,0.9)"
              >
                {p.choice.aiBadge}
              </Badge>

              <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900">{aiCard.name}</Heading>
              <Text mt={2} fontSize="sm" color="gray.700" lineHeight={1.75}>{aiCard.tagline}</Text>

              <ProductCheckList label={p.choice.solvesLabel} items={aiCard.solves} mt={6} />
              <ProductCheckList label={p.choice.featuresLabel} items={aiCard.features} mt={6} />

              <Box mt={6} bg="blue.50" border="1px solid" borderColor="blue.100" rounded="xl" px={4} py={3}>
                <Text fontSize="xs" color="gray.800" lineHeight={1.75}>{aiCard.note}</Text>
              </Box>

              <Box flex="1 1 auto" />

              <HStack mt={6} align="baseline" spacing={2} flexWrap="wrap">
                <Text fontSize={{ base: '3xl', md: '4xl' }} fontWeight="800" color="brand.navy" lineHeight={1}>
                  {aiCard.price.amount}
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">{aiCard.price.unit}</Text>
                <Text fontSize="xs" color="gray.600" lineHeight={1.6}>{aiCard.price.fine}</Text>
              </HStack>

              <HStack mt={6} spacing={3} flexWrap="wrap">
                {/* 原来「立即开通」点开的是演示播放器、「听一通真实通话」跳的是预约页 —— 标签与去向对不上。
                    预约功能下架后一并理顺：立即开通与导航栏一致走合作方开通页，听通话进通话演示页。 */}
                <Button as="a" href={PARTNER_PAGE_URL} target="_blank" rel="noopener noreferrer" variant="amber" rightIcon={<FiArrowRight />}>{aiCard.cta}</Button>
                <Button variant="outline" rightIcon={<FiPlay />} onClick={onCases}>{aiCard.ctaAlt}</Button>
              </HStack>

              {/* 「立即开通」会跳出本站 —— 用户产生疑问的位置就在这个按钮上，先在这里交代一次 */}
              <Text mt={3.5} fontSize="xs" color="gray.600" lineHeight="1.75">
                {p.partnerInline}
              </Text>
            </MotionBox>

            {/* 单独号码 —— 白底朴素卡，深色按钮收尾 */}
            <MotionBox
              {...SOFT_CARD}
              p={{ base: 7, md: 8 }}
              display="flex"
              flexDirection="column"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900">{numCard.name}</Heading>
              <Text mt={2} fontSize="sm" color="gray.700" lineHeight={1.75}>{numCard.tagline}</Text>

              <ProductCheckList label={p.choice.solvesLabel} items={numCard.solves} mt={6} />
              <ProductCheckList label={p.choice.featuresLabel} items={numCard.features} mt={6} />

              <Box mt={6} bg="blue.50" border="1px solid" borderColor="blue.100" rounded="xl" px={4} py={3}>
                <Text fontSize="xs" color="gray.800" lineHeight={1.75}>{numCard.note}</Text>
              </Box>

              <Box flex="1 1 auto" />

              {/* 号码卡有一行「按需 …」，上面用细分隔线断开；AI 卡没有这一行 */}
              {numCard.aside && (
                <React.Fragment>
                  <Divider mt={6} borderColor="gray.300" />
                  <Text mt={4} fontSize="xs" color="gray.700" lineHeight={1.7}>
                    <Box as="span" fontWeight="bold" color="gray.900">{numCard.aside.label}</Box>
                    {' '}
                    {numCard.aside.text}
                  </Text>
                </React.Fragment>
              )}

              {/* 「了解号码方案」是咨询，不是直接下单：落到定价页看费用明细 */}
              <Button mt={6} alignSelf="flex-start" variant="navy" rightIcon={<FiArrowRight />} onClick={onPricing}>
                {numCard.cta}
              </Button>
            </MotionBox>
          </SimpleGrid>
        </Box>

        {/* 合作方说明条已下架（原「为什么「立即开通」会离开本站」整条蓝卡）。
            原因：产品页的任务是「帮你选产品」，叙事线是 痛点 → 两种配置 → 五步上线；
            而这条卡回答的是「为什么按钮会跳到别的网站」—— 那是信任/流程疑问，不是选型疑问，
            插在两张产品卡和五步流程之间，把主线打断了。
            而且它用的还是和产品卡同一套 SOFT_CARD 样式，视觉上和「第三种产品」平级。
            同一件事在产品页本来还说两遍（按钮下方一行 + 这一条），属于自我放大。
            完整解释保留在「关于我们 → 合作方」那一整屏，那里才是讲「我们和谁合作」的地方；
            产品页只留按钮下方那一行内联小字，位置更准、成本更低。
            文案 partnerNote 仍留在 landingContent.js（中英各一份），要恢复直接搬回来即可。 */}

        {/* 五步流程：首圆实心、其余白底描边，中间一条连接线 */}
        <Box mt={{ base: 16, md: 24 }}>
          <SectionHeader title={p.flowTitle} subtitle={p.flowSubtitle} />
          <Box position="relative" mt={14}>
            <Box display={{ base: 'none', md: 'block' }} position="absolute" top="28px" left="9%" right="9%" h="2px" bgGradient="linear(to-r, blue.100, blue.300, blue.100)" zIndex={0} />
            <SimpleGrid columns={{ base: 1, md: 5 }} spacing={{ base: 8, md: 6 }} position="relative" zIndex={1}>
              {p.flow.map((step, i) => (
                <MotionBox key={step.title} textAlign="center" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.3, delay: i * 0.06 }}>
                  <Flex justify="center">
                    <Flex
                      w={14}
                      h={14}
                      rounded="full"
                      bg={i === 0 ? 'brand.navy' : 'white'}
                      color={i === 0 ? 'white' : 'blue.600'}
                      align="center"
                      justify="center"
                      border="1px solid"
                      borderColor={i === 0 ? 'brand.navy' : 'gray.300'}
                      boxShadow={i === 0 ? '0 10px 24px -10px rgba(11,61,92,0.7)' : '0 6px 18px -12px rgba(11,61,92,0.5)'}
                    >
                      <Icon as={PRODUCT_FLOW_ICONS[step.icon] || FiPhone} boxSize={6} />
                    </Flex>
                  </Flex>
                  <Text mt={4} fontSize="xs" fontWeight="bold" color="amber.600" letterSpacing="0.08em">
                    {`${p.flowStepLabel} ${i + 1}`}
                  </Text>
                  <Heading fontSize="md" mt={2} color="gray.900" lineHeight={1.45}>{step.title}</Heading>
                  <Text fontSize="sm" color="gray.700" mt={2} lineHeight={1.85}>{step.text}</Text>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Box>

          {/* 号码的入口触点：原「三层链路」里「客户在哪里找到号码」那一卡的内容，
              合并到这里作为五步流程的入口说明，不再与步骤 1 重复叙述。 */}
          {p.touchpoints && (
            <Flex mt={12} mb={{ base: 6, md: 12 }} wrap="wrap" justify="center" align="center" gap={2.5}>
              <Text fontSize="xs" fontWeight="bold" color="gray.700" mr={1}>
                {p.touchpoints.label}
              </Text>
              {p.touchpoints.items.map((tp) => (
                <Box
                  key={tp}
                  px={3}
                  py={1.5}
                  rounded="full"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                  fontSize="xs"
                  color="gray.800"
                  lineHeight={1.6}
                >
                  {tp}
                </Box>
              ))}
            </Flex>
          )}
        </Box>
      </Container>

      {/* ============ SMS links —— 模板里这一段是浅蓝色带 ============ */}
      <Box id="flow" bg="blue.50" py={SOFT_SECTION_PY} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="7xl">
          <SectionHeader
            eyebrow={c.flow.eyebrow}
            title={c.flow.title}
            subtitle={c.flow.subtitle}
            eyebrowStyle="text"
          />

          {/* 演示语言切换：客户以英文为主，但官网上也要能演示中文，两种都备着 */}
          <HStack
            justify="center"
            mt={10}
            spacing={1}
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            rounded="full"
            p={1}
            w="fit-content"
            mx="auto"
            role="group"
            aria-label={lang === 'zh' ? '演示语言' : 'Demo language'}
          >
            {[['zh', '中文'], ['en', 'English']].map(([val, label]) => {
              const on = demoLang === val;
              return (
                <Button
                  key={val}
                  size="sm"
                  onClick={() => setDemoLang(val)}
                  variant="ghost"
                  rounded="full"
                  px={5}
                  h="34px"
                  bg={on ? 'brand.navy' : 'transparent'}
                  color={on ? 'white' : 'gray.700'}
                  fontWeight="semibold"
                  fontSize="sm"
                  _hover={on ? { bg: 'brand.navy' } : { bg: 'blue.50' }}
                >
                  {label}
                </Button>
              );
            })}
          </HStack>

          {/* 短信链接：两个完整流程 —— 模板里是聊天气泡，不是编号步骤 */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={8}>
            {demoSms.map((item, i) => (
              // key 带上 demoLang：切换语言时整卡重挂，对话从头开始播
              <SmsChatCard key={`${demoLang}-${i}`} item={item} zh={demoLang === 'zh'} index={i} />
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ============ Interface (inside the product) —— 模板里是白底 2 列后台小样 ============ */}
      <Box id="interface" bg="white" py={SOFT_SECTION_PY} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="7xl">
          <SectionHeader
            eyebrow={c.interface.eyebrow}
            title={c.interface.title}
            subtitle={c.interface.subtitle}
            eyebrowStyle="text"
          />
        </Container>
        {/* 2026-10-19：截图带比正文列（7xl / 1280px）更宽，最大 1600px。
            三张后台原图 2384–2461px 宽，塞进 1246px 的正文列只剩 0.52 倍，
            中文从 10px 缩到 5px 就糊了；放宽后大屏上能到 0.65 左右，
            再配合 ZoomShot 的点击放大，细节才看得清。标题仍留在 7xl 容器里与全站对齐。 */}
        <Box maxW="1600px" mx="auto" px={{ base: 5, md: 8 }}>
          {/* 2026-10-19：三张图纵向堆叠会把这一块撑到 3468px（产品页最长的一段），
              改成 01/02/03 标签切换后整块约 1100px，点标签换图，图片仍是全宽。
              三张图比例接近（1.88 / 1.88 / 1.91），切换时高度只差十来像素，不会跳动。
              MockWindow 组件与其样式仍保留在本文件（现在没有调用点），要恢复旧版直接接回来即可。 */}
          <Box
            ref={shotRef}
            mt={{ base: 10, md: 12 }}
            onMouseEnter={() => setShotPaused(true)}
            onMouseLeave={() => setShotPaused(false)}
          >
            <HStack justify="center" spacing={2} flexWrap="wrap" role="tablist" aria-label={c.interface.eyebrow}>
              {c.interface.items.map((item, i) => {
                const on = i === activeShot;
                return (
                  <Button
                    key={item.title}
                    role="tab"
                    id={`interface-tab-${i}`}
                    aria-selected={on}
                    aria-controls="interface-panel"
                    onClick={() => setActiveShot(i)}
                    variant="ghost"
                    h="auto"
                    px={{ base: 4, md: 5 }}
                    py={{ base: 2.5, md: 3 }}
                    rounded="full"
                    border="1.5px solid"
                    borderColor={on ? 'brand.navy' : 'gray.300'}
                    bg={on ? 'brand.navy' : 'white'}
                    color={on ? 'white' : 'gray.700'}
                    fontWeight="semibold"
                    fontSize={{ base: 'sm', md: 'md' }}
                    whiteSpace="nowrap"
                    transition="all .2s ease"
                    _hover={on ? { bg: 'brand.navy' } : { borderColor: 'blue.400', color: 'blue.600', bg: 'white' }}
                    _active={on ? { bg: 'brand.navy' } : { bg: 'white' }}
                  >
                    <Text as="span" fontSize="xs" fontWeight="bold" opacity={on ? 0.75 : 0.5} mr={2}>
                      {String(i + 1).padStart(2, '0')}
                    </Text>
                    {item.title}
                  </Button>
                );
              })}
            </HStack>

            <Box id="interface-panel" role="tabpanel" aria-labelledby={`interface-tab-${activeShot}`} mt={{ base: 7, md: 8 }}>
              {/* 横向滑动：四屏并排放在一条轨道里，靠 translateX 切换。
                  之前用淡入淡出（key 换掉 + opacity 0→1），旧图会先瞬间消失、
                  新图再从透明淡出来，中间有一瞬空白，看着就是"不丝滑"。
                  滑动没有空白帧，方向感也对。
                  pb + 负 mb 是给卡片投影留出裁剪空间，否则底部阴影会被 overflow 切出硬边。 */}
              <Box overflow="hidden" pb="60px" mb="-60px">
                <MotionBox
                  display="flex"
                  alignItems="flex-start"
                  initial={{ x: '0%' }}
                  animate={{ x: `${-activeShot * 100}%` }}
                  transition={{ type: 'tween', ease: [0.32, 0.72, 0, 1], duration: 0.55 }}
                >
                  {c.interface.items.map((item, i) => (
                    <Box
                      key={item.img}
                      flex="0 0 100%"
                      minW="100%"
                      pointerEvents={i === activeShot ? 'auto' : 'none'}
                    >
                      <Text
                        textAlign="center"
                        fontSize={{ base: 'sm', md: 'md' }}
                        color="gray.700"
                        lineHeight={1.9}
                        maxW="3xl"
                        mx="auto"
                        mb={{ base: 6, md: 7 }}
                      >
                        {item.text}
                      </Text>
                      <ZoomShot
                        src={item.img}
                        alt={item.alt || item.title}
                        caption={`${String(i + 1).padStart(2, '0')} / ${String(c.interface.items.length).padStart(2, '0')} · ${item.title}`}
                        zh={lang === 'zh'}
                        onOpenChange={setShotZoomed}
                      />
                    </Box>
                  ))}
                </MotionBox>
              </Box>
            </Box>
          </Box>
          {/* 页尾「能力边界」说明与免责小字已按要求下架。原来这里有两块：
              (1) c.interface.note 那行居中小字（「自动错误统计、实时转接等尚未确认的能力…」）；
              (2) NoteDisclosure 折叠块「服务范围与能力边界」，展开 7 条。
              文案仍完整保留在 landingContent.js（products.interface.note / products.scope，
              中英各一份），NoteDisclosure 组件也仍留在本文件，要恢复把这两段接回来即可。
              ⚠️ 折叠块里原本写明「转接、语音信箱、外呼、验证码接收、实时订单查询、库存与
              退款操作等默认不包含」，属对外免责声明；下架后产品页不再有这层声明。 */}
        </Box>
      </Box>

      {/* ============ FAQ —— 三张截图之后、页脚之前的收尾问答 ============
          定价页的 FAQ 只答「钱」，帮助中心的 FAQ 只答「账户注册」，
          这里补的是「产品能力 / 开始前准备 / 记录留存」这一组，三处不重复。
          样式与定价页 FAQ 保持一致（同一个 Accordion 写法）。 */}
      <Box id="product-faq" bg="blue.50" py={SOFT_SECTION_PY} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="4xl">
          <SectionHeader title={c.faqTitle} subtitle={c.faqSubtitle} />
          <Accordion allowMultiple mt={10} border="none">
            {c.faqs.map((item) => (
              <AccordionItem key={item.q} border="1px solid" borderColor="gray.300" rounded="lg" mb={4} overflow="hidden" bg="white">
                <h3>
                  <AccordionButton px={5} py={4} _expanded={{ bg: 'blue.50' }} transition="background 0.2s ease">
                    <Box flex="1" textAlign="left" fontWeight="semibold" color="gray.900">{item.q}</Box>
                    <AccordionIcon color="blue.500" />
                  </AccordionButton>
                </h3>
                <AccordionPanel pb={4} bg="gray.50" color="gray.800" lineHeight={1.8} fontSize="sm">
                  {item.a}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>
    </Box>
  );
};

// ============ Cases view ============
const CasesView = ({ lang, onContact, openDemo }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  return (
    <Box pt="64px" pb={20}>
      {/* ============ Hero ============ */}
      <Box className="gradient-mesh" pt={{ base: 10, md: 16 }} pb={{ base: 12, md: 16 }}>
        <Container maxW="5xl">
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" fontSize="sm">
              {c.casesHero.eyebrow}
            </Badge>
            <Heading as="h1" mt={4} fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }} lineHeight={1.2} color="gray.900">
              <Box as="span" display="block" fontWeight="600">{c.casesHero.title}</Box>
              <Box as="span" display="block" fontWeight="800" mt={1}>{c.casesHero.titleBold}</Box>
            </Heading>
            <Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="gray.800" lineHeight={1.8}>
              {c.casesHero.subtitle}
            </Text>
            <HStack mt={8} justify="center" spacing={4} flexWrap="wrap">
              <Button variant="primary" size="lg" rightIcon={<FiPlay />} onClick={openDemo}>
                {c.casesHero.ctaPrimary}
              </Button>
              <VoiceTrialButton lang={lang} size="lg" />
            </HStack>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={12} maxW="3xl" mx="auto">
            {c.casesHero.stats.map((s) => (
              <Box key={s.label} textAlign="center" p={4} bg="white" rounded="xl" border="1px solid" borderColor="blue.100">
                <Text fontSize="2xl" fontWeight="800" color="blue.600" lineHeight={1}>{s.value}</Text>
                <Text fontSize="sm" color="gray.700" mt={1}>{s.label}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ============ Live demo ============ */}
      <Box id="demo" className="gradient-mesh" color="gray.900" py={{ base: 16, md: 22 }} sx={{ scrollMarginTop: '80px' }} position="relative" overflow="hidden">
        {/* decorative blobs for liveliness */}
        <Box aria-hidden="true" position="absolute" w="380px" h="380px" rounded="full" bg="blue.200" opacity={0.35} filter="blur(80px)" top="-120px" right="-100px" className="animate-float" />
        <Box aria-hidden="true" position="absolute" w="320px" h="320px" rounded="full" bg="amber.100" opacity={0.55} filter="blur(70px)" bottom="-100px" left="-80px" className="animate-float-slow" sx={{ animationDelay: '-4s' }} />

        <Container maxW="6xl" position="relative" zIndex={1}>
          <Box textAlign="center" maxW="3xl" mx="auto" mb={{ base: 10, md: 14 }}>
            <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" fontSize="sm">
              {c.demo.eyebrow}
            </Badge>
            <Heading as="h2" mt={4} fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }} lineHeight={1.2} color="gray.900">
              <Box as="span" display="block" fontWeight="600">{c.demo.title}</Box>
            </Heading>
            <Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.8}>
              {c.demo.subtitle}
            </Text>
          </Box>

          <Stack spacing={{ base: 4, md: 5 }}>
            {c.demo.cases.map((item, index) => (
              <DemoPlayerRow key={item.id} item={item} index={index} c={c} onPlay={() => openDemo(item.id)} />
            ))}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

// ============ Blog view ============
const BLOG_ILLU = ['blue', 'amber', 'teal'];
const PAGE_SIZE = 6; // 文章列表每页条数

const BlogView = ({ lang, onCta, onOpenPost }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const b = c.blog;
  const isZh = lang === 'zh';
  const [activeCat, setActiveCat] = useState('all');
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('home'); // 'home' 首页 | 'list' 分类/搜索结果页
  const [videoItem, setVideoItem] = useState(null);
  const videoModal = useDisclosure();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const [page, setPage] = useState(1);

  // 点击外部 / Esc 关闭搜索联想
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onDoc = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [searchOpen]);

  const openVideo = (v) => {
    setVideoItem(v);
    videoModal.onOpen();
  };

  // 真实分类过滤：直接取当前选中分类的 tag，避免 key 映射脱节
  const activeCatObj = b.categories.find((cat) => cat.key === activeCat);
  const wantTag = activeCatObj && activeCatObj.tag;
  const byCat = activeCat === 'all' || !wantTag
    ? b.allPosts
    : b.allPosts.filter((p) => p.tag === wantTag);

  const q = query.trim().toLowerCase();
  const matches = (p) =>
    !q || p.title.toLowerCase().includes(q) || (p.tag || '').toLowerCase().includes(q);
  const filtered = q ? byCat.filter(matches) : byCat;

  // 分页：筛选条件一变就回到第 1 页
  useEffect(() => { setPage(1); }, [activeCat, query, mode]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // 板块数据切分（同一份 allPosts，保证"最新"与下方栏目一致）
  const latest = b.allPosts.slice(0, 3);
  const news = b.allPosts.filter((p) => p.tag === b.categories.find((x) => x.key === 'product')?.tag).slice(0, 3);
  const stories = b.allPosts.filter((p) => p.tag === b.categories.find((x) => x.key === 'usecase')?.tag).slice(0, 3);
  // 分类页里的视频（'all' 时给全部）
  const catVideos = activeCat === 'all' ? b.videos.items : b.videos.items.filter((v) => v.cat === activeCat);

  // 进入分类 / 搜索结果页（真正的页面切换：切换内容 + 回到顶部）
  const goToList = (catKey, tagQuery) => {
    if (catKey) setActiveCat(catKey);
    if (tagQuery !== undefined) setQuery(tagQuery);
    setMode('list');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backHome = () => {
    setMode('home');
    setActiveCat('all');
    setQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.4, delay },
  });

  // 文章配图：列表卡、主推卡、编辑精选都按 id 回查 allPosts，拿到同一张图
  const imgOf = (p) =>
    (p && (p.img || (b.allPosts.find((x) => x.id === p.id) || {}).img)) || '';

  // 小卡片（图占位 + 标题 + 元信息）
  // animate=false 用于分页网格：避免翻页时每一张卡都重新播放入场动画
  //
  // 必须写成「返回 JSX 的函数」而不是行内组件（把 MiniCard 声明成组件再当标签用）：
  // 行内组件在父组件每次重渲染时都会产生新的函数引用，React 会整棵卸载重建，
  // framer-motion 随之回到 initial（opacity:0）并重新挂 IntersectionObserver；
  // 若这次重建发生在卡片正处于视口外时（例如滚回页面顶部），卡片会永久停在透明。
  const renderMiniCard = (p, i = 0, animate = true) => (
    <MotionBox
      key={p.id || p.title}
      className="hover-lift"
      cursor="pointer"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      bg="white"
      h="100%"
      display="flex"
      flexDirection="column"
      onClick={() => onOpenPost && onOpenPost(p)}
      {...(animate ? fade(i * 0.08) : {})}
    >
      <Box position="relative" bg={BLOG_ILLU[i % 3] + '.50'} h={{ base: '160px', md: '180px' }} overflow="hidden">
        {imgOf(p) ? (
          <Image src={imgOf(p)} alt="" position="absolute" inset={0} w="full" h="full" objectFit="cover" />
        ) : (
          <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" color={BLOG_ILLU[i % 3] + '.300'}>
            <Icon as={FiImage} boxSize={9} />
          </Box>
        )}
        <Box position="absolute" top={3} left={3}>
          <Badge colorScheme={BLOG_ILLU[i % 3]} variant="solid" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="bold">
            {p.tag}
          </Badge>
        </Box>
      </Box>
      <Stack flex="1" p={5} spacing={3}>
        <Heading fontSize="lg" lineHeight={1.4} color="gray.900" fontWeight="semibold" noOfLines={2}>
          {p.title}
        </Heading>
        <HStack spacing={2} fontSize="xs" color="gray.500" pt="auto" mt="auto">
          <Text>{p.date}</Text>
          <Text>·</Text>
          <Text>{p.readTime}</Text>
          <Icon as={FiArrowRight} color="blue.500" boxSize={3.5} ml="auto" />
        </HStack>
      </Stack>
    </MotionBox>
  );

  // 视频卡片（资讯中心「视频与实录」与分类结果页共用；将来接真实视频只改弹窗里的播放器）
  const renderVideoCard = (v, i = 0) => (
    <MotionBox
      key={v.id}
      className="hover-lift"
      cursor="pointer"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      bg="white"
      h="100%"
      display="flex"
      flexDirection="column"
      onClick={() => openVideo(v)}
      {...fade(i * 0.06)}
    >
      <Box position="relative" h={{ base: '180px', md: '190px' }} bgGradient="linear(to-br, #0B3D5C, #1B6A94)" flexShrink={0}>
        <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center">
          <Flex w="56px" h="56px" rounded="full" bg="whiteAlpha.900" color="#0B3D5C" align="center" justify="center" boxShadow="lg">
            <Icon as={FiPlay} boxSize={6} ml="2px" />
          </Flex>
        </Box>
        <Badge position="absolute" top={3} left={3} bg="whiteAlpha.800" color="#0B3D5C" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="bold">
          {v.tag}
        </Badge>
        <Badge position="absolute" bottom={3} right={3} bg="blackAlpha.700" color="white" px={2} py={0.5} rounded="md" fontSize="xs" fontWeight="medium">
          {v.duration}
        </Badge>
      </Box>
      <Stack p={5} spacing={2} flex="1">
        <Heading fontSize="md" lineHeight={1.45} color="gray.900" fontWeight="semibold" noOfLines={2}>
          {v.title}
        </Heading>
        <Text fontSize="sm" color="gray.600" lineHeight={1.7} noOfLines={2}>{v.desc}</Text>
        <Text fontSize="xs" color="gray.400" pt={1} mt="auto">{v.date}</Text>
      </Stack>
    </MotionBox>
  );

  // 列表行内容（首页"全部文章"用；占位行复用同一份标记，保证高度完全一致）
  const postRowInner = (p) => (
    <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} gap={{ base: 3, md: 6 }}>
      <HStack spacing={2} minW={{ md: '160px' }}>
        <Badge colorScheme="blue" variant="subtle" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="bold">
          {p.tag}
        </Badge>
      </HStack>
      <Heading flex="1" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.4} color="gray.900" fontWeight="medium">
        {p.title}
      </Heading>
      <HStack spacing={3} fontSize="sm" color="gray.500" flexShrink={0}>
        <Text>{p.date}</Text>
        <Text display={{ base: 'none', md: 'inline' }}>·</Text>
        <Text display={{ base: 'none', md: 'inline' }}>{p.readTime}</Text>
        <Icon as={FiArrowRight} color="blue.500" boxSize={4} />
      </HStack>
    </Flex>
  );

  // 分页条
  const renderPager = (total, current, onChange) => {
    const pages = Math.ceil(total / PAGE_SIZE);
    if (pages <= 1) return null;
    return (
      <HStack spacing={2} justify="center" mt={8} flexWrap="wrap">
        <IconButton
          aria-label={isZh ? '上一页' : 'Previous page'}
          icon={<Icon as={FiChevronLeft} />}
          size="sm"
          variant="outline"
          colorScheme="gray"
          rounded="lg"
          isDisabled={current <= 1}
          onClick={() => onChange(current - 1)}
        />
        {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
          <Button
            key={n}
            size="sm"
            minW="36px"
            px={0}
            rounded="lg"
            variant={n === current ? 'solid' : 'outline'}
            colorScheme={n === current ? 'blue' : 'gray'}
            fontWeight={n === current ? 'bold' : 'medium'}
            onClick={() => onChange(n)}
          >
            {n}
          </Button>
        ))}
        <IconButton
          aria-label={isZh ? '下一页' : 'Next page'}
          icon={<Icon as={FiChevronRight} />}
          size="sm"
          variant="outline"
          colorScheme="gray"
          rounded="lg"
          isDisabled={current >= pages}
          onClick={() => onChange(current + 1)}
        />
      </HStack>
    );
  };

  // 搜索框 + 联想面板（热门搜索 / 猜你想看 / 无结果引导）
  // 注意：以函数调用形式渲染（renderSearch(...)），否则组件身份每次 render 变化会导致输入框失焦
  const renderSearch = ({ size = 'lg', maxW = 'xl', scope = 'all', align = 'center' } = {}) => {
    const big = size === 'lg';
    // scope: 'all' = 全站搜索；其他值 = 只在该分类内搜索
    const scopeCat = b.categories.find((x) => x.key === scope);
    const scopeTag = scope === 'all' ? null : (scopeCat || {}).tag;
    const catKeywords = (scopeCat && scopeCat.keywords) || [];
    const pool = scopeTag ? b.allPosts.filter((p) => p.tag === scopeTag) : b.allPosts;
    const scoped = !!scopeTag;
    const sq = query.trim().toLowerCase();
    const sug = sq
      ? pool
          .filter((p) => p.title.toLowerCase().includes(sq) || (p.tag || '').toLowerCase().includes(sq))
          .slice(0, 5)
      : pool.slice(0, 4);
    const showPopular = !scoped && (!sq || sug.length === 0);
    const listTitle = scoped && !sq ? b.search.inCategoryTitle : b.search.suggestTitle;

    const pickPost = (p) => {
      setSearchOpen(false);
      onOpenPost && onOpenPost(p);
    };
    const pickTag = (t) => {
      setQuery(t.query);
      setSearchOpen(true);
    };

    return (
      <Box ref={searchRef} position="relative" maxW={maxW} mx={align === 'center' ? 'auto' : 0} w="100%" textAlign="left" zIndex={30}>
        {/* 图标位置与 Input 的 pl 用同一套显式 px 值，避免随 size 漂移导致重叠 */}
        <Box
          position="absolute"
          left={big ? '1rem' : '0.875rem'}
          top="50%"
          transform="translateY(-50%)"
          color={searchOpen ? 'blue.500' : 'gray.400'}
          pointerEvents="none"
          zIndex={2}
          lineHeight={0}
        >
          <Icon as={FiSearch} boxSize={big ? '20px' : '16px'} />
        </Box>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setSearchOpen(true)}
          placeholder={scoped ? b.search.scopedPlaceholder : b.hero.searchPlaceholder}
          size={big ? 'lg' : 'md'}
          bg={big ? 'white' : 'gray.50'}
          pl={big ? '3rem' : '2.625rem'}
          pr={4}
          h={big ? '56px' : undefined}
          borderRadius="full"
          border="1px solid"
          borderColor={searchOpen ? 'blue.300' : 'gray.200'}
          boxShadow={big ? 'md' : 'none'}
          fontSize="md"
          _placeholder={{ color: 'gray.400' }}
          _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 3px rgba(41,171,226,0.18)', bg: 'white' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { setSearchOpen(false); goToList(null, undefined); }
          }}
        />

        {searchOpen && (
          <Box
            position="absolute"
            top="calc(100% + 10px)"
            left={0}
            right={0}
            bg="white"
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="xl"
            overflow="hidden"
            zIndex={40}
          >
            <Box p={3}>
              {/* 热门搜索（空输入，或无结果时给出路） */}
              {showPopular && (
                <Box px={2} pt={1} pb={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.06em" textTransform="uppercase" mb={2}>
                    {b.search.popularTitle}
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {b.hero.tags.map((t) => (
                      <Button
                        key={t.label}
                        size="xs"
                        variant="subtle"
                        colorScheme="blue"
                        rounded="full"
                        fontWeight="medium"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickTag(t)}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </HStack>
                </Box>
              )}

              {/* 分类内搜索：给出该分类的热门词条（不只是文章） */}
              {scoped && catKeywords.length > 0 && (
                <Box px={2} pt={1} pb={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.06em" textTransform="uppercase" mb={2}>
                    {b.search.keywordsTitle}
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {catKeywords.map((k) => (
                      <Button
                        key={k.label}
                        size="xs"
                        variant={query.trim() === k.query ? 'solid' : 'subtle'}
                        colorScheme="blue"
                        rounded="full"
                        fontWeight="medium"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickTag(k)}
                      >
                        {k.label}
                      </Button>
                    ))}
                  </HStack>
                </Box>
              )}

              {/* 无结果引导：全站搜索与分类内搜索给不同的出路 */}
              {sq && sug.length === 0 && (
                <Box px={2} py={3}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                    {scoped ? b.search.scopedEmptyTitle : b.search.emptyTitle}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1} lineHeight={1.7}>
                    {scoped ? b.search.scopedEmptyHint : b.search.emptyHint}
                  </Text>
                </Box>
              )}

              {/* 猜你想看 / 匹配到的文章 */}
              {sug.length > 0 && (
                <>
                  {showPopular && <Divider my={2} />}
                  <Text px={2} pt={1} pb={1.5} fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.06em" textTransform="uppercase">
                    {listTitle}
                  </Text>
                  <Stack spacing={0}>
                    {sug.map((p) => (
                      <Flex
                        key={p.title}
                        as="button"
                        align="center"
                        gap={3}
                        w="100%"
                        px={2}
                        py={2.5}
                        borderRadius="lg"
                        textAlign="left"
                        transition="background 0.15s"
                        _hover={{ bg: 'blue.50' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickPost(p)}
                      >
                        <Badge colorScheme="blue" variant="subtle" rounded="full" px={2} py={0.5} fontSize="2xs" fontWeight="bold" flexShrink={0}>
                          {p.tag}
                        </Badge>
                        <Text fontSize="sm" color="gray.800" noOfLines={1} flex="1">{p.title}</Text>
                        <Icon as={FiArrowRight} color="gray.400" boxSize={3.5} flexShrink={0} />
                      </Flex>
                    ))}
                  </Stack>
                </>
              )}
            </Box>

            {/* 底部：分类内搜索 → 提示改为搜全站；全站搜索 → 浏览全部 */}
            <Flex
              as="button"
              w="100%"
              px={5}
              py={3}
              align="center"
              justify="space-between"
              bg="gray.50"
              borderTop="1px solid"
              borderColor="gray.100"
              fontSize="sm"
              fontWeight="medium"
              color="blue.600"
              _hover={{ bg: 'blue.50' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSearchOpen(false);
                if (scoped) { setQuery(''); goToList('all', ''); } else { goToList('all', ''); }
              }}
            >
              <Text>{scoped ? b.search.searchAll : b.search.browseAll}</Text>
              <Icon as={FiArrowRight} boxSize={4} />
            </Flex>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box pt="64px" pb={20}>
      {/* ===== 0. 资讯中心子导航（Twilio 风格：深色常驻条，承担"这是资讯中心"的标识） ===== */}
      <Box as="nav" position="sticky" top="64px" zIndex={1100} bg="#0B3D5C" boxShadow="sm">
        <Container maxW="6xl">
          <Flex h="56px" align="center" justify="space-between" gap={6}>
            <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color="white" whiteSpace="nowrap">
              {isZh ? 'Bizsprite 资讯中心' : 'Bizsprite Insights'}
            </Text>
            <HStack spacing={{ base: 4, md: 7 }} overflowX="auto" sx={{ '&::-webkit-scrollbar': { display: 'none' } }}>
              {b.categories.map((cat) => (
                <Box
                  key={cat.key}
                  as="button"
                  position="relative"
                  py={2}
                  fontSize="sm"
                  fontWeight={activeCat === cat.key ? 'semibold' : 'medium'}
                  color={activeCat === cat.key ? 'white' : 'blue.100'}
                  whiteSpace="nowrap"
                  transition="color 0.2s"
                  _hover={{ color: 'white' }}
                  onClick={() => goToList(cat.key, '')}
                >
                  {cat.label}
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom="0"
                    h="2px"
                    rounded="full"
                    bg="amber.400"
                    opacity={activeCat === cat.key ? 1 : 0}
                    transition="opacity 0.2s"
                  />
                </Box>
              ))}
            </HStack>
          </Flex>
        </Container>
      </Box>

      {mode === 'list' ? (
        /* ===== 分类 / 搜索 结果页（真正的"页面切换"） ===== */
        <Box as="section" bg="white" pt={{ base: 8, md: 10 }} pb={{ base: 14, md: 20 }} minH="62vh">
          <Container maxW="6xl">
            <Button
              variant="ghost"
              size="sm"
              colorScheme="blue"
              leftIcon={<Icon as={FiArrowLeft} />}
              px={2}
              mb={6}
              fontWeight="medium"
              _hover={{ bg: 'blue.50' }}
              onClick={backHome}
            >
              {isZh ? '返回全部文章' : 'Back to all posts'}
            </Button>

            <Heading fontSize={{ base: '2xl', md: '3xl' }} color="gray.900" mb={6}>
              {activeCat === 'all'
                ? (isZh ? '全部内容' : 'Everything')
                : (b.categories.find((x) => x.key === activeCat) || {}).label}
            </Heading>

            {/* 结果页内的搜索（作用域收窄到当前分类） */}
            <Box mb={10}>
              {renderSearch({ size: 'md', maxW: 'lg', scope: activeCat, align: 'left' })}
            </Box>

            {/* 分类内的视频：分类页不只放文章 */}
            {catVideos.length > 0 && (
              <Box mb={12}>
                <HStack align="center" spacing={3} mb={5}>
                  <Box w="10px" h="10px" rounded="full" bg="blue.500" />
                  <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900">
                    {b.videos.inCategoryTitle}
                  </Heading>
                  <Badge colorScheme="blue" variant="subtle" rounded="full" px={2.5} py={0.5} fontSize="xs" fontWeight="bold">
                    {catVideos.length}
                  </Badge>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {catVideos.map((v, i) => renderVideoCard(v, i))}
                </SimpleGrid>
              </Box>
            )}

            {/* 文章 */}
            <HStack align="center" spacing={3} mb={5}>
              <Box w="10px" h="10px" rounded="full" bg="amber.500" />
              <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900">
                {isZh ? '本分类文章' : 'Articles'}
              </Heading>
              <Badge colorScheme="amber" variant="subtle" rounded="full" px={2.5} py={0.5} fontSize="xs" fontWeight="bold">
                {filtered.length}
              </Badge>
              {q && (
                <Text fontSize="sm" color="gray.500">
                  {isZh ? `关键词“${query}”` : `for “${query}”`}
                </Text>
              )}
            </HStack>

            {filtered.length ? (
              <>
                <MotionBox {...fade()}>
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
                    {paged.map((p) => renderMiniCard(p, 0, false))}
                    {/* 补足到每页条数：翻页时网格高度不变，页码条不会上下跳 */}
                    {totalPages > 1 && Array.from({ length: Math.max(0, PAGE_SIZE - paged.length) }).map((_, idx) => (
                      <Box key={`ph-${idx}`} aria-hidden="true" visibility="hidden" h="100%">
                        {renderMiniCard(paged[idx] || b.allPosts[0], 0, false)}
                      </Box>
                    ))}
                  </SimpleGrid>
                </MotionBox>
                {renderPager(filtered.length, safePage, setPage)}
              </>
            ) : (
              <Box py={16} textAlign="center" color="gray.500">
                {isZh ? '没有匹配的文章，换个关键词或分类试试。' : 'No matching posts — try another keyword or category.'}
              </Box>
            )}
          </Container>
        </Box>
      ) : (
      <>
      {/* ===== 1. Hero（含搜索 + 热门标签） ===== */}
      <Box as="section" className="gradient-mesh" position="relative" pt={{ base: 10, md: 14 }} pb={{ base: 10, md: 12 }} sx={{ scrollMarginTop: '128px' }}>
        {/* 装饰光斑单独包一层并裁剪，避免 section 自身 overflow:hidden 把搜索联想面板裁掉 */}
        <Box aria-hidden="true" position="absolute" inset={0} overflow="hidden" pointerEvents="none">
          <Box w="420px" h="420px" rounded="full" bg="blue.200" opacity={0.32} filter="blur(90px)" top="-160px" right="-100px" className="animate-float" />
          <Box w="320px" h="320px" rounded="full" bg="amber.200" opacity={0.28} filter="blur(80px)" bottom="-140px" left="-70px" className="animate-float-slow" sx={{ animationDelay: '-5s' }} />
        </Box>
        <Container maxW="5xl" position="relative" zIndex={1}>
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" letterSpacing="0.08em" fontSize="sm">
              {b.hero.eyebrow}
            </Badge>
            <Heading as="h1" mt={5} fontSize={{ base: '3xl', md: '5xl' }} lineHeight={1.15} color="gray.900">
              <Box as="span" display="block" fontWeight="600">{b.hero.title}</Box>
              <Box as="span" display="block" fontWeight="800" className="gradient-text" mt={1}>{b.hero.titleBold}</Box>
            </Heading>
            <Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.8} maxW="2xl" mx="auto">
              {b.hero.subtitle}
            </Text>

            {/* 搜索框（含联想面板） */}
            <Box mt={8}>
              {renderSearch({ size: 'lg', maxW: 'xl' })}
            </Box>

            {/* 热门标签 */}
            <HStack mt={5} spacing={2} justify="center" flexWrap="wrap">
              <Text fontSize="sm" color="gray.500" mr={1}>{isZh ? '热门：' : 'Trending:'}</Text>
              {b.hero.tags.map((t) => (
                <Button
                  key={t.label}
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  rounded="full"
                  fontWeight="medium"
                  bg="white"
                  borderColor="blue.200"
                  color="blue.700"
                  _hover={{ bg: 'blue.50' }}
                  onClick={() => goToList(null, t.query)}
                >
                  {t.label}
                </Button>
              ))}
            </HStack>
          </Box>
        </Container>
      </Box>

      {/* ===== 2. Latest（主推 + 两条次推） ===== */}
      <Box as="section" className="gradient-mesh" py={{ base: 10, md: 14 }}>
        <Container maxW="6xl">
          <HStack mb={8} align="flex-end" justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <HStack align="center" spacing={3}>
                <Box w="10px" h="10px" rounded="full" bg="amber.500" />
                <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.900">{b.sections.latest.title}</Heading>
              </HStack>
              <Text mt={2} fontSize="sm" color="gray.600">{b.sections.latest.subtitle}</Text>
            </Box>
            <HStack as="button" spacing={2} color="blue.600" fontWeight="medium" fontSize="sm" className="link-underline" onClick={() => goToList('all', '')}>
              <Text>{b.sections.viewAll}</Text>
              <Icon as={FiArrowRight} />
            </HStack>
          </HStack>

          <Grid templateColumns={{ base: '1fr', lg: '1.15fr 1fr' }} gap={6} alignItems="stretch">
            {/* 主推大卡 */}
            <MotionBox
              className="hover-lift"
              cursor="pointer"
              borderRadius="2xl"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="md"
              bg="white"
              display="flex"
              flexDirection="column"
              onClick={() => onOpenPost && onOpenPost(b.featured)}
              {...fade()}
            >
              <Box position="relative" bg="blue.50" h={{ base: '200px', md: '250px' }} overflow="hidden">
                {imgOf(b.featured) ? (
                  <Image src={imgOf(b.featured)} alt="" position="absolute" inset={0} w="full" h="full" objectFit="cover" />
                ) : (
                  <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" color="blue.300">
                    <Icon as={FiImage} boxSize={12} />
                  </Box>
                )}
                <Box position="absolute" top={4} left={4}>
                  <Badge colorScheme="blue" variant="solid" px={3} py={1} rounded="full" fontSize="xs" fontWeight="bold">
                    {b.featured.tag}
                  </Badge>
                </Box>
              </Box>
              <Stack flex="1" p={{ base: 6, md: 8 }} spacing={4} justify="center">
                <Heading fontSize={{ base: 'xl', md: '2xl' }} lineHeight={1.35} color="gray.900">
                  {b.featured.title}
                </Heading>
                <Text fontSize="md" color="gray.600" lineHeight={1.8} noOfLines={3}>
                  {b.featured.excerpt}
                </Text>
                <HStack spacing={3} fontSize="sm" color="gray.500" pt={2}>
                  <Text>{b.featured.date}</Text>
                  <Text>·</Text>
                  <Text>{b.featured.readTime}</Text>
                </HStack>
              </Stack>
            </MotionBox>

            {/* 右侧两条 */}
            <Stack spacing={6}>
              {b.editorPicks.map((p, i) => (
                <MotionBox
                  key={p.title}
                  className="hover-lift"
                  cursor="pointer"
                  borderRadius="2xl"
                  overflow="hidden"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  bg="white"
                  display={{ base: 'block', sm: 'flex' }}
                  flex="1"
                  onClick={() => onOpenPost && onOpenPost(p)}
                  {...fade(0.08 + i * 0.08)}
                >
                  <Box position="relative" bg={i % 2 === 0 ? 'amber.50' : 'teal.50'} flex={{ sm: '0 0 38%' }} minH={{ base: '140px', sm: 'auto' }} overflow="hidden">
                    {imgOf(p) ? (
                      <Image src={imgOf(p)} alt="" position="absolute" inset={0} w="full" h="full" objectFit="cover" />
                    ) : (
                      <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" color={i % 2 === 0 ? 'amber.300' : 'teal.300'}>
                        <Icon as={FiImage} boxSize={8} />
                      </Box>
                    )}
                  </Box>
                  <Stack flex="1" p={5} spacing={2.5} justify="center">
                    <Badge colorScheme={i % 2 === 0 ? 'amber' : 'teal'} variant="subtle" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="bold" w="fit-content">
                      {p.tag}
                    </Badge>
                    <Heading fontSize="lg" lineHeight={1.4} color="gray.900" noOfLines={2}>{p.title}</Heading>
                    <Text fontSize="sm" color="gray.600" lineHeight={1.7} noOfLines={2}>{p.excerpt}</Text>
                    <HStack spacing={2} fontSize="xs" color="gray.500">
                      <Text>{p.date}</Text>
                      <Text>·</Text>
                      <Text>{p.readTime}</Text>
                    </HStack>
                  </Stack>
                </MotionBox>
              ))}
            </Stack>
          </Grid>
        </Container>
      </Box>

      {/* ===== 3. 产品动态 ===== */}
      <Box as="section" bg="white" py={{ base: 10, md: 14 }}>
        <Container maxW="6xl">
          <HStack mb={8} align="flex-end" justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <HStack align="center" spacing={3}>
                <Box w="10px" h="10px" rounded="full" bg="blue.500" />
                <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.900">{b.sections.news.title}</Heading>
              </HStack>
              <Text mt={2} fontSize="sm" color="gray.600">{b.sections.news.subtitle}</Text>
            </Box>
            <HStack as="button" spacing={2} color="blue.600" fontWeight="medium" fontSize="sm" className="link-underline" onClick={() => goToList('product', '')}>
              <Text>{b.sections.viewAll}</Text>
              <Icon as={FiArrowRight} />
            </HStack>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {news.map((p, i) => renderMiniCard(p, i))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== 4. 客户故事 ===== */}
      <Box as="section" className="gradient-mesh" py={{ base: 10, md: 14 }}>
        <Container maxW="6xl">
          <HStack mb={8} align="flex-end" justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <HStack align="center" spacing={3}>
                <Box w="10px" h="10px" rounded="full" bg="amber.500" />
                <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.900">{b.sections.stories.title}</Heading>
              </HStack>
              <Text mt={2} fontSize="sm" color="gray.600">{b.sections.stories.subtitle}</Text>
            </Box>
            <HStack as="button" spacing={2} color="blue.600" fontWeight="medium" fontSize="sm" className="link-underline" onClick={() => goToList('usecase', '')}>
              <Text>{b.sections.viewAll}</Text>
              <Icon as={FiArrowRight} />
            </HStack>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {stories.map((p, i) => renderMiniCard(p, i + 1))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== 5. 视频与实录（占位缩略图，接入真实视频只需补 src） =====
          老板要求视频往后放、排在「全部文章」前面：先看文章，再决定要不要看视频。 */}
      <Box as="section" bg="white" py={{ base: 10, md: 14 }}>
        <Container maxW="6xl">
          <HStack mb={8} align="flex-end" justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <HStack align="center" spacing={3}>
                <Box w="10px" h="10px" rounded="full" bg="blue.500" />
                <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.900">{b.videos.title}</Heading>
              </HStack>
              <Text mt={2} fontSize="sm" color="gray.600">{b.videos.subtitle}</Text>
            </Box>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {b.videos.items.map((v, i) => renderVideoCard(v, i))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== 6. 全部文章（筛选在顶部子导航，这里只显示结果） ===== */}
      <Box as="section" id="all-posts" bg="white" py={{ base: 10, md: 14 }} sx={{ scrollMarginTop: '128px' }}>
        <Container maxW="6xl">
          <HStack mb={6} align="baseline" justify="space-between" flexWrap="wrap" gap={3}>
            <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.900">
              {isZh ? '全部文章' : 'All posts'}
            </Heading>
            <Text fontSize="sm" color="gray.500">
              {filtered.length} {isZh ? '篇' : 'articles'}
              {q ? (isZh ? ` · 关键词“${query}”` : ` · for “${query}”`) : ''}
            </Text>
          </HStack>

          {(q || activeCat !== 'all') && (
            <HStack mb={5} spacing={3} align="center">
              <Text fontSize="sm" color="gray.500">
                {isZh ? '当前分类：' : 'Filter: '}
                <Text as="span" color="gray.800" fontWeight="medium">
                  {(b.categories.find((x) => x.key === activeCat) || {}).label}
                </Text>
              </Text>
              <Button size="xs" variant="outline" colorScheme="gray" rounded="full" leftIcon={<Icon as={FiRepeat} />} onClick={() => { setQuery(''); setActiveCat('all'); }}>
                {isZh ? '重置筛选' : 'Reset filters'}
              </Button>
            </HStack>
          )}

          {/* 动画只挂在容器上（once: true），翻页时列表直接换内容，不会整块重新淡入 */}
          <MotionBox {...fade()}>
          <Stack spacing={4}>
            {paged.map((p) => (
              <Box
                key={p.title}
                className="hover-lift"
                cursor="pointer"
                p={{ base: 4, md: 6 }}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                _hover={{ borderColor: 'blue.300' }}
                onClick={() => onOpenPost && onOpenPost(p)}
              >
                {postRowInner(p)}
              </Box>
            ))}
            {/* 补足到每页条数：翻页时列表高度不变，页码条不会上下跳 */}
            {totalPages > 1 && Array.from({ length: Math.max(0, PAGE_SIZE - paged.length) }).map((_, idx) => (
              <Box
                key={`ph-${idx}`}
                aria-hidden="true"
                visibility="hidden"
                p={{ base: 4, md: 6 }}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
              >
                {postRowInner(paged[idx] || b.allPosts[0])}
              </Box>
            ))}
            {filtered.length === 0 && (
              <Box py={12} textAlign="center" color="gray.500" fontSize="sm">
                {isZh ? '没有匹配的文章，换个关键词或分类试试。' : 'No matching posts — try another keyword or category.'}
              </Box>
            )}
          </Stack>
          </MotionBox>
          {renderPager(filtered.length, safePage, setPage)}
        </Container>
      </Box>

      {/* ===== 7. Subscribe ===== */}
      <Box as="section" className="gradient-mesh" py={{ base: 10, md: 14 }} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="3xl">
          <MotionBox
            p={{ base: 8, md: 12 }}
            bg="white"
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            textAlign="center"
            {...fade()}
          >
            <Flex w="52px" h="52px" mx="auto" mb={4} rounded="2xl" bg="blue.50" color="blue.600" align="center" justify="center">
              <Icon as={FiMail} boxSize={5} />
            </Flex>
            <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.900" mb={3}>
              {b.subscribe.title}
            </Heading>
            <Text color="gray.600" fontSize="md" mb={6} lineHeight={1.8} maxW="xl" mx="auto">
              {b.subscribe.subtitle}
            </Text>
            {subscribed ? (
              <HStack justify="center" spacing={2} color="green.600" fontWeight="medium" py={3}>
                <Icon as={FiCheckCircle} />
                <Text>{b.subscribe.success}</Text>
              </HStack>
            ) : (
              <HStack as="form" maxW="md" mx="auto" spacing={2} onSubmit={(e) => {
                e.preventDefault();
                if (!email || subscribing) return;
                setSubscribing(true);
                setSubscribeError('');
                subscribeInsights(email, lang)
                  .then(() => setSubscribed(true))
                  .catch((err) => setSubscribeError(b.subscribe[{ invalid_email: 'invalidEmail', too_many_requests: 'tooMany' }[err.message]] || b.subscribe.error))
                  .finally(() => setSubscribing(false));
              }}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={b.subscribe.placeholder}
                  size="lg"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #29ABE2' }}
                  required
                />
                <Button type="submit" variant="amber" size="lg" px={8} isLoading={subscribing}>
                  {b.subscribe.cta}
                </Button>
              </HStack>
            )}
            {subscribeError && !subscribed && (
              <Text role="alert" color="red.600" fontSize="sm" mt={3}>{subscribeError}</Text>
            )}
          </MotionBox>
        </Container>
      </Box>
      </>
      )}

      {/* ===== 视频弹窗（接入真实视频只需把播放器塞进 AspectRatio） ===== */}
      {/* 注意：overlay 不要加 backdropFilter，blur 会让开关动画明显掉帧 */}
      <Modal isOpen={videoModal.isOpen} onClose={videoModal.onClose} size="3xl" isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0B1220" color="white" borderRadius="2xl" overflow="hidden" overflowY="auto" maxH="92vh" mx={4}>
          <ModalCloseButton color="whiteAlpha.800" zIndex={3} _hover={{ bg: 'whiteAlpha.200' }} />
          <Box position="relative" bgGradient="linear(to-br, #0B3D5C, #1B6A94)" px={6} pt={10} pb={6}>
            <AspectRatio ratio={16 / 9}>
              <Flex direction="column" align="center" justify="center" gap={3} textAlign="center">
                <Flex w="72px" h="72px" rounded="full" bg="whiteAlpha.200" color="white" align="center" justify="center">
                  <Icon as={FiPlay} boxSize={8} ml="1" />
                </Flex>
                <Text fontSize="sm" color="blue.100" maxW="sm" lineHeight={1.7}>{b.videos.modalNote}</Text>
              </Flex>
            </AspectRatio>
          </Box>
          {videoItem && (
            <ModalBody py={6} px={6}>
              <HStack spacing={3} mb={3} flexWrap="wrap">
                <Badge colorScheme="blue" variant="solid" rounded="full" px={2.5} py={0.5} fontSize="xs" fontWeight="bold">
                  {videoItem.tag}
                </Badge>
                <Text fontSize="xs" color="whiteAlpha.700">{videoItem.duration} · {videoItem.date}</Text>
              </HStack>
              <Heading fontSize={{ base: 'lg', md: 'xl' }} color="white" lineHeight={1.4} mb={3}>
                {videoItem.title}
              </Heading>
              <Text fontSize="sm" color="whiteAlpha.800" lineHeight={1.8}>{videoItem.desc}</Text>
            </ModalBody>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};


// ============ Insights article detail view ============
const POST_COVER = { blue: 'blue.50', amber: 'amber.50', teal: 'teal.50' };
const POST_COVER_ICON = { blue: 'blue.300', amber: 'amber.300', teal: 'teal.300' };

const BlogPostView = ({ lang, post, onBack, onOpenPost, onCta }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const b = c.blog;
  const t = b.article;
  const isZh = lang === 'zh';
  const art = (blogArticles[lang] || blogArticles.en)[post && post.id] || null;
  const body = art ? art.body : [];

  const [activeSec, setActiveSec] = useState(0);
  const [readProgress, setReadProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  // 目录：由正文里的 h2 小节自动生成
  const sections = body.map((blk, i) => ({ blk, i })).filter((x) => x.blk.type === 'h2');

  // 目录高亮 + 阅读进度（跟随滚动）
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setReadProgress(max > 0 ? Math.min(1, Math.max(0, el.scrollTop / max)) : 0);
      let idx = 0;
      sections.forEach((s, k) => {
        const node = document.getElementById(`sec-${s.i}`);
        if (node && node.getBoundingClientRect().top <= 150) idx = k;
      });
      setActiveSec(idx);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post && post.id, body.length]);

  const jump = (i) => {
    const node = document.getElementById(`sec-${i}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* clipboard unavailable */ }
  };

  // 上下篇 / 相关阅读
  const combined = [b.featured, ...b.editorPicks, ...b.allPosts].filter((p) => p && p.id);
  const idx = combined.findIndex((p) => p.id === (post && post.id));
  const prev = idx > 0 ? combined[idx - 1] : null;
  const next = idx >= 0 && idx < combined.length - 1 ? combined[idx + 1] : null;
  const sameTag = b.allPosts.filter((p) => p.id !== (post && post.id) && p.tag === (post && post.tag));
  const related = (sameTag.length ? sameTag : b.allPosts.filter((p) => p.id !== (post && post.id))).slice(0, 3);

  const coverKey = (art && art.cover) || 'blue';

  const renderBlock = (blk, i) => {
    switch (blk.type) {
      case 'h2':
        return (
          <Heading
            key={i}
            id={`sec-${i}`}
            as="h2"
            mt={{ base: 8, md: 10 }}
            mb={4}
            fontSize={{ base: 'xl', md: '2xl' }}
            lineHeight={1.35}
            color="gray.900"
            sx={{ scrollMarginTop: '96px' }}
          >
            {blk.text}
          </Heading>
        );
      case 'p':
        return (
          <Text key={i} mb={6} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.9}>
            {blk.text}
          </Text>
        );
      case 'ul':
        return (
          <VStack key={i} align="stretch" spacing={3} mb={8}>
            {blk.items.map((it, k) => (
              <Flex key={k} align="flex-start" gap={3}>
                <Box mt="11px" w="6px" h="6px" rounded="full" bg="blue.400" flexShrink={0} />
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.8}>{it}</Text>
              </Flex>
            ))}
          </VStack>
        );
      case 'quote':
        return (
          <Box key={i} my={10} pl={{ base: 5, md: 7 }} borderLeft="4px solid" borderColor="blue.400">
            <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="semibold" color="gray.900" lineHeight={1.5} fontStyle="italic">
              {blk.text}
            </Text>
          </Box>
        );
      case 'stat':
        return (
          <SimpleGrid
            key={i}
            columns={{ base: 1, sm: blk.items.length }}
            spacing={4}
            my={8}
            p={{ base: 5, md: 7 }}
            borderRadius="2xl"
            className="gradient-mesh"
            border="1px solid"
            borderColor="blue.100"
          >
            {blk.items.map((s, k) => (
              <Box key={k} textAlign="center">
                <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800" color="blue.600" lineHeight={1.1}>{s.value}</Text>
                <Text mt={2} fontSize="sm" color="gray.600" lineHeight={1.5}>{s.label}</Text>
              </Box>
            ))}
          </SimpleGrid>
        );
      case 'callout':
        return (
          <Box key={i} my={8} p={{ base: 5, md: 6 }} bg="amber.50" borderRadius="xl" borderLeft="4px solid" borderColor="amber.400">
            <HStack spacing={2} mb={2} color="amber.700">
              <Icon as={FiInfo} />
              <Text fontWeight="bold">{blk.title}</Text>
            </HStack>
            <Text color="gray.700" lineHeight={1.8}>{blk.text}</Text>
          </Box>
        );
      case 'img':
        return (
          <Box key={i} my={8}>
            <Box position="relative" borderRadius="2xl" overflow="hidden" bg={POST_COVER[coverKey]} h={{ base: '200px', md: '320px' }} border="1px solid" borderColor="gray.200">
              {blk.src ? (
                <Image src={blk.src} alt={blk.caption || ''} position="absolute" inset={0} w="full" h="full" objectFit="cover" />
              ) : (
                <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" color={POST_COVER_ICON[coverKey]}>
                  <Icon as={FiImage} boxSize={12} />
                </Box>
              )}
            </Box>
            {blk.caption && <Text mt={3} fontSize="sm" color="gray.500" textAlign="center">{blk.caption}</Text>}
          </Box>
        );
      default:
        return null;
    }
  };

  // 封面图：从 allPosts 里按 id 回查。主推卡 / 编辑精选这两份数据没有 img 字段，
  // 直接读 post.img 会拿不到，正文页就退回占位块了。
  const coverImg =
    (post && post.img) ||
    ((c.blog.allPosts.find((x) => x.id === post.id) || {}).img) ||
    '';

  // 找不到文章时的兜底
  if (!art) {
    return (
      <Box pt="64px" minH="70vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4} textAlign="center">
          <Heading fontSize="2xl" color="gray.900">{t.notFound}</Heading>
          <Button variant="outline" colorScheme="blue" leftIcon={<Icon as={FiArrowLeft} />} onClick={onBack}>
            {t.notFoundBack}
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box pt="64px" pb={16}>
      {/* ===== 文章头部（含封面） ===== */}
      <Box as="section" className="gradient-mesh" position="relative" overflow="hidden" pt={{ base: 8, md: 12 }} pb={{ base: 8, md: 10 }}>
        <Box aria-hidden="true" position="absolute" w="360px" h="360px" rounded="full" bg="blue.200" opacity={0.28} filter="blur(90px)" top="-150px" right="-90px" className="animate-float" />
        <Container maxW="4xl" position="relative" zIndex={1}>
          <Button
            variant="ghost"
            size="sm"
            colorScheme="blue"
            leftIcon={<Icon as={FiArrowLeft} />}
            onClick={onBack}
            px={2}
            mb={5}
            fontWeight="medium"
            _hover={{ bg: 'blue.50' }}
          >
            {t.back}
          </Button>

          <HStack spacing={3} mb={4} flexWrap="wrap">
            <Badge colorScheme="blue" variant="solid" px={3} py={1} rounded="full" fontSize="xs" fontWeight="bold">
              {post.tag}
            </Badge>
            <Text fontSize="sm" color="gray.500">{post.date}</Text>
            <Text fontSize="sm" color="gray.400">·</Text>
            <HStack spacing={1} fontSize="sm" color="gray.500">
              <Icon as={FiClock} boxSize={3.5} />
              <Text>{post.readTime}</Text>
            </HStack>
          </HStack>

          <Heading as="h1" fontSize={{ base: '2xl', md: '4xl' }} lineHeight={1.25} color="gray.900" letterSpacing="-0.01em">
            {post.title}
          </Heading>

          {post.excerpt && (
            <Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color="gray.600" lineHeight={1.7}>
              {post.excerpt}
            </Text>
          )}

          {/* 作者 */}
          <HStack mt={5} spacing={3}>
            <Flex w="36px" h="36px" rounded="full" bg="blue.100" color="blue.700" align="center" justify="center" fontWeight="bold" fontSize="sm" flexShrink={0}>
              {(art.author.name || '?').slice(0, 1)}
            </Flex>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.900">{art.author.name}</Text>
              <Text fontSize="xs" color="gray.500">{art.author.role}</Text>
            </Box>
          </HStack>
        </Container>

        {/* 封面图：有配图就渲染真实图片，没有的仍用配色占位块 */}
        <Container maxW="5xl" position="relative" zIndex={1} mt={{ base: 6, md: 8 }}>
          <Box
            position="relative"
            borderRadius="2xl"
            overflow="hidden"
            bg={POST_COVER[coverKey]}
            h={{ base: '190px', md: '340px' }}
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
          >
            {coverImg ? (
              <Image src={coverImg} alt={post.title || ''} position="absolute" inset={0} w="full" h="full" objectFit="cover" />
            ) : (
              <Box position="absolute" inset={0} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} color={POST_COVER_ICON[coverKey]}>
                <Icon as={FiImage} boxSize={{ base: 10, md: 12 }} />
                <Text fontSize="sm" fontWeight="medium">{isZh ? '封面图占位' : 'Cover image placeholder'}</Text>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* ===== 正文 ===== */}
      <Box bg="white" pt={{ base: 8, md: 12 }} pb={{ base: 6, md: 8 }}>
        <Container maxW="6xl">
          <Grid templateColumns={{ base: '1fr', lg: '240px minmax(0, 1fr)' }} gap={{ base: 0, lg: 12 }} alignItems="start">
            {/* 侧栏：目录卡片 + 订阅（桌面端） */}
            <Box display={{ base: 'none', lg: 'block' }} position="sticky" top="96px">
              <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="white" p={4} boxShadow="sm">
                <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.08em" textTransform="uppercase" mb={3}>
                  {t.toc}
                </Text>

                {/* 阅读进度（放在目录卡片内，替代原来页面顶部的进度条） */}
                <Flex align="center" gap={2} mb={3}>
                  <Box flex="1" h="3px" rounded="full" bg="gray.100" overflow="hidden">
                    <Box h="100%" w={`${readProgress * 100}%`} bg="blue.400" rounded="full" transition="width 0.15s linear" />
                  </Box>
                  <Text fontSize="xs" color="gray.400" fontWeight="medium" flexShrink={0}>
                    {sections.length ? Math.min(activeSec + 1, sections.length) : 0}/{sections.length}
                  </Text>
                </Flex>

                <VStack align="stretch" spacing={0.5}>
                  {sections.map((s, k) => (
                    <Box
                      key={s.i}
                      as="button"
                      display="flex"
                      alignItems="flex-start"
                      gap={2.5}
                      textAlign="left"
                      py={1.5}
                      px={2}
                      borderRadius="md"
                      bg={activeSec === k ? 'blue.50' : 'transparent'}
                      transition="all 0.2s"
                      _hover={{ bg: 'blue.50' }}
                      onClick={() => jump(s.i)}
                    >
                      <Box
                        mt="7px"
                        w={activeSec === k ? '7px' : '6px'}
                        h={activeSec === k ? '7px' : '6px'}
                        rounded="full"
                        bg={activeSec === k ? 'blue.500' : 'gray.300'}
                        flexShrink={0}
                        transition="all 0.2s"
                      />
                      <Text
                        fontSize="sm"
                        lineHeight={1.45}
                        fontWeight={activeSec === k ? 'semibold' : 'normal'}
                        color={activeSec === k ? 'blue.700' : 'gray.600'}
                      >
                        {s.blk.text}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>

              {/* 订阅：放在目录下方，替代页面底部的大块 CTA */}
              <Box mt={4} p={4} borderRadius="xl" bgGradient="linear(to-br, blue.500, blue.700)" color="white">
                <Text fontSize="sm" fontWeight="bold" mb={1}>{t.sideCtaTitle}</Text>
                <Text fontSize="xs" color="blue.50" lineHeight={1.6} mb={3}>{t.sideCtaText}</Text>
                <Button size="sm" variant="amber" w="full" onClick={onCta}>{t.ctaButton}</Button>
              </Box>
            </Box>

            {/* 正文内容 */}
            <Box maxW="720px">
              {body.map((blk, i) => renderBlock(blk, i))}

              {/* 作者 + 分享（合并为一行，减少纵向占用） */}
              <Divider my={8} />
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
                <HStack spacing={3}>
                  <Flex w="40px" h="40px" rounded="full" bg="blue.100" color="blue.700" align="center" justify="center" fontWeight="bold" fontSize="sm" flexShrink={0}>
                    {(art.author.name || '?').slice(0, 1)}
                  </Flex>
                  <Box>
                    <Text fontSize="xs" color="gray.500">{t.writtenBy}</Text>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.900">{art.author.name} · {art.author.role}</Text>
                  </Box>
                </HStack>
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" colorScheme="blue" rounded="full" leftIcon={<Icon as={copied ? FiCheck : FiLink2} />} onClick={copyLink}>
                    {copied ? t.copied : t.copyLink}
                  </Button>
                  <Button size="sm" variant="ghost" colorScheme="gray" rounded="full" leftIcon={<Icon as={FiMail} />} onClick={onCta}>
                    {isZh ? '邮件咨询' : 'Email us'}
                  </Button>
                </HStack>
              </Flex>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* ===== 上下篇（带色彩，避免生硬的方框） ===== */}
      <Box bg="white" pb={{ base: 8, md: 10 }}>
        <Container maxW="6xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {[prev, next].map((p, i) => {
              const tone = i === 0 ? 'blue' : 'amber';
              const empty = !p;
              return (
                <Box
                  key={i}
                  as="button"
                  textAlign="left"
                  p={5}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={empty ? 'gray.200' : `${tone}.200`}
                  bgGradient={empty ? undefined : `linear(to-br, ${tone}.50, white)`}
                  bg={empty ? 'gray.50' : undefined}
                  transition="all 0.25s"
                  _hover={empty ? {} : { borderColor: `${tone}.300`, transform: 'translateY(-3px)', boxShadow: 'lg' }}
                  onClick={() => p && onOpenPost(p)}
                  cursor={empty ? 'default' : 'pointer'}
                  opacity={empty ? 0.6 : 1}
                >
                  <Flex align="center" gap={3} mb={2.5}>
                    <Flex
                      w="32px"
                      h="32px"
                      rounded="full"
                      bg={empty ? 'gray.300' : `${tone}.500`}
                      color="white"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={i === 0 ? FiArrowLeft : FiArrowRight} boxSize={4} />
                    </Flex>
                    <Text fontSize="xs" fontWeight="bold" letterSpacing="0.06em" textTransform="uppercase" color={empty ? 'gray.400' : `${tone}.700`}>
                      {i === 0 ? t.prev : t.next}
                    </Text>
                  </Flex>
                  <Text fontSize="md" fontWeight="semibold" color={empty ? 'gray.400' : 'gray.900'} lineHeight={1.5} noOfLines={2}>
                    {p
                      ? p.title
                      : (i === 0
                        ? (isZh ? '已经是第一篇了' : 'This is the first post')
                        : (isZh ? '已经是最后一篇了' : 'This is the last post'))}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== 相关阅读（紧凑文字卡） ===== */}
      <Box as="section" className="gradient-mesh" py={{ base: 8, md: 10 }}>
        <Container maxW="6xl">
          <HStack mb={6} align="center" spacing={3}>
            <Box w="10px" h="10px" rounded="full" bg="amber.500" />
            <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900">{t.related}</Heading>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {related.map((p, i) => (
              <MotionBox
                key={p.id || p.title}
                className="hover-lift"
                cursor="pointer"
                p={5}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                onClick={() => onOpenPost(p)}
              >
                <Badge colorScheme={['blue', 'amber', 'teal'][i % 3]} variant="subtle" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="bold" mb={3}>
                  {p.tag}
                </Badge>
                <Heading fontSize="md" lineHeight={1.4} color="gray.900" fontWeight="semibold" noOfLines={2} mb={3}>
                  {p.title}
                </Heading>
                <HStack spacing={2} fontSize="xs" color="gray.500">
                  <Text>{p.date}</Text>
                  <Text>·</Text>
                  <Text>{p.readTime}</Text>
                </HStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== 移动端订阅（桌面端已在侧栏目录下方） ===== */}
      <Box as="section" bg="white" pt={{ base: 6, md: 8 }} display={{ base: 'block', lg: 'none' }}>
        <Container maxW="6xl">
          <Box p={6} borderRadius="xl" bgGradient="linear(to-br, blue.500, blue.700)" color="white" textAlign="center">
            <Text fontWeight="bold" mb={1}>{t.sideCtaTitle}</Text>
            <Text fontSize="sm" color="blue.50" mb={4}>{t.sideCtaText}</Text>
            <Button size="sm" variant="amber" onClick={onCta}>{t.ctaButton}</Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};


// ============ Pricing view ============
const PricingView = ({ lang, onContactSales, onHelp }) => {
  const p = landingContent[lang].pricing;
  const fb = p.feeBreakdown;

  return (
    <Box pt={{ base: '100px', md: '120px' }} pb={20} minH="100vh" position="relative" overflow="hidden">
      {/* animated decorative blobs */}
      <Box aria-hidden="true" position="absolute" w="460px" h="460px" rounded="full" bg="blue.200" opacity={0.3} filter="blur(90px)" top="-180px" right="-120px" className="animate-float" />
      <Box aria-hidden="true" position="absolute" w="360px" h="360px" rounded="full" bg="amber.200" opacity={0.25} filter="blur(80px)" bottom="20%" left="-140px" className="animate-float-slow" sx={{ animationDelay: '-5s' }} />
      <Container maxW="5xl" position="relative" zIndex={1}>
        {/* Centered header */}
        <VStack spacing={4} textAlign="center">
          <HStack justify="center" spacing={2}>
            <Box w="10px" h="10px" rounded="full" bg="amber.500" />
            <Text fontSize="md" fontWeight="bold" color="gray.500" letterSpacing="wider">
              {p.eyebrow}
            </Text>
          </HStack>
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '5xl' }}
            lineHeight={1.25}
            color="gray.900"
          >
            {p.titleLine1}
            <br />
            <Text as="span" className="gradient-text">
              {p.titleLine2}
            </Text>
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }} maxW="2xl" lineHeight={1.85}>
            {p.subtitle}
          </Text>
        </VStack>

        {/* Price card + refund card, close together */}
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={4}
          mt={{ base: 10, md: 12 }}
          align="stretch"
        >
          {/* Main pricing card */}
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            rounded="2xl"
            shadow="sm"
            flex={{ lg: 1.7 }}
            p={{ base: 6, md: 8 }}
          >
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {p.planLabel}
            </Text>

            <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 5, md: 8 }} align={{ md: 'center' }} mt={4}>
              {/* Price */}
              <Box w={{ md: '190px' }} flexShrink={0}>
                <HStack align="baseline" spacing={2}>
                  <Text fontSize="5xl" fontWeight="extrabold" color="brand.navy" lineHeight={1}>
                    {p.price}
                  </Text>
                  <Text fontSize="md" color="gray.500" fontWeight="medium">
                    {p.perMonth}
                  </Text>
                  <Text fontSize="lg" color="gray.400" textDecoration="line-through">
                    {p.wasPrice}
                  </Text>
                </HStack>
                <Text fontSize="xs" color="blue.600" mt={2}>
                  {p.priceNote}
                </Text>
              </Box>

              <Divider display={{ base: 'block', md: 'none' }} borderColor="gray.200" />
              <Divider
                orientation="vertical"
                display={{ base: 'none', md: 'block' }}
                h="72px"
                borderColor="gray.200"
              />

              {/* Features */}
              <VStack align="stretch" spacing={3} flex={1}>
                {p.features.map((feat) => (
                  <HStack key={feat} spacing={2.5} align="flex-start">
                    <Icon as={FiCheckCircle} color="blue.500" boxSize="18px" mt="2px" flexShrink={0} />
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                      {feat}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Flex>

            {/* Actions */}
            <HStack spacing={3} mt={7}>
              <Button variant="outline" flex={1} onClick={onContactSales}>
                {p.contactSales}
              </Button>
              <Button
                as="a"
                href={PARTNER_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="amber"
                flex={1}
              >
                {p.getStarted}
              </Button>
            </HStack>
          </MotionBox>

          {/* Refund guarantee card */}
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.08 }}
            bg="amber.50"
            border="1px solid"
            borderColor="amber.200"
            rounded="2xl"
            flex={{ lg: 1 }}
            p={7}
            textAlign="center"
            justify="center"
          >
            <Flex
              w="48px"
              h="48px"
              rounded="full"
              bg="amber.500"
              color="white"
              align="center"
              justify="center"
              mx="auto"
              mb={4}
            >
              <Icon as={FiShield} boxSize={6} />
            </Flex>
            <Text fontWeight="bold" color="amber.700" fontSize="md">
              {p.refundTitle}
            </Text>
            <Text color="gray.600" fontSize="sm" lineHeight={1.8} mt={3}>
              {p.refundText}
            </Text>
          </MotionBox>
        </Flex>

        {/* Bottom assurances */}
        <HStack
          as="ul"
          spacing={0}
          mt={8}
          justify="center"
          flexWrap="wrap"
          divider={
            <Text as="span" color="gray.300" px={3}>
              ·
            </Text>
          }
        >
          {p.assurances.map((item) => (
            <Text key={item} as="li" fontSize="xs" color="gray.500">
              {item}
            </Text>
          ))}
        </HStack>

        {/* ============ 费用构成四类 —— 先讲构成，下面再给明细表 ============ */}
        <Box mt={16}>
          <VStack spacing={3} textAlign="center" mb={8}>
            <HStack justify="center" spacing={2}>
              <Box w="10px" h="10px" rounded="full" bg="amber.500" />
              <Text fontSize="md" fontWeight="bold" color="gray.500" letterSpacing="wider">
                {p.otherCostsTitle}
              </Text>
            </HStack>
            <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }} maxW="2xl" lineHeight={1.8}>
              {p.otherCostsText}
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
            {p.otherCosts.map((item, i) => {
              const Icon = { FiPhone, FiMic, FiMessageSquare, FiZap }[item.icon] || FiInfo;
              return (
                <MotionBox
                  key={item.title}
                  p={6}
                  bg="white"
                  rounded="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.3, delay: i * 0.06 }}
                  _hover={{ borderColor: 'blue.300', boxShadow: '0 16px 32px rgba(11,61,92,0.08)' }}
                >
                  <Flex w="44px" h="44px" rounded="full" bg="blue.500" color="white" align="center" justify="center" mb={4} boxShadow="md">
                    <Icon as={Icon} boxSize={5} />
                  </Flex>
                  <Heading fontSize="md" mb={2} color="gray.900">{item.title}</Heading>
                  <Text fontSize="sm" color="gray.700" lineHeight={1.7}>{item.text}</Text>
                </MotionBox>
              );
            })}
          </SimpleGrid>
        </Box>

        {/* ============ Screen 2: Fee Breakdown ============ */}
        <Box mt={20} id="fee-breakdown">
          {/* Section header */}
          <VStack spacing={3} textAlign="center" mb={10}>
            <HStack justify="center" spacing={2}>
              <Box w="10px" h="10px" rounded="full" bg="amber.500" />
              <Text fontSize="md" fontWeight="bold" color="gray.500" letterSpacing="wider">
                {fb.eyebrow}
              </Text>
            </HStack>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} color="gray.900">
              {fb.title}
            </Heading>
            <Text color="gray.500" fontSize={{ base: 'sm', md: 'md' }} maxW="2xl" lineHeight={1.8}>
              {fb.subtitle}
            </Text>
          </VStack>

          {/* Fee tables */}
          <VStack spacing={8} align="stretch">
            {fb.sections.map((section, sIdx) => (
              <MotionBox
                key={section.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.4, delay: sIdx * 0.1 }}
                rounded="2xl"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
                shadow="sm"
                bg="white"
              >
                {/* Section header bar */}
                <Flex
                  align="center"
                  justify="center"
                  gap={3}
                  px={{ base: 4, md: 6 }}
                  py={4}
                  bg="brand.navy"
                >
                  <Box w="4px" h="20px" bg="amber.500" rounded="full" flexShrink={0} />
                  <Text fontSize="md" fontWeight="bold" color="white" textAlign="center">
                    {section.label}
                  </Text>
                </Flex>

                {/* Table */}
                <Box overflowX="auto">
                  <Table size="sm" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <Thead>
                      <Tr bg="gray.50">
                        <Th fontWeight="bold" color="gray.600" fontSize="xs" whiteSpace="nowrap" width="26%">
                          {fb.col.item}
                        </Th>
                        <Th fontWeight="bold" color="gray.600" fontSize="xs" whiteSpace="nowrap" width="16%" textAlign="left">
                          {fb.col.unit}
                        </Th>
                        <Th fontWeight="bold" color="blue.600" fontSize="xs" whiteSpace="nowrap" width="14%" textAlign="left">
                          {fb.col.price}
                        </Th>
                        <Th fontWeight="bold" color="gray.600" fontSize="xs" whiteSpace="nowrap" width="44%">
                          {fb.col.note}
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {section.groups.map((group) => (
                        <React.Fragment key={group.label}>
                          {/* Group header row */}
                          <Tr bg="blue.50">
                            <Td colSpan={4} py={2} px={{ base: 3, md: 4 }}>
                              <Text fontSize="xs" fontWeight="bold" color="blue.700" textTransform="uppercase" letterSpacing="wider">
                                {group.label}
                              </Text>
                            </Td>
                          </Tr>
                          {/* Item rows */}
                          {group.items.map((row, i) => {
                            const notBilled = row.note === '{{notBilled}}';
                            const isTbd = row.price === '{{tbd}}';
                            const noteText = notBilled ? fb.notBilled : row.note;
                            const priceText = isTbd ? fb.tbd : row.price;
                            const isZero = row.price === '$0' || isTbd;
                            return (
                              <Tr
                                key={row.item}
                                _hover={{ bg: 'gray.50' }}
                                bg={i % 2 === 1 ? 'gray.50' : 'white'}
                                opacity={isZero ? 0.6 : 1}
                              >
                                <Td fontWeight="medium" color="gray.800" fontSize="sm" py={2.5} px={{ base: 3, md: 4 }}>
                                  {row.item}
                                </Td>
                                <Td color="gray.600" fontSize="sm" py={2.5} whiteSpace="nowrap" px={{ base: 2, md: 3 }}>
                                  {row.unit}
                                </Td>
                                <Td color={isTbd ? 'amber.600' : 'blue.600'} fontWeight="bold" fontSize="sm" py={2.5} whiteSpace="nowrap" px={{ base: 2, md: 3 }}>
                                  {priceText}
                                </Td>
                                <Td color={notBilled ? 'gray.400' : 'gray.600'} fontSize="xs" py={2.5} px={{ base: 3, md: 4 }}>
                                  {noteText}
                                </Td>
                              </Tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </MotionBox>
            ))}
          </VStack>

          {/* Footnote */}
          <Text fontSize="xs" color="gray.400" textAlign="center" mt={6} lineHeight={1.8}>
            {lang === 'zh'
              ? '以上为参考单价，最终费用以实际开通时确认为准。'
              : 'Reference unit prices above; final fees confirmed at activation.'}
          </Text>
        </Box>

        {/* 上线指南已移到帮助中心（用户先看清流程，再往下找解决方法）。
            定价页只留一条指路链接，避免同一段内容在两个页面各说一遍。 */}
        <Box mt={20} id="procurement" textAlign="center">
          <Text fontSize="sm" color="gray.600">
            {lang === 'zh' ? '想知道从付款到正式接听要走哪些步骤、费用怎么构成？' : 'Want the full path from payment to going live, and what the fees cover?'}
          </Text>
          <Button mt={4} variant="outline" size="sm" rightIcon={<FiArrowRight />} onClick={onHelp}>
            {lang === 'zh' ? '到帮助中心看上线指南' : 'Read the launch guide in the Help Center'}
          </Button>
        </Box>

        {/* ============ Pricing FAQ ============ */}
        <Box mt={20}>
          {/* 标题原来只是一个带小圆点的小字标签，太小了。
              改成跟首页 FAQ 同一套 SectionHeader（居中大标题 + 副标题）。 */}
          <SectionHeader title={p.faqsTitle} subtitle={p.faqsSubtitle} />
          <Accordion allowMultiple mt={10} border="none">
            {p.faqs.map((item) => (
              <AccordionItem key={item.q} border="1px solid" borderColor="gray.300" rounded="lg" mb={4} overflow="hidden">
                <h3>
                  <AccordionButton px={5} py={4} _expanded={{ bg: 'blue.50' }} transition="background 0.2s ease">
                    <Box flex="1" textAlign="left" fontWeight="semibold" color="gray.900">{item.q}</Box>
                    <AccordionIcon color="blue.500" />
                  </AccordionButton>
                </h3>
                <AccordionPanel pb={4} bg="gray.50" color="gray.800" lineHeight={1.8} fontSize="sm">
                  {item.a}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>

      </Container>
    </Box>
  );
};

// ============ About view ============
const AboutView = ({ lang, onCta }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const a = c.about;

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.4, delay },
  });

  /* ---------- 「安静版」卡片语言（只作用于关于我们页） ----------
     参考 Zendesk 那种舒服感：统一白底 + 一根发丝边框 + 不要阴影，
     颜色只留给图标和小的角色标签。SOFT_CARD 是产品页/首页共用的，
     这里不动它，单独定义一份，避免影响其他界面。 */
  const calmCard = {
    bg: 'white',
    rounded: '2xl',
    border: '1px solid',
    borderColor: 'gray.300',
    boxShadow: 'none',
    h: '100%',
    sx: { transition: 'border-color 0.25s ease, box-shadow 0.25s ease' },
    _hover: { borderColor: 'blue.200', boxShadow: '0 8px 24px rgba(11,61,92,0.06)' },
  };

  // 统一的区块头：居中、标题大、副标题灰，下面留足空
  const head = (eyebrow, title, subtitle, boldTitle) => (
    <VStack spacing={4} textAlign="center" maxW="3xl" mx="auto" mb={{ base: 10, md: 14 }}>
      {eyebrow ? (
        <Text fontSize="md" fontWeight="bold" color="blue.600" letterSpacing="wider">
          {eyebrow}
        </Text>
      ) : null}
      <Heading as="h2" fontSize={{ base: '2xl', md: '3xl', xl: '4xl' }} lineHeight={1.3} color="gray.900">
        {boldTitle ? (
          <>
            <Box as="span" display="block" fontWeight="600">{title}</Box>
            <Box as="span" display="block" fontWeight="800">{boldTitle}</Box>
          </>
        ) : (
          title
        )}
      </Heading>
      {subtitle ? (
        <Text color="gray.700" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.9}>
          {subtitle}
        </Text>
      ) : null}
    </VStack>
  );

  // 一句收尾的话：整页统一成「灰字居中」，不再各段一套写法
  const closing = (text, delay = 0) => (
    <MotionBox mt={{ base: 10, md: 14 }} maxW="2xl" mx="auto" textAlign="center" {...fade(delay)}>
      <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.800" fontWeight="medium" lineHeight={1.9}>
        {text}
      </Text>
    </MotionBox>
  );

  return (
    <Box>
      {/* ===== Screen 1: Hero ===== */}
      <Box as="section" className="gradient-mesh" position="relative" overflow="hidden" pt={{ base: 16, md: 24 }} pb={{ base: 16, md: 24 }}>
        {/* 只留一团很淡的光，原来三团装饰光斑太热闹 */}
        <Box aria-hidden="true" position="absolute" w="560px" h="560px" rounded="full" bg="blue.200" opacity={0.18} filter="blur(90px)" top="-240px" right="-160px" />
        <Container maxW="4xl" position="relative" zIndex={1} textAlign="center">
          <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" fontSize="sm">
            {a.hero.eyebrow}
          </Badge>
          <Heading as="h1" mt={6} fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }} lineHeight={1.22} color="gray.900">
            <Box as="span" display="block" fontWeight="600">{a.hero.title}</Box>
            <Box as="span" display="block" fontWeight="800" mt={1}>{a.hero.titleBold}</Box>
          </Heading>
          <Text mt={7} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.95}>
            {a.hero.body}
          </Text>
          <Button mt={9} variant="primary" size="lg" rightIcon={<FiArrowRight />} onClick={onCta}>
            {a.hero.cta}
          </Button>
          <Text mt={10} fontSize="sm" color="gray.600" maxW="2xl" mx="auto" lineHeight={1.8}>
            {a.hero.caption}
          </Text>
        </Container>
      </Box>

      {/* ===== Screen 1.5: What we do（先讲清楚公司做什么） ===== */}
      <Box as="section" bg="white">
        <Container maxW="6xl" py={{ base: 16, md: 24 }}>
          {head(a.focus.eyebrow, a.focus.title, null, a.focus.titleBold)}

          <MotionBox maxW="3xl" mx="auto" textAlign="center" mb={{ base: 12, md: 16 }} {...fade()}>
            <Text color="gray.700" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.9}>{a.focus.body}</Text>
          </MotionBox>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {a.focus.cards.map((card, i) => {
              const Ico = ABOUT_ICONS[card.icon] || FiInfo;
              return (
                <MotionBox key={card.title} {...calmCard} p={{ base: 7, md: 8 }} {...fade(i * 0.08)}>
                  <Flex {...softIconBox('blue', 13)} mb={6}>
                    <Icon as={Ico} boxSize={6} />
                  </Flex>
                  <Heading fontSize="lg" mb={3} color="gray.900" lineHeight={1.4}>{card.title}</Heading>
                  <Text fontSize="sm" color="gray.700" lineHeight={1.85}>{card.desc}</Text>
                </MotionBox>
              );
            })}
          </SimpleGrid>

          {closing(a.focus.caption)}
        </Container>
      </Box>

      {/* ===== Screen 2: Service details ===== */}
      <Box as="section" bg="gray.100">
        <Container maxW="6xl" py={{ base: 16, md: 24 }}>
          {head(a.details.eyebrow, a.details.title, null, a.details.titleBold)}

          <VStack maxW="3xl" mx="auto" spacing={5} textAlign="center" mb={{ base: 12, md: 16 }}>
            {a.details.body1 ? (
              <Text color="gray.700" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.9}>{a.details.body1}</Text>
            ) : null}
            {/* body2 可能为空（新稿只有一段）——空串不渲染，避免 VStack 多出一个空节点撑出间距 */}
            {a.details.body2 ? (
              <Text color="gray.700" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.9}>{a.details.body2}</Text>
            ) : null}
          </VStack>

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
            {a.details.cards.map((card, i) => {
              const Ico = ABOUT_ICONS[card.icon] || FiInfo;
              const tone = card.bg === 'yellow' ? 'amber' : 'blue';
              return (
                <MotionBox key={card.title} {...calmCard} p={{ base: 6, md: 7 }} {...fade(i * 0.06)}>
                  <Flex {...softIconBox(tone, 12)} mb={5}>
                    <Icon as={Ico} boxSize={5} />
                  </Flex>
                  <Heading fontSize="md" mb={2.5} color="gray.900" lineHeight={1.5}>{card.title}</Heading>
                  <Text fontSize="sm" color="gray.700" lineHeight={1.8}>{card.sub}</Text>
                </MotionBox>
              );
            })}
          </SimpleGrid>

          {closing(a.details.caption)}
        </Container>
      </Box>

      {/* ===== Screen 3: Technical capability（技术能力） ===== */}
      <Box as="section" bg="white">
        <Container maxW="6xl" py={{ base: 16, md: 24 }}>
          {head(a.capability.eyebrow, a.capability.title, a.capability.subtitle, a.capability.titleBold)}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {a.capability.cards.map((card, i) => (
              <MotionBox key={card.title} {...calmCard} p={{ base: 7, md: 8 }} {...fade(i * 0.07)}>
                <Text fontSize="xs" fontWeight="bold" color="blue.600" letterSpacing="wider" textTransform="uppercase" mb={4}>
                  {card.label}
                </Text>
                <Heading fontSize="lg" color="gray.900" mb={3} lineHeight={1.45}>{card.title}</Heading>
                <Text fontSize="sm" color="gray.700" lineHeight={1.85}>{card.desc}</Text>
              </MotionBox>
            ))}
          </SimpleGrid>

          {closing(a.capability.caption)}
        </Container>
      </Box>

      {/* ===== Screen 4: Values（价值观：决定做什么，也决定不做什么） ===== */}
      <Box as="section" bg="gray.100">
        <Container maxW="6xl" py={{ base: 16, md: 24 }}>
          {head(a.values.eyebrow, a.values.title, a.values.subtitle, a.values.titleBold)}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {a.values.items.map((item, i) => (
              <MotionBox key={item.title} {...calmCard} p={{ base: 7, md: 8 }} display="flex" {...fade(i * 0.07)}>
                <Flex w={9} h={9} flexShrink={0} rounded="full" bg="blue.50" color="blue.600" align="center" justify="center" fontSize="sm" fontWeight="bold" mr={5} mt="2px">
                  {i + 1}
                </Flex>
                <Box>
                  <Heading fontSize="lg" color="gray.900" mb={3} lineHeight={1.45}>{item.title}</Heading>
                  <Text fontSize="sm" color="gray.700" lineHeight={1.85}>{item.desc}</Text>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== Screen 5: Product principles =====
          已按用户要求整块删除（2026-10-19）。`about.principles` 数据仍保留在 landingContent.js，
          要恢复把这段接回来即可。 */}

      {/* ===== Screen 6: Partner（合作方：为什么「立即开通」会跳出本站） ===== */}
      <Box as="section" bg="gray.100">
        <Container maxW="6xl" py={{ base: 16, md: 24 }}>
          {head(a.partner.eyebrow, a.partner.title, a.partner.subtitle, a.partner.titleBold)}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {a.partner.sides.map((side, i) => {
              const Ico = ABOUT_ICONS[side.icon] || FiInfo;
              return (
                <MotionBox key={side.title} {...calmCard} p={{ base: 7, md: 8 }} {...fade(i * 0.08)}>
                  <Flex {...softIconBox(i === 0 ? 'blue' : 'amber', 12)} mb={5}>
                    <Icon as={Ico} boxSize={5} />
                  </Flex>
                  <Heading fontSize="lg" mb={3} color="gray.900" lineHeight="1.4">{side.title}</Heading>
                  <Text fontSize="sm" color="gray.700" lineHeight="1.85">{side.desc}</Text>
                </MotionBox>
              );
            })}
          </SimpleGrid>

          {/* 点开通之后的具体流程 —— 把用户最可能困惑的那一步摊开讲 */}
          <MotionBox
            mt={{ base: 10, md: 14 }}
            maxW="3xl"
            mx="auto"
            {...calmCard}
            h="auto"
            p={{ base: 8, md: 10 }}
            textAlign="center"
            {...fade(0.16)}
          >
            <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900" mb={4} lineHeight="1.45">
              {a.partner.jumpTitle}
            </Heading>
            <Text color="gray.700" fontSize="md" lineHeight="1.9">{a.partner.jumpBody}</Text>
            <Text mt={4} color="blue.600" fontSize="sm" fontWeight="semibold" lineHeight="1.8">
              {a.partner.jumpNote}
            </Text>
            <Button
              mt={7}
              as="a"
              href={PARTNER_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              rightIcon={<FiExternalLink />}
            >
              {a.partner.jumpCta}
            </Button>
          </MotionBox>
        </Container>
      </Box>

    </Box>
  );
};

// =====================================================================
// 帮助中心
// 三种状态全部收在组件内部（不新增全局 view，Header 因此不会卸载）：
//   home    搜索 + 热门词 + 6 大主题 + 常见问题 + 故障排查 + 联系支持
//   list    结果页：面包屑 + 主题筛选条 + 文章卡（双列）
//   article 正文页：面包屑 + 本页目录（吸附 + 滚动跟随）+ 目标 / 前置 /
//           步骤 / 完成标志 / 常见问题 / 相关文章 + 反馈 + 底部导航
// 文章步骤里的 shot 字段统一渲染成虚线占位框，真实截图到位后换成 Image 即可。
// =====================================================================
const HELP_CAT_TONES = ['blue', 'amber'];

/* ---------------- 上线指南 · 五步横向轮播 ----------------
   ⚠️ 2026-10-30 起帮助中心首页不再调用这个组件：五步改成「行式 + 展开操作要点」，
   一屏就能看全流程，不用滑五次。轮播实现先留着——如果以后要放回定价页
   （那里现在只留了一条指向帮助中心的链接）可以直接接回来。
   原来是竖排五张大卡，整段接近两屏，讲解时要来回滚，第一步和第五步隔得太远。
   改成「一屏一步」的横向轮播：固定尺寸、箭头 / 序号圆点 / 触摸滑动都能翻。
   用原生 scroll-snap 实现——触摸滑动和顺滑动画都是浏览器自带的，不引第三方轮播库。
   卡片等高：轨道 align-items: stretch + 卡片 h="full"，所以是「固定大小」。 */
const LAUNCH_GAP = 20; // 轨道里卡片之间的间距，sync/goTo 的步长计算要和它对上
const LaunchGuideCarousel = ({ steps, labels, onOpenArticle }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [edge, setEdge] = useState({ start: true, end: false });
  const total = steps.length;

  // 当前页 = 滚动位置 / 卡片步长；同时判断是否已经滚到两头（用来禁用箭头）
  const sync = () => {
    const el = trackRef.current;
    if (!el || !el.children.length) return;
    const step = el.children[0].offsetWidth + LAUNCH_GAP;
    const i = Math.round(el.scrollLeft / step);
    setActive(Math.max(0, Math.min(total - 1, i)));
    setEdge({
      start: el.scrollLeft <= 2,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    sync();
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
    // steps 变化时重新绑定即可，sync 每次渲染都是新函数，不放进依赖避免反复解绑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const kid = el.children[Math.max(0, Math.min(total - 1, i))];
    if (kid) el.scrollTo({ left: kid.offsetLeft, behavior: 'smooth' });
  };

  // 两侧的大箭头。桌面显示、移动端隐藏——移动端卡片占满宽度，悬浮箭头会压住内容，
  // 而且滑动本来就更自然。
  const sideArrow = (dir) => (
    <IconButton
      aria-label={dir === 'prev' ? labels.prev : labels.next}
      icon={<Icon as={dir === 'prev' ? FiChevronLeft : FiChevronRight} boxSize={5} />}
      // 注意：主题的 space 刻度里没有 11（只有 10 和 12），写 w={11} 会退化成 11px
      w="44px"
      h="44px"
      minW="44px"
      rounded="full"
      bg="white"
      color="gray.800"
      border="1px solid"
      borderColor="gray.300"
      boxShadow="0 6px 18px -8px rgba(11,61,92,0.5)"
      flexShrink={0}
      display={{ base: 'none', md: 'inline-flex' }}
      isDisabled={dir === 'prev' ? edge.start : edge.end}
      _hover={{ bg: 'blue.50', color: 'blue.600', borderColor: 'blue.300' }}
      _disabled={{ opacity: 0.35, boxShadow: 'none' }}
      onClick={() => goTo(dir === 'prev' ? active - 1 : active + 1)}
    />
  );

  return (
    <Box>
      {/* 两侧箭头与轨道同处一个 flex 行：箭头在轨道外侧、垂直居中，不压卡片内容，
          也不用负偏移——窄屏时容器本身没有外边距，负偏移会撑出横向滚动条。 */}
      <Flex align="center" gap={{ base: 0, md: 3 }}>
        {sideArrow('prev')}
        <Box
          ref={trackRef}
          display="flex"
          flex="1"
          minW={0}
          gap={`${LAUNCH_GAP}px`}
          py={3}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {steps.map((s, i) => {
            const tone = i % 2 ? 'amber' : 'blue';
            return (
              <Box key={s.title} flex="0 0 100%" minW={0} display="flex" sx={{ scrollSnapAlign: 'start' }}>
                <Box {...SOFT_CARD} p={{ base: 6, md: 8 }} w="full" display="flex" flexDirection="column">
                  {/* 轨道是 stretch，五张卡等高，翻页时高度不跳。每一步文案长短不一，
                      内容短的那张底部会空一块 —— 所以右栏做成纵向 flex，把跳转按钮压到底，
                      空白落在要点和按钮之间，翻页时按钮位置也不动。 */}
                  <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 6, md: 10 }} align="stretch" flex="1">
                    {/* 左栏：序号 + 图标 + 标题 + 这一步在做什么 */}
                    <Box
                      flex={{ base: 'none', md: '0 0 44%' }}
                      minW={0}
                      display="flex"
                      flexDirection="column"
                      justifyContent={{ base: 'flex-start', md: 'center' }}
                    >
                      <HStack spacing={3} align="center">
                        <Flex
                          w={9}
                          h={9}
                          rounded="full"
                          bg="blue.500"
                          color="white"
                          align="center"
                          justify="center"
                          fontSize="sm"
                          fontWeight="bold"
                          flexShrink={0}
                        >
                          {i + 1}
                        </Flex>
                        <Flex {...softIconBox(tone, 11)}>
                          <Icon as={GUIDE_ICONS[s.icon] || FiZap} boxSize={5} />
                        </Flex>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wider">
                          {labels.stepLabel} {i + 1}
                        </Text>
                      </HStack>
                      <Heading fontSize={{ base: 'lg', md: 'xl' }} color="gray.900" mt={5} lineHeight={1.4}>
                        {s.title}
                      </Heading>
                      <Text fontSize="sm" color="gray.700" lineHeight={1.85} mt={3}>
                        {s.desc}
                      </Text>
                    </Box>

                    {/* 右栏：操作要点 + 跳到对应帮助文章。
                        分隔线用 gray.300（#E5E7EB）——gray.200 其实是浅蓝背景色，白底上几乎看不见 */}
                    <Box
                      flex="1"
                      minW={0}
                      pl={{ md: 10 }}
                      borderLeft={{ md: '1px solid' }}
                      borderColor={{ md: 'gray.300' }}
                      display="flex"
                      flexDirection="column"
                    >
                      <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wider" mb={3}>
                        {labels.pointsLabel}
                      </Text>
                      <VStack align="stretch" spacing={2.5} flex="1">
                        {s.items.map((it) => (
                          <HStack key={it} align="flex-start" spacing={2.5}>
                            <Icon as={FiCheck} boxSize="13px" color={`${tone}.500`} mt="5px" flexShrink={0} />
                            <Text fontSize="sm" color="gray.700" lineHeight={1.75}>
                              {it}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                      {s.links && s.links.length ? (
                        <HStack spacing={2} mt={5} flexWrap="wrap">
                          {s.links.map((l) => (
                            <Button
                              key={l.id}
                              size="xs"
                              rounded="full"
                              variant="outline"
                              colorScheme="blue"
                              fontWeight="medium"
                              rightIcon={<Icon as={FiArrowRight} boxSize="12px" />}
                              onClick={() => onOpenArticle(l.id)}
                            >
                              {l.label}
                            </Button>
                          ))}
                        </HStack>
                      ) : null}
                    </Box>
                  </Flex>
                </Box>
              </Box>
            );
          })}
        </Box>

        {sideArrow('next')}
      </Flex>

      {/* 控制条：只保留五个序号，作为「当前看到第几步」的进度指示。
          主控是两侧的大箭头，不必再点下面这一排。 */}
      <HStack spacing={2} justify="center" mt={4} flexWrap="wrap">
        {steps.map((s, i) => (
          <Button
            key={s.title}
            size="sm"
            minW="36px"
            px={0}
            rounded="lg"
            variant={i === active ? 'solid' : 'outline'}
            colorScheme={i === active ? 'blue' : 'gray'}
            fontWeight={i === active ? 'bold' : 'medium'}
            aria-label={`${labels.goTo} ${i + 1}`}
            aria-current={i === active ? 'step' : undefined}
            onClick={() => goTo(i)}
          >
            {i + 1}
          </Button>
        ))}
      </HStack>

      {/* 移动端两侧不显示箭头，给一句滑动提示；桌面箭头已经很明显，不重复 */}
      <Text display={{ base: 'block', md: 'none' }} mt={3} fontSize="xs" color="gray.500" textAlign="center">
        {labels.hint}
      </Text>
    </Box>
  );
};

const HelpCenterView = ({ lang, onPricing, articleId = null, onArticle }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const h = c.help;
  const zh = lang === 'zh';

  // 四层状态：
  //   openId   → 正在看哪篇文章（有值就是文章页）
  //   listMode → 分类页（含故障排查）
  //   guide    → 开通指南（五步总览）
  //   都为空   → 入口页
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [listMode, setListMode] = useState(false);
  const [guide, setGuide] = useState(false);
  const [openId, setOpenId] = useState(articleId);
  const [voted, setVoted] = useState(null);
  const [reason, setReason] = useState(null); // 「不太有用」之后选的原因
  const [section, setSection] = useState('goal');
  const [openBranch, setOpenBranch] = useState(() => (h.articles.find((x) => x.id === articleId) || {}).cat || null); // 左栏展开到哪个分类

  // 文章有自己的网址（/help/<id>）：地址变了（直达、后退）就打开对应文章，换文章时把地址报给上层。
  useEffect(() => {
    if (articleId === openId) return;
    if (articleId) openArticle(articleId);
    else setOpenId(null);
  }, [articleId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (onArticle && openId !== articleId) onArticle(openId);
  }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 结果区长在首页里，回车时滚到它，而不是跳到另一个页面
  const resultsRef = useRef(null);

  const catOf = (key) => h.categories.find((x) => x.key === key) || {};
  const catTitle = (key) => catOf(key).title || '';
  const catIcon = (key) => HELP_ICONS[catOf(key).icon] || FiHelpCircle;
  const countOf = (key) => h.articles.filter((a) => a.cat === key).length;

  // 全局顺序：按分类顺序把文章摊平，就是「从开通到接听」的真实路径。
  // 上一篇 / 下一篇走这条线，而不是走分类内的序号 —— 这样能跨节往下读。
  const flow = h.categories.flatMap((cat) => h.articles.filter((a) => a.cat === cat.key));

  const q = query.trim().toLowerCase();
  // 命中范围：标题 / 摘要 / 目标 / 前置条件 / 步骤 / 完成标志 / 常见问题
  const haystack = (a) =>
    [
      a.title,
      a.summary,
      a.goal,
      ...(a.before || []),
      ...(a.steps || []).flatMap((s) => [s.t, s.d]),
      a.confirm,
      ...((a.faq || []).flatMap((f) => [f.q, f.a])),
    ]
      .join(' ')
      .toLowerCase();

  const matchQ = (a) => !q || haystack(a).includes(q);

  // 相关度打分。原来只做 includes 过滤，顺序按分类摊平，
  // 搜「登录」第一条会是「点『立即开通』之后会发生什么」（只是正文里提过登录），
  // 标题里明明有「登录」的那篇反而排在后面 —— 这就是「没有精确匹配」的来源。
  const scoreOf = (a) => {
    if (!q) return 0;
    const t = (a.title || '').toLowerCase();
    const sm = (a.summary || '').toLowerCase();
    let s = 0;
    if (t === q) s += 200;
    else if (t.startsWith(q)) s += 120;
    else if (t.includes(q)) s += 80;
    if (sm.includes(q)) s += 30;
    if (catTitle(a.cat).toLowerCase().includes(q)) s += 20;
    if ((a.goal || '').toLowerCase().includes(q)) s += 12;
    return s || 5; // 只在正文命中：垫底，但仍然算命中
  };

  const hitAll = h.articles.filter(matchQ).sort((x, y) => scoreOf(y) - scoreOf(x));
  // 「故障排查」的条目不是文章，但同样是一条能点的答案：搜「验证码」「没人接」也得搜得到
  const troubleHits = q
    ? h.trouble
        .filter((t) => `${t.title} ${t.desc}`.toLowerCase().includes(q))
        .map((t, i) => ({ id: `tb-${i}`, cat: 'troubleshoot', title: t.title, summary: t.desc, read: null, to: t.to }))
    : [];
  // 文章一条都没命中时，再看分类名：搜「故障」这种词，用户要的其实是整个分类
  const catHits =
    q && hitAll.length === 0
      ? h.categories
          .filter((cat) => `${cat.title} ${cat.desc}`.toLowerCase().includes(q))
          .map((cat) => ({ id: `cat-${cat.key}`, cat: cat.key, title: cat.title, summary: cat.desc, read: null, toCat: cat.key }))
      : [];
  const hits = [...catHits, ...hitAll, ...troubleHits];
  const article = openId ? h.articles.find((x) => x.id === openId) : null;

  const tocItems = article
    ? [
        { id: 'goal', label: h.article.goalLabel },
        ...(article.before && article.before.length ? [{ id: 'before', label: h.article.beforeLabel }] : []),
        { id: 'steps', label: h.article.stepsLabel },
        { id: 'confirm', label: h.article.confirmLabel },
        ...(article.faq && article.faq.length ? [{ id: 'faq', label: h.article.faqLabel }] : []),
        { id: 'related', label: h.article.relatedLabel },
      ]
    : [];

  const toTop = () => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  };

  // 打开文章：顺手把左栏对应的分类展开，否则当前位置在树里是「藏起来」的
  const openArticle = (id) => {
    const target = h.articles.find((x) => x.id === id);
    setOpenId(id);
    setOpenBranch(target ? target.cat : null);
    setGuide(false);
    setVoted(null);
    setReason(null);
    toTop();
  };

  const openGuide = () => {
    setGuide(true);
    setOpenId(null);
    setListMode(false);
    setActiveCat(null);
    setQuery('');
    toTop();
  };

  const enterCat = (key) => {
    setActiveCat(key);
    setQuery('');
    setOpenId(null); // 中栏优先级是「文章 > 列表」，不清就永远停在上一篇
    setGuide(false);
    setListMode(true);
    setOpenBranch(key);
    toTop();
  };

  const clearQuery = () => {
    setQuery('');
    setOpenId(null);
    setGuide(false);
    setListMode(false);
    setActiveCat(null);
    toTop();
  };

  const backHome = () => {
    setOpenId(null);
    setGuide(false);
    setListMode(false);
    setActiveCat(null);
    setQuery('');
    toTop();
  };

  const jumpTo = (id) => {
    const el = document.getElementById(`help-sec-${id}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88, behavior: 'smooth' });
  };

  // 正文页：右栏目录跟着滚动高亮（只是目录定位，不是阅读进度）
  useEffect(() => {
    if (!article) return undefined;
    setSection('goal');
    const els = tocItems.map((t) => document.getElementById(`help-sec-${t.id}`)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setSection(vis[0].target.id.replace('help-sec-', ''));
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // tocItems 由 article 派生，这里只需跟 article / 语言走
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, lang]);

  // 命中词高亮（只标第一处，不用正则，避免转义问题）
  const mark = (text) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q);
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <Box as="mark" bg="amber.200" color="gray.900" px={0.5} rounded="sm">
          {text.slice(idx, idx + q.length)}
        </Box>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const crumbs = (items) => (
    <HStack spacing={2} fontSize="sm" color="gray.600" flexWrap="wrap">
      {items.map((it, i) => (
        <React.Fragment key={`${it.label}-${i}`}>
          {i > 0 && <Icon as={FiChevronRight} boxSize="13px" color="gray.400" flexShrink={0} />}
          {it.onClick ? (
            <Box as="button" type="button" onClick={it.onClick} color="blue.600" fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
              {it.label}
            </Box>
          ) : (
            <Text color="gray.800" fontWeight="semibold" noOfLines={1} maxW={{ base: '190px', md: '460px' }}>
              {it.label}
            </Text>
          )}
        </React.Fragment>
      ))}
    </HStack>
  );

  // 搜索框：图标与文字同一套 px，任何尺寸下都不会错位
  // 搜索框：图标与文字同一套 px，任何尺寸下都不会错位。
  // 不再挂「联想下拉」——结果本来就长在首页下面，再弹一层浮层只是挡住它。
  const searchBox = ({ size = 'lg', center = true } = {}) => (
    <Box position="relative" maxW="2xl" mx={center ? 'auto' : 0} w="100%">
      <Box
        position="absolute"
        left="1rem"
        top="50%"
        transform="translateY(-50%)"
        color={query ? 'blue.500' : 'gray.400'}
        pointerEvents="none"
        zIndex={3}
        lineHeight={0}
      >
        <Icon as={FiSearch} boxSize={size === 'lg' ? '20px' : '18px'} />
      </Box>
      <Input
        size={size}
        h={size === 'lg' ? '56px' : '48px'}
        bg="white"
        borderRadius="xl"
        pl="3rem"
        pr={query ? '3rem' : 4}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && q) {
            e.preventDefault();
            // 结果就在下面，回车只是把人送过去，不换页
            if (resultsRef.current) {
              window.scrollTo({ top: resultsRef.current.getBoundingClientRect().top + window.scrollY - 88, behavior: 'smooth' });
            }
          }
          if (e.key === 'Escape') setQuery('');
        }}
        placeholder={h.hero.searchPlaceholder}
        borderColor="gray.300"
        boxShadow="0 12px 30px rgba(11,61,92,0.07)"
        _hover={{ borderColor: 'blue.300' }}
        _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #29ABE2' }}
        _placeholder={{ color: 'gray.400' }}
      />
      {query && (
        <IconButton
          aria-label={zh ? '清除' : 'Clear'}
          icon={<Icon as={FiX} />}
          size="sm"
          variant="ghost"
          rounded="full"
          position="absolute"
          right="0.75rem"
          top="50%"
          transform="translateY(-50%)"
          color="gray.500"
          zIndex={4}
          _hover={{ bg: 'gray.100' }}
          onClick={clearQuery}
        />
      )}
    </Box>
  );

  /* ================= 入口页 =================
     组织方式不是「功能模块平铺」，而是把官方《开通与账户管理 SOP》的五步
     直接当主页：卖家从注册到接听的顺序就是导航顺序。
     分类退到「按主题浏览」一行式列表 + 左栏，不再占首页的卡片墙。 */

  /* ---------------- 搜索结果：留在首页，不跳页 ----------------
     原来回车会跳到「结果页」，那页顶上又是一个搜索框 —— 等于让人在第二个框里重搜一次，
     而且结果只按分类顺序摊平、不看相关度，搜「登录」标题里带登录的那篇反而排后面。
     现在：结果直接长在首页搜索框下面，一个输入框到底；排序按相关度。 */
  const renderSearchResults = () => (
    <Box ref={resultsRef} sx={{ scrollMarginTop: '88px' }}>
      <Flex align="flex-end" justify="space-between" gap={4} flexWrap="wrap">
        <Box minW={0}>
          <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} color="gray.900" fontWeight="800" lineHeight={1.3}>
            {zh ? `“${query.trim()}”的结果` : `Results for “${query.trim()}”`}
          </Heading>
          <Text mt={1.5} fontSize="sm" color="gray.600">
            {hits.length
              ? zh
                ? `共 ${hits.length} 条，按相关度排序。`
                : `${hits.length} results, ranked by relevance.`
              : zh
              ? '没有命中。'
              : 'No matches.'}
          </Text>
        </Box>
        <Box as="button" type="button" onClick={clearQuery} color="blue.600" fontSize="sm" fontWeight="semibold" _hover={{ textDecoration: 'underline' }} flexShrink={0} pb={1}>
          {zh ? '清除搜索' : 'Clear search'}
        </Box>
      </Flex>

      {hits.length === 0 ? (
        <Box mt={8} pt={8} borderTop="1px solid" borderColor="gray.300">
          <Text fontWeight="bold" fontSize="md" color="gray.900">
            {h.emptyTitle}
          </Text>
          <Text mt={2} fontSize="sm" color="gray.600" lineHeight={1.8}>
            {h.emptyHint}
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={0} mt={6} borderTop="1px solid" borderColor="gray.300">
          {hits.map((a) => (
            <Box
              key={a.id}
              as="button"
              type="button"
              w="full"
              textAlign="left"
              py={{ base: 4, md: 5 }}
              borderBottom="1px solid"
              borderColor="gray.300"
              _hover={{ '& .row-title': { color: 'blue.600' } }}
              onClick={() => (a.toCat ? enterCat(a.toCat) : openArticle(a.to || a.id))}
            >
              <Flex align="flex-start" gap={3}>
                <Box flex="1" minW={0}>
                  <Text className="row-title" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} color="gray.900" lineHeight={1.5} transition="color 0.15s ease">
                    {mark(a.title)}
                  </Text>
                  <Text mt={1.5} fontSize="sm" color="gray.600" lineHeight={1.8} noOfLines={2}>
                    {a.summary}
                  </Text>
                  {/* 分类命中时，标题就是分类名，下面再写一遍分类是重复 */}
                  {!a.toCat && (
                    <HStack mt={2} spacing={3} fontSize="xs" color="gray.500">
                      <HStack spacing={1.5}>
                        <Icon as={catIcon(a.cat)} boxSize="11px" />
                        <Text>{catTitle(a.cat)}</Text>
                      </HStack>
                      {a.read ? (
                        <HStack spacing={1.5}>
                          <Icon as={FiClock} boxSize="11px" />
                          <Text>{a.read}</Text>
                        </HStack>
                      ) : null}
                    </HStack>
                  )}
                </Box>
                <Icon as={FiArrowRight} boxSize="16px" color="gray.400" mt={1} flexShrink={0} />
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );

  /* ================= 入口页 =================
     结构来自 10-30 的方案稿：搜索 → 第一次使用（两条路径）→ 按主题 → 热门问题
     → 没有找到答案 → 产品规则与安全。
     刻意不做的事：
       · 首页不再放五步轮播。轮播要连点五次才看得完流程，改成一个「开通指南」页，
         五步平铺、一屏看完；
       · 首页不再放费用构成、服务边界、故障排查双栏、深色联系大块。
         费用归到「账单与费用」分类，边界归到「产品规则与安全」，故障排查成为分类。 */

  // 第一次使用：两张大一点的入口卡（走流程 / 查账户）
  const renderQuickStart = () => (
    <Box>
      <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" color="gray.900">
        {h.quickStart.title}
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={5}>
        {h.quickStart.cards.map((card) => (
          <Box
            key={card.key}
            as="button"
            type="button"
            textAlign="left"
            p={5}
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            rounded="2xl"
            transition="all 0.18s ease"
            _hover={{ borderColor: 'blue.400', boxShadow: '0 12px 28px -16px rgba(11,61,92,0.45)' }}
            onClick={() => (card.key === 'guide' ? openGuide() : openArticle('register'))}
          >
            <HStack spacing={3} mb={2.5}>
              <Flex w="36px" h="36px" rounded="xl" bg="blue.50" color="blue.600" align="center" justify="center" flexShrink={0}>
                <Icon as={card.key === 'guide' ? FiPlay : FiUser} boxSize="16px" />
              </Flex>
              <Text fontWeight="bold" fontSize="md" color="gray.900">
                {card.title}
              </Text>
            </HStack>
            <Text fontSize="sm" color="gray.600" lineHeight={1.8}>
              {card.desc}
            </Text>
            <HStack mt={3.5} spacing={1.5} color="blue.600" fontSize="sm" fontWeight="semibold">
              <Text>{card.cta}</Text>
              <Icon as={FiArrowRight} boxSize="14px" />
            </HStack>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );

  // 按主题：两列六项，每项一行「图标 + 标题 + 一句说明 + 箭头」
  const renderTopics = () => (
    <Box>
      <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" color="gray.900">
        {h.allTopicsTitle}
      </Heading>
      <Text mt={2} fontSize="sm" color="gray.600" lineHeight={1.8}>
        {h.allTopicsSub}
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={5}>
        {h.categories.map((cat, i) => (
          <Flex
            key={cat.key}
            as="button"
            type="button"
            align="center"
            gap={3}
            p={4}
            textAlign="left"
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            rounded="xl"
            transition="all 0.18s ease"
            _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
            onClick={() => enterCat(cat.key)}
          >
            <Flex {...softIconBox(HELP_CAT_TONES[i % HELP_CAT_TONES.length], 9)} flexShrink={0}>
              <Icon as={HELP_ICONS[cat.icon] || FiHelpCircle} boxSize="15px" />
            </Flex>
            <Box flex="1" minW={0}>
              <Text className="topic-title" fontWeight="bold" fontSize="sm" color="gray.900">
                {cat.title}
              </Text>
              <Text mt={0.5} fontSize="xs" color="gray.600" lineHeight={1.7} noOfLines={2}>
                {cat.desc}
              </Text>
            </Box>
            <Icon as={FiChevronRight} boxSize="15px" color="gray.400" flexShrink={0} />
          </Flex>
        ))}
      </SimpleGrid>
    </Box>
  );

  // 热门问题：行式手风琴，点开看短答案
  const renderHotQuestions = () => (
    <Box>
      <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" color="gray.900">
        {h.faqTitle}
      </Heading>
      <Text mt={2} fontSize="sm" color="gray.600" lineHeight={1.8}>
        {h.faqSubtitle}
      </Text>
      <Accordion allowToggle mt={4} border="none" reduceMotion>
        <VStack align="stretch" spacing={0}>
          {h.faq.map((f) => (
            <AccordionItem key={f.q} border="none" borderTop="1px solid" borderColor="gray.300" _last={{ borderBottom: '1px solid', borderColor: 'gray.300' }}>
              <AccordionButton px={0} py={4} _hover={{ bg: 'transparent' }} _expanded={{ color: 'blue.700' }}>
                <Text flex="1" textAlign="left" fontWeight="semibold" fontSize="md" color="gray.900" lineHeight={1.7}>
                  {f.q}
                </Text>
                <AccordionIcon color="gray.400" flexShrink={0} />
              </AccordionButton>
              <AccordionPanel px={0} pt={0} pb={5}>
                <Text fontSize="sm" color="gray.700" lineHeight={1.9}>
                  {f.a}
                </Text>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </VStack>
      </Accordion>
    </Box>
  );

  // 没有找到答案：邮件 + 电话两条通道都给，和文章页右栏「还是没解决？」保持一致
  const renderNoAnswer = () => (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'flex-start', md: 'center' }}
      justify="space-between"
      gap={{ base: 4, md: 6 }}
      p={{ base: 5, md: 6 }}
      rounded="2xl"
      bg="blue.50"
      border="1px solid"
      borderColor="blue.100"
    >
      <Box>
        <Text fontWeight="bold" fontSize="md" color="gray.900">
          {h.noAnswer.title}
        </Text>
        <Text mt={1} fontSize="sm" color="gray.800" lineHeight={1.8}>
          {h.noAnswer.desc}
        </Text>
        <Text mt={1.5} fontSize="xs" color="gray.700" lineHeight={1.7}>
          {h.noAnswer.note}
        </Text>
      </Box>
      <HStack spacing={2.5} flexShrink={0} flexWrap="wrap" rowGap={2}>
        <Button
          as="a"
          href={`mailto:${h.noAnswer.email}`}
          leftIcon={<Icon as={FiMail} boxSize="15px" />}
          size="sm"
          h="38px"
          px={5}
          rounded="full"
          bg="white"
          color="blue.700"
          border="1px solid"
          borderColor="blue.200"
          fontWeight="semibold"
          _hover={{ bg: 'blue.100', borderColor: 'blue.300' }}
        >
          {h.noAnswer.email}
        </Button>
        <Button
          as="a"
          href={`tel:${h.contact.demoValue.replace(/[^+\d]/g, '')}`}
          leftIcon={<Icon as={FiPhone} boxSize="15px" />}
          size="sm"
          h="38px"
          px={5}
          rounded="full"
          bg="amber.400"
          color="gray.900"
          fontWeight="bold"
          _hover={{ bg: 'amber.500' }}
        >
          {h.contact.demoValue}
        </Button>
      </HStack>
    </Flex>
  );

  // 产品规则与安全：原来首页中间的「服务边界与数据」，挪到底部、改成可展开
  const renderRules = () => (
    <Box>
      <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" color="gray.900">
        {h.rules.title}
      </Heading>
      <Text mt={2} fontSize="sm" color="gray.600" lineHeight={1.8}>
        {h.rules.subtitle}
      </Text>
      <Accordion allowToggle mt={4} border="none" reduceMotion>
        <VStack align="stretch" spacing={0}>
          {h.rules.items.map((it) => (
            <AccordionItem key={it.title} border="none" borderTop="1px solid" borderColor="gray.300" _last={{ borderBottom: '1px solid', borderColor: 'gray.300' }}>
              <AccordionButton px={0} py={4} _hover={{ bg: 'transparent' }} _expanded={{ color: 'blue.700' }}>
                <HStack flex="1" textAlign="left" spacing={3} align="flex-start">
                  <Icon as={HELP_ICONS[it.icon] || FiShield} boxSize="16px" color="blue.500" mt="3px" flexShrink={0} />
                  <Box>
                    <Text fontWeight="semibold" fontSize="md" color="gray.900" lineHeight={1.6}>
                      {it.title}
                    </Text>
                    <Text mt={0.5} fontSize="sm" color="gray.600" lineHeight={1.7}>
                      {it.desc}
                    </Text>
                  </Box>
                </HStack>
                <AccordionIcon color="gray.400" flexShrink={0} mt={1} />
              </AccordionButton>
              <AccordionPanel px={0} pt={0} pb={5} pl={7}>
                <Text fontSize="sm" color="gray.700" lineHeight={1.9}>
                  {it.text}
                </Text>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </VStack>
      </Accordion>
    </Box>
  );

  const renderHome = () => (
    <>
      <Box className="gradient-mesh" position="relative" pt={{ base: 12, md: 16 }} pb={{ base: 10, md: 12 }}>
        {/* 光斑单独包一层并裁剪：section 自己不能 overflow:hidden，否则会把搜索联想面板裁掉 */}
        <Box aria-hidden="true" position="absolute" inset={0} overflow="hidden" pointerEvents="none">
          <Box position="absolute" w="360px" h="360px" rounded="full" bg="blue.200" opacity={0.35} filter="blur(80px)" top="-140px" right="-80px" className="animate-float" />
          <Box position="absolute" w="300px" h="300px" rounded="full" bg="amber.100" opacity={0.5} filter="blur(70px)" bottom="-120px" left="-90px" className="animate-float-slow" />
        </Box>

        <Container maxW="4xl" position="relative" zIndex={1}>
          <Box textAlign="center">
            <Text fontSize="sm" fontWeight="bold" letterSpacing="0.14em" textTransform="uppercase" color="blue.600">
              {h.hero.eyebrow}
            </Text>
            <Heading as="h1" mt={3} fontSize={{ base: '3xl', md: '4xl' }} lineHeight={1.2} color="gray.900" fontWeight="800">
              {h.hero.title}
            </Heading>
            <Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.8} maxW="2xl" mx="auto">
              {h.hero.subtitle}
            </Text>
          </Box>

          <Box mt={7}>{searchBox({ size: 'lg' })}</Box>

          {/* 热门词：先给方向，别让人对着空搜索框发呆 */}
          <Flex mt={4} justify="center" align="center" gap={2} flexWrap="wrap">
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {h.hero.hotLabel}
            </Text>
            {h.hero.hot.map((kw) => (
              <Box
                key={kw}
                as="button"
                type="button"
                px={3.5}
                py={1.5}
                rounded="full"
                bg="white"
                border="1px solid"
                borderColor="gray.300"
                fontSize="sm"
                color="gray.800"
                fontWeight="medium"
                transition="all 0.2s ease"
                _hover={{ borderColor: 'blue.400', color: 'blue.600', transform: 'translateY(-1px)' }}
                onClick={() => {
                  setQuery(kw);
                  setOpenId(null);
                  setListMode(false);
                }}
              >
                {kw}
              </Box>
            ))}
          </Flex>
        </Container>
      </Box>

      <Box bg="white" pb={{ base: 12, md: 16 }}>
        <Container maxW="4xl">
          {q ? (
            renderSearchResults()
          ) : (
            <>
              {renderQuickStart()}
              <Box mt={{ base: 12, md: 14 }}>{renderTopics()}</Box>
              <Box mt={{ base: 12, md: 14 }}>{renderHotQuestions()}</Box>
              <Box mt={{ base: 10, md: 12 }}>{renderNoAnswer()}</Box>
              <Box mt={{ base: 12, md: 14 }}>{renderRules()}</Box>
            </>
          )}
        </Container>
      </Box>
    </>
  );

  /* ---------------- 开通指南：五步总览 ----------------
     原来首页是「五步横向轮播」，一屏一步，要连点五次才看得完整个流程。
     现在单独一页，五步全部平铺，序号 / 标题 / 说明 / 操作要点 / 入口一次看全。 */
  const renderGuide = () => {
    const g = c.procurement;
    return (
      <Box pt="64px" bg="white" minH="100vh">
        <Container maxW="4xl" px={{ base: 5, md: 8 }} py={{ base: 8, md: 12 }}>
          {crumbs([{ label: h.article.home, onClick: backHome }, { label: zh ? '开通指南' : 'Activation guide' }])}
          <Heading as="h1" mt={4} fontSize={{ base: '2xl', md: '3xl' }} color="gray.900" fontWeight="800" lineHeight={1.25}>
            {zh ? '从开通到正式接听，五步' : 'From sign-up to answering calls, in five steps'}
          </Heading>
          <Text mt={3} fontSize="md" color="gray.700" lineHeight={1.8} maxW="2xl">
            {zh
              ? '按顺序做完这五步，电话就能接起来。每一步都对应一篇有完整步骤的指南。'
              : 'Work through these five steps in order and your phone starts answering. Each one maps to a full guide.'}
          </Text>

          <VStack align="stretch" spacing={0} mt={8} borderTop="1px solid" borderColor="gray.300">
            {g.steps.map((s, i) => (
              <Box key={s.title} py={{ base: 5, md: 6 }} borderBottom="1px solid" borderColor="gray.300">
                <Flex align="flex-start" gap={{ base: 3, md: 5 }}>
                  <Text
                    flexShrink={0}
                    w={{ base: '28px', md: '38px' }}
                    fontSize={{ base: 'sm', md: 'md' }}
                    fontWeight="800"
                    color="blue.500"
                    fontVariantNumeric="tabular-nums"
                    lineHeight={1.6}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </Text>
                  <Box flex="1" minW={0}>
                    <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} color="gray.900" lineHeight={1.5}>
                      {s.title}
                    </Text>
                    <Text mt={1.5} fontSize="sm" color="gray.700" lineHeight={1.8}>
                      {s.desc}
                    </Text>

                    {s.items && s.items.length ? (
                      <VStack align="stretch" spacing={2} mt={3.5}>
                        {s.items.map((it) => (
                          <HStack key={it} align="flex-start" spacing={2.5}>
                            <Icon as={FiCheck} boxSize="13px" color="blue.500" mt="5px" flexShrink={0} />
                            <Text fontSize="sm" color="gray.600" lineHeight={1.75}>
                              {it}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    ) : null}

                    {s.links && s.links.length ? (
                      <HStack spacing={2} mt={3.5} flexWrap="wrap">
                        {s.links.map((l) => (
                          <Button
                            key={l.id}
                            size="xs"
                            rounded="full"
                            variant="outline"
                            colorScheme="blue"
                            fontWeight="medium"
                            rightIcon={<Icon as={FiArrowRight} boxSize="12px" />}
                            onClick={() => openArticle(l.id)}
                          >
                            {l.label}
                          </Button>
                        ))}
                      </HStack>
                    ) : null}
                  </Box>
                </Flex>
              </Box>
            ))}
          </VStack>

          {g.stepNote && (
            <Text mt={5} fontSize="xs" color="gray.500" lineHeight={1.8}>
              {g.stepNote}
            </Text>
          )}
        </Container>
      </Box>
    );
  };

  // 分类下的条目。普通分类是文章；「故障排查」没有独立文章，
  // 条目就是「症状 → 对应指南」，点一下直接跳到能解决问题的文章。
  const rowsOf = (key) =>
    key === 'troubleshoot'
      ? h.trouble.map((t, i) => ({ key: `tb-${i}`, title: t.title, desc: t.desc, target: t.to, read: null }))
      : h.articles
          .filter((a) => a.cat === key)
          .map((a) => ({ key: a.id, title: a.title, desc: a.summary, target: a.id, read: a.read }));

  const renderRail = () => (
    <Box mt={{ base: 0, lg: '-6px' }}>
      <VStack align="stretch" spacing={0}>
        {h.categories.map((cat) => {
          const items = rowsOf(cat.key);
          const on = openBranch === cat.key || (!openBranch && activeCat === cat.key);
          const here = activeCat === cat.key && !article;
          return (
            <Box key={cat.key}>
              <Flex
                align="center"
                gap={1}
                rounded="md"
                bg={here ? 'blue.50' : 'transparent'}
                _hover={{ bg: 'blue.50' }}
                transition="background 0.15s ease"
              >
                <Box
                  as="button"
                  type="button"
                  flex="1"
                  minW={0}
                  textAlign="left"
                  px={2}
                  py={2}
                  fontWeight={here ? 'bold' : 'semibold'}
                  fontSize="sm"
                  color={here ? 'blue.700' : 'gray.800'}
                  onClick={() => setOpenBranch(on ? null : cat.key)}
                >
                  <Text noOfLines={1}>{cat.title}</Text>
                </Box>
                <Icon
                  as={FiChevronRight}
                  boxSize="13px"
                  color="gray.400"
                  mr={2}
                  flexShrink={0}
                  cursor="pointer"
                  transition="transform 0.18s ease"
                  transform={on ? 'rotate(90deg)' : 'none'}
                  onClick={() => setOpenBranch(on ? null : cat.key)}
                />
              </Flex>
              {on && (
                <VStack align="stretch" spacing={0} mb={2} mt={0.5}>
                  {items.map((a) => {
                    const cur = openId === a.target;
                    return (
                      <Box
                        key={a.key}
                        as="button"
                        type="button"
                        w="full"
                        textAlign="left"
                        pl={4}
                        pr={2}
                        py={1.5}
                        rounded="md"
                        position="relative"
                        bg={cur ? 'blue.50' : 'transparent'}
                        color={cur ? 'blue.700' : 'gray.600'}
                        fontWeight={cur ? 'semibold' : 'normal'}
                        fontSize="sm"
                        lineHeight={1.55}
                        _hover={{ color: 'blue.700', bg: 'blue.50' }}
                        onClick={() => openArticle(a.target)}
                        _before={
                          cur
                            ? {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '22%',
                                h: '56%',
                                w: '2px',
                                bg: 'blue.500',
                                rounded: 'full',
                              }
                            : undefined
                        }
                      >
                        <Text>{a.title}</Text>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          );
        })}
      </VStack>

      {/* 只留「帮助中心首页」。原来这里还有一条「全部主题」——
          6 个分类已经全在树里，再给一个「全部」是同一个东西的第二个入口，
          点进去还是一条和树完全同构的分组列表，纯属重复。 */}
      <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.300">
        <Flex as="button" type="button" align="center" gap={2} px={2} py={2} rounded="md" fontSize="sm" color="gray.600" textAlign="left" _hover={{ bg: 'blue.50', color: 'blue.700' }} onClick={backHome}>
          <Icon as={FiArrowLeft} boxSize="13px" flexShrink={0} />
          <Text>{zh ? '帮助中心首页' : 'Help home'}</Text>
        </Flex>
      </Box>
    </Box>
  );

  /* ---------------- 右栏：本页目录 + 求助 ----------------
     只有文章页出现。只放「这篇的目录」和求助入口，不做全站阅读进度。
     目录做成浅蓝卡片、当前项白底 + 蓝点：字号提一级、颜色压深，
     原来 xs + gray.500 在浅底上几乎要凑近看。
     求助区两条都留着：邮件（贴日志/截图）+ 电话（听产品演示）。 */
  const renderSide = () => (
    <Box>
      <Box bg="#EFF8FD" rounded="2xl" p={4}>
        <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={2.5} px={1}>
          {h.article.tocTitle}
        </Text>
        <VStack align="stretch" spacing={0.5}>
          {tocItems.map((t) => {
            const on = section === t.id;
            return (
              <Flex
                key={t.id}
                as="button"
                type="button"
                align="center"
                gap={2}
                textAlign="left"
                w="full"
                px={3}
                py={2}
                rounded="xl"
                bg={on ? 'white' : 'transparent'}
                boxShadow={on ? '0 1px 3px rgba(11,61,92,0.12)' : 'none'}
                color={on ? 'blue.700' : 'gray.850'}
                fontWeight={on ? 'bold' : 'medium'}
                fontSize="md"
                lineHeight={1.5}
                transition="all 0.15s ease"
                _hover={{ bg: on ? 'white' : 'rgba(255,255,255,0.65)', color: 'blue.600' }}
                onClick={() => jumpTo(t.id)}
              >
                <Box w="6px" h="6px" rounded="full" flexShrink={0} bg={on ? 'blue.500' : 'transparent'} />
                <Text>{t.label}</Text>
              </Flex>
            );
          })}
        </VStack>
      </Box>

      <Box mt={4} bg="white" border="1px solid" borderColor="gray.200" rounded="2xl" p={4}>
        <Text fontSize="md" fontWeight="bold" color="gray.900">
          {h.article.stillStuck}
        </Text>

        <Text mt={4} fontSize="sm" fontWeight="bold" color="gray.800">
          {h.contact.emailLabel}
        </Text>
        <Text mt={1} fontSize="xs" color="gray.600" lineHeight={1.7}>
          {h.contact.emailNote}
        </Text>
        <Button
          as="a"
          href={`mailto:${h.contact.email}`}
          mt={2.5}
          w="full"
          h="34px"
          size="sm"
          variant="outline"
          borderColor="blue.300"
          color="blue.600"
          rounded="full"
          fontSize="xs"
          fontWeight="semibold"
          _hover={{ bg: 'blue.50', borderColor: 'blue.400' }}
        >
          {h.contact.email}
        </Button>

        <Text mt={4} fontSize="sm" fontWeight="bold" color="gray.800">
          {h.contact.demoLabel}
        </Text>
        <Text mt={1} fontSize="xs" color="gray.600" lineHeight={1.7}>
          {h.contact.demoNote}
        </Text>
        <Button
          as="a"
          href={`tel:${h.contact.demoValue.replace(/[^+\d]/g, '')}`}
          mt={2.5}
          w="full"
          h="34px"
          size="sm"
          bg="amber.400"
          color="gray.900"
          rounded="full"
          fontSize="xs"
          fontWeight="bold"
          _hover={{ bg: 'amber.500' }}
        >
          {h.contact.demoValue}
        </Button>
      </Box>
    </Box>
  );

  /* ---------------- 上一篇 / 下一篇 ----------------
     按「分类顺序 + 分类内顺序」走全局序列，读完一节自然进入下一节。 */
  const renderPager = () => {
    if (!article) return null;
    const gi = flow.findIndex((a) => a.id === article.id);
    const prev = gi > 0 ? flow[gi - 1] : null;
    const next = gi >= 0 && gi < flow.length - 1 ? flow[gi + 1] : null;
    if (!prev && !next) return null;

    const cell = (a, dir) => {
      const cross = a.cat !== article.cat;
      const label = dir === 'prev' ? (cross ? (zh ? '上一节' : 'Previous section') : zh ? '上一篇' : 'Previous') : cross ? (zh ? '下一节' : 'Next section') : zh ? '下一篇' : 'Next';
      return (
        <Box
          as="button"
          type="button"
          flex="1"
          minW={0}
          textAlign={dir === 'prev' ? 'left' : 'right'}
          py={4}
          _hover={{ '& .pg-title': { color: 'blue.600' } }}
          onClick={() => openArticle(a.id)}
        >
          <HStack spacing={1.5} justify={dir === 'prev' ? 'flex-start' : 'flex-end'} color="gray.500">
            {dir === 'prev' && <Icon as={FiArrowLeft} boxSize="12px" />}
            <Text fontSize="xs" fontWeight="semibold">
              {label}
            </Text>
            {dir === 'next' && <Icon as={FiArrowRight} boxSize="12px" />}
          </HStack>
          <Text className="pg-title" mt={1} fontSize="sm" fontWeight="bold" color="gray.900" lineHeight={1.6} transition="color 0.15s ease">
            {a.title}
          </Text>
          {cross && (
            <Text mt={0.5} fontSize="xs" color="gray.500">
              {catTitle(a.cat)}
            </Text>
          )}
        </Box>
      );
    };

    return (
      <Flex
        mt={10}
        gap={{ base: 6, md: 8 }}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="gray.300"
        mb={8}
      >
        {prev ? cell(prev, 'prev') : <Box flex="1" />}
        {next ? cell(next, 'next') : <Box flex="1" />}
      </Flex>
    );
  };

  /* ---------------- 列表页：分类 / 全部主题 / 搜索结果 ----------------
     全部行式：标题 + 摘要 + 时长 + 箭头。分类页按分类分组，结果页不再分组。 */
  /* ---------------- 分类页 ----------------
     一条行式列表走到底，不再分「文章 / 症状」两套渲染：
     rowsOf 已经把两种条目统一成 { title, desc, read, target }。 */
  const renderList = () => {
    const headTitle = activeCat ? catTitle(activeCat) : h.articlesTitle;
    const rows = activeCat ? rowsOf(activeCat) : [];

    return (
      <Box>
        {crumbs([{ label: h.article.home, onClick: backHome }, { label: headTitle }])}
        <Heading as="h1" mt={4} fontSize={{ base: '2xl', md: '3xl' }} color="gray.900" fontWeight="800" lineHeight={1.25}>
          {headTitle}
        </Heading>
        <Text mt={2.5} fontSize="sm" color="gray.600" lineHeight={1.8} maxW="2xl">
          {activeCat ? catOf(activeCat).desc : h.allTopicsSub}
        </Text>

        {rows.length === 0 ? (
          <Box mt={10} py={10} borderTop="1px solid" borderColor="gray.300">
            <Text fontWeight="bold" fontSize="md" color="gray.900">
              {h.emptyTitle}
            </Text>
            <Text mt={2} fontSize="sm" color="gray.600" lineHeight={1.8}>
              {h.emptyHint}
            </Text>
            <Button mt={5} size="sm" variant="ghost" colorScheme="blue" onClick={backHome}>
              {zh ? '回到帮助中心首页' : 'Back to Help home'}
            </Button>
          </Box>
        ) : (
          <VStack align="stretch" spacing={0} mt={6} borderTop="1px solid" borderColor="gray.300">
            {rows.map((r) => (
              <Box
                key={r.key}
                as="button"
                type="button"
                w="full"
                textAlign="left"
                py={{ base: 4, md: 5 }}
                borderBottom="1px solid"
                borderColor="gray.300"
                _hover={{ '& .row-title': { color: 'blue.600' } }}
                onClick={() => openArticle(r.target)}
              >
                <Flex align="flex-start" gap={3}>
                  <Box flex="1" minW={0}>
                    <Text className="row-title" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} color="gray.900" lineHeight={1.5} transition="color 0.15s ease">
                      {r.title}
                    </Text>
                    <Text mt={1.5} fontSize="sm" color="gray.600" lineHeight={1.8} noOfLines={2}>
                      {r.desc}
                    </Text>
                    {r.read ? (
                      <HStack mt={2} spacing={1.5} fontSize="xs" color="gray.500">
                        <Icon as={FiClock} boxSize="11px" />
                        <Text>{r.read}</Text>
                      </HStack>
                    ) : null}
                  </Box>
                  <Icon as={FiArrowRight} boxSize="16px" color="gray.400" mt={1} flexShrink={0} />
                </Flex>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  /* ---------------- 正文页 ---------------- */
  const articleSection = (id, label, children) => (
    <Box id={`help-sec-${id}`} sx={{ scrollMarginTop: '88px' }} mb={{ base: 9, md: 11 }}>
      <Text fontSize="xs" fontWeight="bold" letterSpacing="0.1em" textTransform="uppercase" color="gray.500" mb={3}>
        {label}
      </Text>
      {children}
    </Box>
  );

  const renderArticle = () => {
    if (!article) {
      return (
        <Box py={16} textAlign="center">
          <Text fontWeight="bold" fontSize="lg" color="gray.900">
            {h.article.notFound}
          </Text>
          <Button mt={6} variant="outline" colorScheme="blue" leftIcon={<Icon as={FiArrowLeft} />} onClick={backHome}>
            {h.article.backToList}
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {crumbs([
          { label: h.article.home, onClick: backHome },
          { label: catTitle(article.cat), onClick: () => enterCat(article.cat) },
          { label: article.title },
        ])}

        <Heading as="h1" mt={4} fontSize={{ base: '2xl', md: '3xl' }} color="gray.900" fontWeight="800" lineHeight={1.25}>
          {article.title}
        </Heading>

        <HStack mt={3} spacing={{ base: 3, md: 4 }} flexWrap="wrap" color="gray.500" fontSize="xs">
          <HStack spacing={1.5}>
            <Icon as={FiClock} boxSize="12px" />
            <Text>{article.read}</Text>
          </HStack>
          <HStack spacing={1.5}>
            <Icon as={FiBook} boxSize="12px" />
            <Text>
              {h.article.updatedLabel} {h.article.updatedOn}
            </Text>
          </HStack>
        </HStack>

        <Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.85}>
          {article.summary}
        </Text>

        <Box mt={6} pt={6} borderTop="1px solid" borderColor="gray.300">
          {/* 小屏目录：横向可滑 */}
          <Box display={{ base: 'block', xl: 'none' }} mb={8} overflowX="auto" sx={{ '&::-webkit-scrollbar': { display: 'none' } }}>
            <HStack spacing={2} pb={1}>
              {tocItems.map((t) => {
                const on = section === t.id;
                return (
                  <Box
                    key={t.id}
                    as="button"
                    type="button"
                    flexShrink={0}
                    px={3}
                    py={1}
                    rounded="full"
                    fontSize="xs"
                    fontWeight={on ? 'bold' : 'medium'}
                    bg={on ? 'blue.500' : 'white'}
                    color={on ? 'white' : 'gray.700'}
                    border="1px solid"
                    borderColor={on ? 'blue.500' : 'gray.300'}
                    onClick={() => jumpTo(t.id)}
                  >
                    {t.label}
                  </Box>
                );
              })}
            </HStack>
          </Box>

          {articleSection(
            'goal',
            h.article.goalLabel,
            <Box bg="blue.50" border="1px solid" borderColor="blue.100" rounded="xl" p={{ base: 4, md: 5 }}>
              <Text fontSize="md" color="gray.900" fontWeight="medium" lineHeight={1.85}>
                {article.goal}
              </Text>
            </Box>
          )}

          {article.before && article.before.length > 0
            ? articleSection(
                'before',
                h.article.beforeLabel,
                <VStack align="stretch" spacing={2.5}>
                  {article.before.map((b) => (
                    <HStack key={b} align="flex-start" spacing={2.5}>
                      <Icon as={FiCheck} boxSize="14px" color="blue.500" mt="5px" flexShrink={0} />
                      <Text fontSize="md" color="gray.900" lineHeight={1.8}>
                        {b}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              )
            : null}

          {articleSection(
            'steps',
            h.article.stepsLabel,
            <VStack align="stretch" spacing={7}>
              {article.steps.map((s, i) => (
                <Box key={s.t}>
                  <HStack align="flex-start" spacing={3.5}>
                    <Text flexShrink={0} w="22px" fontSize="sm" fontWeight="800" color="blue.500" fontVariantNumeric="tabular-nums" lineHeight={1.6}>
                      {i + 1}
                    </Text>
                    <Box flex="1" minW={0}>
                      <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} color="gray.900" lineHeight={1.5}>
                        {s.t}
                      </Text>
                      <Text mt={1.5} fontSize="md" color="gray.800" lineHeight={1.9}>
                        {s.d}
                      </Text>
                    </Box>
                  </HStack>
                  {/* 截图位：有 shotImg 就渲染真实截图（可点击放大），否则保留虚线占位。
                      加图只需在文章 step 里补一个 shotImg: '/help/xxx.png'。 */}
                  {(s.shot || s.shotImg) && (
                    <Box mt={3} ml={{ base: 0, md: '36px' }}>
                      {s.shotImg ? (
                        <>
                          <ZoomShot src={s.shotImg} alt={s.shot || s.t} caption={s.shot || s.t} zh={zh} />
                          {s.shot && (
                            <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
                              {s.shot}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Box rounded="xl" border="1px dashed" borderColor="gray.300" bg="gray.50" px={5} py={6} textAlign="center">
                          <Icon as={FiImage} boxSize="18px" color="gray.400" mx="auto" mb={2} />
                          <Text fontSize="sm" fontWeight="medium" color="gray.600">
                            {s.shot}
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            {h.article.shotLabel}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          )}

          {articleSection(
            'confirm',
            h.article.confirmLabel,
            <Box bg="amber.50" border="1px solid" borderColor="amber.100" rounded="xl" p={{ base: 4, md: 5 }}>
              <Text fontSize="md" color="gray.900" lineHeight={1.85}>
                {article.confirm}
              </Text>
            </Box>
          )}

          {article.faq && article.faq.length > 0
            ? articleSection(
                'faq',
                h.article.faqLabel,
                <Accordion allowToggle border="none" reduceMotion>
                  <VStack align="stretch" spacing={0}>
                    {article.faq.map((f) => (
                      <AccordionItem key={f.q} border="none" borderTop="1px solid" borderColor="gray.300" _last={{ borderBottom: '1px solid', borderColor: 'gray.300' }}>
                        <AccordionButton px={0} py={3.5} _hover={{ bg: 'transparent' }}>
                          <Text flex="1" textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.900" lineHeight={1.7}>
                            {f.q}
                          </Text>
                          <AccordionIcon color="gray.400" flexShrink={0} />
                        </AccordionButton>
                        <AccordionPanel px={0} pt={0} pb={4}>
                          <Text fontSize="sm" color="gray.800" lineHeight={1.9}>
                            {f.a}
                          </Text>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </VStack>
                </Accordion>
              )
            : null}

          {articleSection(
            'related',
            h.article.relatedLabel,
            <VStack align="stretch" spacing={0}>
              {article.related.map((id) => {
                const r = h.articles.find((x) => x.id === id);
                if (!r) return null;
                return (
                  <Box
                    key={id}
                    as="button"
                    type="button"
                    w="full"
                    textAlign="left"
                    py={3.5}
                    borderTop="1px solid"
                    borderColor="gray.300"
                    _last={{ borderBottom: '1px solid', borderColor: 'gray.300' }}
                    _hover={{ '& .rel-title': { color: 'blue.600' } }}
                    onClick={() => openArticle(id)}
                  >
                    <Flex align="center" gap={3}>
                      <Box flex="1" minW={0}>
                        <Text className="rel-title" fontWeight="semibold" fontSize="sm" color="gray.900" lineHeight={1.6} transition="color 0.15s ease">
                          {r.title}
                        </Text>
                        <Text mt={0.5} fontSize="xs" color="gray.500">
                          {catTitle(r.cat)} · {r.read}
                        </Text>
                      </Box>
                      <Icon as={FiArrowRight} boxSize="14px" color="gray.400" flexShrink={0} />
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          )}

          {/* 反馈 */}
          <Box mt={{ base: 8, md: 10 }} pt={5} borderTop="1px solid" borderColor="gray.300">
            {voted === 'no' ? (
              <Box>
                <Text fontSize="sm" color="gray.700" lineHeight={1.8}>
                  {h.article.helpfulThanks}
                </Text>
                <Text mt={4} fontSize="sm" fontWeight="semibold" color="gray.900">
                  {h.article.helpfulReasonLabel}
                </Text>
                <HStack mt={2.5} spacing={2} flexWrap="wrap">
                  {h.article.helpfulReasons.map((r) => {
                    const on = reason === r;
                    return (
                      <Box
                        key={r}
                        as="button"
                        type="button"
                        px={3}
                        py={1.5}
                        rounded="full"
                        border="1px solid"
                        borderColor={on ? 'blue.400' : 'gray.300'}
                        bg={on ? 'blue.50' : 'white'}
                        color={on ? 'blue.700' : 'gray.700'}
                        fontWeight={on ? 'semibold' : 'normal'}
                        fontSize="sm"
                        _hover={{ borderColor: 'blue.400', color: 'blue.600' }}
                        onClick={() => setReason(r)}
                      >
                        {r}
                      </Box>
                    );
                  })}
                </HStack>
                {/* 没有后端收集，就别假装收集了：直接把原因写进邮件主题，用户点一下就能发 */}
                <Button
                  as="a"
                  href={`mailto:${h.contact.email}?subject=${encodeURIComponent(`${h.article.helpfulLabel} — ${article.title}`)}&body=${encodeURIComponent(`${h.article.helpfulReasonLabel}${reason || ''}\n\n`)}`}
                  mt={4}
                  size="sm"
                  rounded="full"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<Icon as={FiMail} boxSize="14px" />}
                >
                  {h.article.helpfulMailCta}
                </Button>
              </Box>
            ) : voted ? (
              <Text fontSize="sm" color="gray.700" lineHeight={1.8}>
                {h.article.helpfulThanks}
              </Text>
            ) : (
              <Flex direction={{ base: 'column', sm: 'row' }} align={{ base: 'flex-start', sm: 'center' }} justify="space-between" gap={3}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                  {h.article.helpfulLabel}
                </Text>
                <HStack spacing={2}>
                  <Button size="xs" variant="outline" colorScheme="blue" leftIcon={<Icon as={FiThumbsUp} />} onClick={() => setVoted('yes')}>
                    {h.article.helpfulYes}
                  </Button>
                  <Button size="xs" variant="ghost" colorScheme="gray" leftIcon={<Icon as={FiThumbsDown} />} onClick={() => setVoted('no')}>
                    {h.article.helpfulNo}
                  </Button>
                </HStack>
              </Flex>
            )}
          </Box>

          {renderPager()}
        </Box>
      </Box>
    );
  };

  /* ---------------- 总布局 ----------------
     入口页：通栏，不带侧栏
     开通指南：通栏，五步平铺（也不需要侧栏）
     分类页 / 文章页：左栏导航 + 中栏（+ 文章页才有右栏目录） */
  if (guide) return renderGuide();
  if (!article && !listMode) {
    return <Box pt="64px">{renderHome()}</Box>;
  }

  return (
    <Box pt="64px" bg="white" minH="100vh">
      <Container maxW="7xl" px={{ base: 5, md: 8 }} py={{ base: 8, md: 10 }}>
        <Grid
          templateColumns={article ? { base: '1fr', lg: '232px minmax(0,1fr)', xl: '232px minmax(0,1fr) 224px' } : { base: '1fr', lg: '232px minmax(0,1fr)' }}
          gap={{ base: 8, lg: 10 }}
          alignItems="start"
        >
          {/* 左栏：分类树。小屏隐藏，靠面包屑回首页 */}
          <Box
            display={{ base: 'none', lg: 'block' }}
            position="sticky"
            top="88px"
            maxH="calc(100vh - 110px)"
            overflowY="auto"
            pr={1}
            sx={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#E2E8F0', borderRadius: '4px' } }}
          >
            {renderRail()}
          </Box>

          {/* 中栏：文章 / 列表 */}
          <Box minW={0}>{article ? renderArticle() : renderList()}</Box>

          {/* 右栏：只在文章页出现，只放本页目录 */}
          {article ? (
            <Box display={{ base: 'none', xl: 'block' }} position="sticky" top="88px">
              {renderSide()}
            </Box>
          ) : null}
        </Grid>
      </Container>
    </Box>
  );
};

// =====================================================================
// 线索表单（只服务「联系我们」页）
// 字段参考 Twilio 的 Talk to Sales：姓名 / 邮箱 / 电话 / 公司 + 一个「主要诉求」。
// 因为我们是按分钟计费的产品，额外补了两个资格字段（销售平台、月话量）——
// 这两个直接决定报价，比问「职位」「公司规模」有用。
// 必填 5–6 项，其余全部选填：每多一个字段都会掉转化。
// =====================================================================
const ChipPicker = ({ options, value, onChange, multi = false }) => {
  const isOn = (o) => (multi ? Array.isArray(value) && value.includes(o) : value === o);
  const toggle = (o) => {
    if (!multi) return onChange(isOn(o) ? '' : o);
    const cur = Array.isArray(value) ? value : [];
    return onChange(isOn(o) ? cur.filter((x) => x !== o) : [...cur, o]);
  };
  return (
    <HStack spacing={2} flexWrap="wrap">
      {options.map((o) => {
        const on = isOn(o);
        return (
          <Button
            key={o}
            type="button"
            size="sm"
            rounded="full"
            variant={on ? 'solid' : 'outline'}
            colorScheme={on ? 'blue' : 'gray'}
            fontWeight={on ? 'bold' : 'medium'}
            onClick={() => toggle(o)}
            aria-pressed={on}
          >
            {on && <Icon as={FiCheck} mr={1.5} boxSize="13px" />}
            {o}
          </Button>
        );
      })}
    </HStack>
  );
};

// 统一的字段外壳：标签 + 选填标记 + 帮助文字 + 错误文字
const Field = ({ label, hint, error, optionalText, children, required, ...rest }) => (
  <FormControl isInvalid={!!error} isRequired={required} {...rest}>
    <FormLabel fontWeight="semibold" color="gray.800" fontSize="sm" mb={2}>
      {label}
      {optionalText && (
        <Text as="span" ml={2} fontWeight="normal" color="gray.400" fontSize="xs">
          {optionalText}
        </Text>
      )}
    </FormLabel>
    {children}
    {hint && !error && (
      <FormHelperText fontSize="xs" color="gray.500" mt={1.5} lineHeight={1.6}>
        {hint}
      </FormHelperText>
    )}
    <FormErrorMessage fontSize="xs" mt={1.5}>
      <Icon as={FiAlertCircle} mr={1} boxSize="13px" />
      {error}
    </FormErrorMessage>
  </FormControl>
);

const INPUT_PROPS = {
  size: 'lg',
  borderRadius: 'xl',
  borderColor: 'gray.300',
  bg: 'white',
  _hover: { borderColor: 'blue.300' },
  _focus: { borderColor: 'blue.500', boxShadow: '0 0 0 1px #29ABE2' },
  _placeholder: { color: 'gray.400' },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 只服务「联系我们」页（原「预约演示」页已下架，kind 参数一并去掉）
const LeadForm = ({ lang }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const f = c.lead.form;
  const s = c.lead.success;
  const page = c.contactPage;
  const a = c.lead.aside;

  const [v, setV] = useState({
    name: '', email: '', phone: '', company: '',
    pick: '', platforms: [], volume: '', message: '', consent: false,
  });
  const [errs, setErrs] = useState({});
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);

  const set = (k, val) => {
    setV((p) => ({ ...p, [k]: val }));
    if (errs[k]) setErrs((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!v.name.trim()) e.name = f.errName;
    if (!EMAIL_RE.test(v.email.trim())) e.email = f.errEmail;
    if (!v.phone.trim()) e.phone = f.errPhone;
    if (!v.company.trim()) e.company = f.errCompany;
    if (!v.pick) e.pick = f.errPick;
    if (!v.consent) e.consent = f.errConsent;
    return e;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrs(e);
    const firstBad = Object.keys(e)[0];
    if (firstBad) {
      const el = formRef.current && formRef.current.querySelector(`[data-field="${firstBad}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy(true);
    const website = formRef.current && formRef.current.querySelector('input[name="website"]');
    submitInquiry({ ...v, website: website ? website.value : '' }, { topics: page.topicOptions, lang })
      .then(() => {
        setSent(true);
        if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      })
      .catch((err) => setErrs({ form: err.message === 'too_many_requests' ? f.errTooMany : f.errSubmit }))
      .finally(() => setBusy(false));
  };

  const reset = () => {
    setV({ name: '', email: '', phone: '', company: '', pick: '', platforms: [], volume: '', message: '', consent: false });
    setErrs({});
    setSent(false);
  };

  if (sent) {
    return (
      <Box bg="white" rounded="2xl" border="1px solid" borderColor="green.200" p={{ base: 6, md: 10 }} textAlign="center" boxShadow="sm">
        <Flex w="56px" h="56px" rounded="full" bg="green.50" color="green.600" align="center" justify="center" mx="auto">
          <Icon as={FiCheckCircle} boxSize="26px" />
        </Flex>
        <Heading as="h2" mt={5} fontSize={{ base: 'xl', md: '2xl' }} color="gray.900">
          {s.title}
        </Heading>
        <Text mt={3} color="gray.600" lineHeight={1.9} maxW="lg" mx="auto">
          {s.contactText}
        </Text>
        <HStack mt={7} spacing={3} justify="center" flexWrap="wrap">
          <Button variant="primary" as="a" href={`mailto:${a.email}`} leftIcon={<FiMail />}>
            {a.email}
          </Button>
          <Button
            variant="outline"
            colorScheme="blue"
            as="a"
            href={`tel:${a.phone.replace(/[^+\d]/g, '')}`}
            leftIcon={<FiPhone />}
          >
            {a.phone}
          </Button>
        </HStack>
        <Button mt={6} variant="ghost" size="sm" colorScheme="gray" onClick={reset}>
          {s.again}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      ref={formRef}
      as="form"
      noValidate
      onSubmit={onSubmit}
      bg="white"
      rounded="2xl"
      border="1px solid"
      borderColor="gray.200"
      p={{ base: 6, md: 8 }}
      boxShadow="sm"
    >
      <Stack spacing={6}>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
          <Field data-field="name" label={f.name} required error={errs.name}>
            <Input {...INPUT_PROPS} value={v.name} onChange={(e) => set('name', e.target.value)} placeholder={f.namePh} autoComplete="name" />
          </Field>
          <Field data-field="email" label={f.email} hint={f.emailHint} required error={errs.email}>
            <Input {...INPUT_PROPS} type="email" value={v.email} onChange={(e) => set('email', e.target.value)} placeholder={f.emailPh} autoComplete="email" />
          </Field>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
          <Field data-field="phone" label={f.phone} hint={f.phoneHint} required error={errs.phone}>
            <Input {...INPUT_PROPS} type="tel" value={v.phone} onChange={(e) => set('phone', e.target.value)} placeholder={f.phonePh} autoComplete="tel" />
          </Field>
          <Field data-field="company" label={f.company} required error={errs.company}>
            <Input {...INPUT_PROPS} value={v.company} onChange={(e) => set('company', e.target.value)} placeholder={f.companyPh} autoComplete="organization" />
          </Field>
        </Grid>

        <Field
          data-field="pick"
          label={page.topicLabel}
          required
          error={errs.pick}
        >
          <ChipPicker
            options={page.topicOptions}
            value={v.pick}
            onChange={(x) => set('pick', x)}
          />
        </Field>

        <Field label={f.platforms} hint={f.platformsHint} optionalText={f.optional}>
          <ChipPicker multi options={f.platformOptions} value={v.platforms} onChange={(x) => set('platforms', x)} />
        </Field>

        <Field label={f.volume} optionalText={f.optional}>
          <ChipPicker options={f.volumeOptions} value={v.volume} onChange={(x) => set('volume', x)} />
        </Field>

        <Field label={f.message} optionalText={f.optional}>
          <Textarea
            rows={4}
            borderRadius="xl"
            borderColor="gray.300"
            bg="white"
            value={v.message}
            onChange={(e) => set('message', e.target.value)}
            placeholder={f.messagePh}
            _hover={{ borderColor: 'blue.300' }}
            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #29ABE2' }}
            _placeholder={{ color: 'gray.400' }}
          />
        </Field>

        <FormControl isInvalid={!!errs.consent}>
          <Checkbox
            isChecked={v.consent}
            onChange={(e) => set('consent', e.target.checked)}
            alignItems="flex-start"
            size="md"
          >
            <Text fontSize="sm" color="gray.600" lineHeight={1.7} textAlign="left">
              {f.consentPre}
              <Link
                as={RouterLink}
                to="/privacy-policy"
                color="blue.600"
                textDecoration="underline"
                onClick={(e) => e.stopPropagation()}
              >
                {f.consentLink}
              </Link>
              {f.consentPost}
            </Text>
          </Checkbox>
          <FormErrorMessage fontSize="xs" mt={1.5}>
            <Icon as={FiAlertCircle} mr={1} boxSize="13px" />
            {errs.consent}
          </FormErrorMessage>
        </FormControl>

        {/* 机器人陷阱：真人看不到这个输入框，脚本会填 */}
        <Box as="input" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
          position="absolute" left="-9999px" w="1px" h="1px" opacity={0} />

        <Box pt={1}>
          {errs.form && (
            <Text role="alert" color="red.600" fontSize="sm" mb={3}>{errs.form}</Text>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            px={8}
            w={{ base: '100%', sm: 'auto' }}
            isLoading={busy}
            loadingText={f.sending}
            rightIcon={<FiSend />}
          >
            {f.submit}
          </Button>
          {/* 表单下方的补充说明（回复时效） */}
          <HStack mt={3} spacing={2} align="start" color="gray.500">
            <Icon as={FiInfo} boxSize="13px" mt="3px" flexShrink={0} />
            <Text fontSize="xs" lineHeight={1.6}>
              {page.formNote}
            </Text>
          </HStack>
          <Text mt={2} fontSize="xs" color="gray.400">
            {f.requiredNote}
          </Text>
        </Box>
      </Stack>
    </Box>
  );
};

// 两栏骨架：左边表单，右边「你会得到什么 / 其他联系方式 / 提交之前」
const LeadPage = ({ lang }) => {
  const c = lang === 'zh' ? landingContent.zh : landingContent.en;
  const a = c.lead.aside;
  const page = c.contactPage;

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.1 },
    transition: { duration: 0.4, delay },
  });

  return (
    <Box pt="64px" pb={20}>
      {/* ============ Hero ============ */}
      <Box className="gradient-mesh" pt={{ base: 10, md: 14 }} pb={{ base: 8, md: 10 }}>
        <Container maxW="6xl">
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" fontSize="sm">
              {page.eyebrow}
            </Badge>
            <Heading as="h1" mt={4} fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }} lineHeight={1.2} color="gray.900" fontWeight="800">
              {page.title}
            </Heading>
            <Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight={1.8}>
              {page.subtitle}
            </Text>
          </Box>
        </Container>
      </Box>

      {/* ============ 表单 + 侧栏 ============ */}
      <Box className="gradient-mesh" pb={{ base: 14, md: 20 }}>
        <Container maxW="6xl">
          <Grid
            templateColumns={{ base: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }}
            gap={{ base: 8, lg: 10 }}
            alignItems="start"
          >
            <MotionBox {...fade()}>
              <LeadForm lang={lang} />
            </MotionBox>

            <Stack spacing={5} as={MotionBox} {...fade(0.08)}>
              {/* ---- 卡 1 ---- */}
              {/* 联系页：也可以直接找我们（邮件 / 电话 / 地址） */}
              <Box bg="#0B3D5C" color="white" rounded="2xl" p={6}>
                <Heading as="h2" fontSize="lg">
                  {page.channelsTitle}
                </Heading>
                <Stack mt={5} spacing={5}>
                  {page.channels.map((ch) => (
                    <HStack key={ch.label} align="start" spacing={3}>
                      <Flex w="34px" h="34px" rounded="lg" bg="whiteAlpha.200" align="center" justify="center" flexShrink={0}>
                        <Icon as={CHANNEL_ICONS[ch.icon] || FiInfo} boxSize="16px" />
                      </Flex>
                      <Box minW={0}>
                        <Text fontSize="xs" color="blue.200">
                          {ch.label}
                        </Text>
                        {ch.href ? (
                          <Link
                            href={ch.href}
                            display="block"
                            fontWeight="bold"
                            fontSize="md"
                            color="white"
                            lineHeight={1.5}
                            _hover={{ color: 'amber.300' }}
                            wordBreak="break-all"
                          >
                            {ch.value}
                          </Link>
                        ) : (
                          <Text fontWeight="bold" fontSize="md" lineHeight={1.5}>
                            {ch.value}
                          </Text>
                        )}
                        {ch.note && (
                          <Text mt={1} fontSize="xs" color="blue.200" lineHeight={1.6}>
                            {ch.note}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                  ))}
                </Stack>
              </Box>

              {/* ---- 卡 2 ---- */}
              {/* 联系页：哪件事走哪个渠道 */}
              <Box bg="white" rounded="2xl" border="1px solid" borderColor="blue.100" p={6} boxShadow="sm">
                <Heading as="h2" fontSize="lg" color="gray.900">
                  {page.routingTitle}
                </Heading>
                <Stack mt={4} spacing={4}>
                  {page.routing.map((r) => (
                    <Box key={r.label} borderLeft="3px solid" borderColor="blue.200" pl={3}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.900" lineHeight={1.6}>
                        {r.label}
                      </Text>
                      <HStack mt={1.5} spacing={2} flexWrap="wrap">
                        <Badge colorScheme="blue" variant="subtle" rounded="full" px={2} py={0.5} fontSize="xs" textTransform="none">
                          {r.via}
                        </Badge>
                        <Text fontSize="xs" color="gray.500" lineHeight={1.6}>
                          {r.note}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* ---- 卡 3：提交之前（回复时效与信息用途） ---- */}
              <Box bg="amber.50" border="1px solid" borderColor="amber.200" rounded="2xl" p={6}>
                <Heading as="h2" fontSize="md" color="gray.900">
                  {a.trustTitle}
                </Heading>
                <Stack mt={3} spacing={2.5}>
                  {a.trust.map((t) => (
                    <HStack key={t} align="start" spacing={2.5}>
                      <Icon as={FiShield} boxSize="14px" color="amber.600" mt="4px" flexShrink={0} />
                      <Text fontSize="sm" color="gray.700" lineHeight={1.7}>
                        {t}
                      </Text>
                    </HStack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

// ============ 首页 hero 右侧轮播（四张卡，每张 5 秒，左右滑动） ============
// 原来这里是一张「听 AI 怎么处理一通来电」的播放卡，点击 openDemo()。
// 现在改成四张卡自动轮播：开通流程 / 部署配置 / 价格 / 服务保障。
// 价格那张直接复用 pricing 块，避免同一套价格在两处各维护一份。
//
// 为什么不用 AnimatePresence 做切换：它是「一张进、一张出」，
// 切换的那半秒里两张卡会同时在 DOM 里，容器高度瞬间翻倍（实测 452px → 808px），
// 页面会被撑得跳一下。改成横向轨道（四张并排 + translateX 整体位移）之后，
// 容器高度由最高的那张决定、全程恒定，视觉上就是纯粹的左右滑动。
const HERO_SLIDE_MS = 5000;
const HERO_GUARANTEE_ICONS = { shield: FiShield, clock: FiClock, headset: FiHeadphones };
// 拖动识别：位移超过这个像素数才算「拖动」，否则仍按「点击」处理
const HERO_DRAG_SLOP = 8;
// 翻页判定：拖动超过卡片宽度的这个比例（且不低于 48px）才翻页，否则弹回原位
const HERO_SWIPE_RATIO = 0.18;
const HERO_SWIPE_MIN_PX = 48;

const HeroCarousel = ({ c, p, onContactSales, onViewPricing }) => {
  const slides = c.slides;
  const total = slides.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // 拖动位移（px），叠加在 translateX 的百分比位移之上；松手后归零
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  // 拖动过程中的临时状态放 ref：pointermove 触发很密，不该每次都引起重渲染
  const drag = useRef({ id: null, startX: 0, startY: 0, axis: null, moved: false, width: 0 });

  // 每张停 5 秒。鼠标悬停、键盘聚焦或正在拖动时暂停，离开 / 松手后从当前这张重新计时。
  // 用 setTimeout 而不是 setInterval，点圆点或拖动切卡时计时才会干净地重置。
  useEffect(() => {
    if (paused || dragging) return undefined;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % total), HERO_SLIDE_MS);
    return () => clearTimeout(timer);
  }, [index, paused, dragging, total]);

  const goTo = (i) => setIndex(((i % total) + total) % total);

  // ---------- 拖动 / 滑动切卡 ----------
  // 鼠标和手指共用同一条 pointer 事件通路，不必分开写 touch / mouse 两套。
  // 要点：方向判定出来之前不 setPointerCapture，否则手机上会把纵向滚动一起吞掉。
  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return; // 只认左键
    drag.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      axis: null,
      moved: false,
      width: e.currentTarget.clientWidth,
    };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (d.id !== e.pointerId || d.axis === 'y') return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.axis) {
      if (Math.abs(dx) < HERO_DRAG_SLOP && Math.abs(dy) < HERO_DRAG_SLOP) return;
      // 以纵向为主的手势交给页面滚动，本组件彻底放手
      if (Math.abs(dy) >= Math.abs(dx)) { d.axis = 'y'; return; }
      d.axis = 'x';
      // 确认是横向之后才捕获指针，拖动才不会因为手指滑出卡片边界而中断
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* 老浏览器忽略 */ }
      setDragging(true);
    }

    d.moved = true;
    // 已经在第一张还要往右、或在最后一张还要往左：加阻尼，
    // 手感上告诉用户「到头了」，而不是硬邦邦地不动
    const atStart = index === 0 && dx > 0;
    const atEnd = index === total - 1 && dx < 0;
    setDragX(atStart || atEnd ? dx * 0.32 : dx);
  };

  const onPointerUp = (e) => {
    const d = drag.current;
    if (d.id !== e.pointerId) return;
    d.id = null;
    if (d.axis !== 'x') return;

    // 用原始位移判定，不用带阻尼的 dragX
    const dx = e.clientX - d.startX;
    const threshold = Math.max(HERO_SWIPE_MIN_PX, d.width * HERO_SWIPE_RATIO);
    // 到两端只弹回、不循环：与拖动时的阻尼保持一致，
    // 否则会出现「手指往右划、整条卡片却往左飞」的错位感
    if (dx <= -threshold && index < total - 1) goTo(index + 1);
    else if (dx >= threshold && index > 0) goTo(index - 1);

    setDragX(0);
    setDragging(false);
  };

  // 手势被系统中断（例如手机上被判定成纵向滚动）—— 只复位，不切卡
  const onPointerCancel = (e) => {
    const d = drag.current;
    if (d.id !== e.pointerId) return;
    d.id = null;
    setDragX(0);
    setDragging(false);
  };

  // 拖动之后紧跟着的那一次 click 必须吞掉：否则松手时正好落在
  // 「立即开通」「联系我们」这类按钮上，会误触发跳转。
  // moved 会在下一次 pointerdown 时复位，所以只影响紧随其后的那一次点击。
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  const renderSlide = (slide, i) => (
    <Box
      key={slide.key}
      w={`${100 / total}%`}
      flexShrink={0}
      px={{ base: 6, md: 8 }}
      py={{ base: 6, md: 7 }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      aria-hidden={i !== index}
      // 非当前那张整块 inert：否则键盘 Tab 会跑进看不见的卡片里，把轮播顶走位
      inert={i !== index}
    >
      <HStack justify="space-between" align="center" mb={4}>
        <Badge colorScheme="blue" variant="solid" rounded="full" px={3} py={1} boxShadow="sm">
          {slide.tag}
        </Badge>
        <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="wider">
          {String(i + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </Text>
      </HStack>

      <Heading as="h2" fontSize={{ base: 'lg', md: 'xl' }} lineHeight={1.35} color="gray.900">
        {slide.title}
      </Heading>
      <Text mt={2.5} fontSize="sm" color="gray.600" lineHeight={1.75}>
        {slide.subtitle}
      </Text>

      {/* 开通流程 / 部署配置：四步竖排 */}
      {slide.steps && (
        <VStack align="stretch" spacing={3} mt={5}>
          {slide.steps.map((step, n) => (
            <HStack key={step.title} spacing={3} align="flex-start">
              <Flex
                w="24px"
                h="24px"
                rounded="full"
                bg="blue.500"
                color="white"
                align="center"
                justify="center"
                flexShrink={0}
                fontSize="xs"
                fontWeight="bold"
                mt="1px"
              >
                {n + 1}
              </Flex>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.900" lineHeight={1.5}>{step.title}</Text>
                <Text fontSize="xs" color="gray.600" mt={0.5} lineHeight={1.7}>{step.text}</Text>
              </Box>
            </HStack>
          ))}
        </VStack>
      )}

      {/* 价格：直接复用定价页首屏那张套餐卡 */}
      {slide.key === 'price' && (
        <Box mt={4}>
          <Text fontSize="sm" color="gray.500" fontWeight="medium">{p.planLabel}</Text>
          <HStack align="baseline" spacing={2} mt={2}>
            <Text fontSize="4xl" fontWeight="extrabold" color="brand.navy" lineHeight={1}>{p.price}</Text>
            <Text fontSize="sm" color="gray.500" fontWeight="medium">{p.perMonth}</Text>
            <Text fontSize="md" color="gray.400" textDecoration="line-through">{p.wasPrice}</Text>
          </HStack>
          <Text fontSize="xs" color="blue.600" mt={2}>{p.priceNote}</Text>
          <VStack align="stretch" spacing={2} mt={4}>
            {p.features.map((feat) => (
              <HStack key={feat} spacing={2} align="flex-start">
                <Icon as={FiCheckCircle} color="blue.500" boxSize="18px" mt="2px" flexShrink={0} />
                <Text fontSize="sm" color="gray.700" fontWeight="medium">{feat}</Text>
              </HStack>
            ))}
          </VStack>
          <HStack spacing={3} mt={5}>
            <Button variant="outline" size="sm" flex={1} onClick={onContactSales}>{p.contactSales}</Button>
            <Button
              as="a"
              href={PARTNER_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="amber"
              size="sm"
              flex={1}
            >
              {p.getStarted}
            </Button>
          </HStack>
        </Box>
      )}

      {/* 服务保障：三条保障 + 一个跳转 */}
      {slide.items && (
        <Box mt={5}>
          <VStack align="stretch" spacing={3.5}>
            {slide.items.map((item) => {
              const ItemIcon = HERO_GUARANTEE_ICONS[item.icon] || FiInfo;
              return (
                <HStack key={item.title} spacing={3} align="flex-start">
                  <Flex
                    w="36px"
                    h="36px"
                    rounded="lg"
                    bg="white"
                    color="blue.600"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    boxShadow="sm"
                  >
                    <Icon as={ItemIcon} boxSize="18px" />
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="gray.900" lineHeight={1.5}>{item.title}</Text>
                    <Text fontSize="xs" color="gray.600" mt={0.5} lineHeight={1.7}>{item.text}</Text>
                  </Box>
                </HStack>
              );
            })}
          </VStack>
          {slide.cta && (
            <Button
              variant="ghost"
              size="sm"
              mt={3}
              px={2}
              color="blue.600"
              rightIcon={<FiArrowRight />}
              onClick={onViewPricing}
            >
              {slide.cta}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      role="region"
      aria-roledescription="carousel"
      aria-label={c.carousel.regionLabel}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* 横向轨道：四张并排，整体左右位移。容器高度 = 最高的那张，切换过程不变 */}
      <Box
        rounded="2xl"
        border="1px solid"
        borderColor="blue.100"
        boxShadow="2xl"
        overflow="hidden"
        bgGradient="linear(to-br, blue.50, white)"
        // ---- 拖动 / 滑动切卡 ----
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={onClickCapture}
        // 拖到卡片里的 <a> 上时，别让浏览器启动原生链接拖拽
        onDragStart={(e) => e.preventDefault()}
        sx={{
          // pan-y：纵向交给浏览器滚动，横向留给这里；不写的话手机上会整页跟着滑
          touchAction: 'pan-y',
          // 鼠标拖动时防止选中文字
          userSelect: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
          // 卡片里的按钮和链接要保持手型光标，不能被上面的 grab 盖掉
          '& a, & button': { cursor: 'pointer' },
        }}
      >
        <Flex
          w={`${total * 100}%`}
          align="stretch"
          transform={`translateX(calc(-${(index * 100) / total}% + ${dragX}px))`}
          // 拖动中把过渡时长设成 0，而不是 transition:none ——
          // 保持 transition-property 一致，松手那一帧才能可靠地补上回弹动画
          transition={dragging ? 'transform 0s linear' : 'transform 0.62s cubic-bezier(0.45, 0, 0.15, 1)'}
        >
          {slides.map(renderSlide)}
        </Flex>
      </Box>

      {/* 圆点：点一下立刻切到那张卡，并重新开始 5 秒计时 */}
      <HStack justify="center" spacing={2} mt={5}>
        {slides.map((item, i) => (
          <Box
            key={item.key}
            as="button"
            type="button"
            p={0}
            border="none"
            cursor="pointer"
            onClick={() => setIndex(i)}
            aria-label={`${c.carousel.goTo}: ${item.tag}`}
            aria-current={i === index ? 'true' : undefined}
            w={i === index ? '26px' : '8px'}
            h="8px"
            rounded="full"
            bg={i === index ? 'blue.500' : 'blue.200'}
            transition="all 0.3s ease"
            _hover={{ bg: i === index ? 'blue.500' : 'blue.300' }}
            _focusVisible={{ boxShadow: 'outline' }}
          />
        ))}
      </HStack>
    </Box>
  );
};

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  // 视图、文章和语言都来自网址（siteRoutes.js）：每个页面有自己的地址，搜索引擎能收录，也能分享、后退。
  // 语言仍写进 localStorage：/privacy-policy 与 /terms 是独立路由，靠它沿用同一种语言。
  const location = useLocation();
  const navigate = useNavigate();
  const route = parsePath(location.pathname) || { lang: 'en', view: 'home', id: null };
  const { lang } = route;
  const view = route.view === 'help-article' ? 'help' : route.view; // 'home' 或任意 PLACEHOLDER_META 的 key，或 'blog-post'
  const activePost = route.view === 'blog-post' ? allPosts(lang).find((p) => p.id === route.id) || null : null;
  const go = (next, id, replace = false) => {
    navigate(pathFor(next, lang, id), { replace });
    window.scrollTo({ top: 0 });
  };
  const setLang = (next) => {
    writeLang(next);
    navigate(pathFor(route.view, next, route.id));
  };
  const [demoCaseId, setDemoCaseId] = useState('case1');
  const demo = useDisclosure();
  const mobileNav = useDisclosure();
  const c = landingContent[lang];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 进站时：旧的 #help / #pricing 直达链接换成对应网址；在 / 落地但上次选过中文的访客转到 /zh。
  // 查询串要带上：后台「以租户登录」落在 /?impersonate=…，由 TenantContext 在这之后读取。
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && PLACEHOLDER_META[hash]) navigate(pathFor(hash, lang) + location.search, { replace: true });
    else if (location.pathname === '/' && readLang() === 'zh') navigate('/zh' + location.search, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyHead(pageMeta(route.view, lang, route.id));
  }, [route.view, lang, route.id]);

  const scrollTo = (target) => {
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 语言切换交给 LangSwitch 组件（它直接调 setLang），这里不再需要 toggleLang
  const openDemo = (caseId = 'case1') => {
    setDemoCaseId(caseId);
    demo.onOpen();
  };

  // 页内视图切换：换网址，不卸载 Header、不刷新页面。
  const showView = (key) => go(key);
  const goHome = () => showView('home');

  // 打开文章详情
  const openPost = (post) => {
    if (!post) return;
    go('blog-post', post.id);
  };

  // 文章详情页在导航高亮里归入「资讯中心」
  const navView = view === 'blog-post' ? 'blog' : view;

  const navLinks = [
    { label: c.nav.home, key: 'home' },
    { label: c.nav.products, key: 'products' },
    { label: c.nav.cases, key: 'cases' },
    { label: c.nav.pricing, key: 'pricing' },
    { label: c.nav.resources, key: 'resources' },
    { label: c.nav.about, key: 'about' },
  ];

  // 语言切换与法律条款页共用同一个分段开关（LangSwitch），视觉完全一致。
  // 原来是「地球图标 + 另一种语言的名字」的单个按钮，看不出当前语言，也不好看。
  const langButton = <LangSwitch lang={lang} setLang={setLang} size="md" />;
  const contactButton = (
    <Button variant="outline" size="md" onClick={() => showView('contact')} fontWeight="medium" borderColor="blue.300" color="blue.600" _hover={{ bg: 'blue.50' }}>
      {c.nav.contact}
    </Button>
  );
  const openButton = (
    <Button as="a" href={PARTNER_PAGE_URL} target="_blank" rel="noopener noreferrer" variant="amber" size="md" fontWeight="bold">
      {c.nav.openAccount}
    </Button>
  );

  return (
    <Box bg="#F3FAFD" minH="100vh">
      <DemoModal isOpen={demo.isOpen} onClose={demo.onClose} title={c.demo.modalTitle} hint={c.demo.modalHint} caseId={demoCaseId} lang={lang} />

      {/* ============ Header (始终常驻，视图切换时不卸载) ============ */}
      <Box
        as="header"
        position="fixed"
        top={0}
        w="100%"
        zIndex={1200}
        bg={isScrolled ? 'rgba(255,255,255,0.92)' : 'white'}
        backdropFilter={isScrolled ? 'blur(16px)' : 'none'}
        borderBottom="1px solid"
        borderColor={isScrolled ? 'blue.100' : 'gray.200'}
        boxShadow={isScrolled ? '0 4px 20px rgba(11,61,92,0.08)' : 'none'}
        transition="all 0.25s ease"
      >
        <Container maxW="7xl">
          <Grid h="64px" alignItems="center" templateColumns="1fr auto 1fr" gap={{ base: 2, lg: 4 }}>
            {/* Left: logo (固定左对齐，宽度不随中间变化) */}
            <Box as="button" onClick={goHome} aria-label="Bizsprite home" justifySelf="start">
              <Text as="span" fontSize="26px" fontWeight="bold" color="black" lineHeight={1} letterSpacing="-0.01em">
                Bizsprite
              </Text>
            </Box>
            {/* Middle: nav links (居中，左右空间自动均分) */}
            <Flex align="center" gap={{ base: 1.5, lg: 3 }}>
              <HStack spacing={{ base: 2, lg: 4 }} display={{ base: 'none', lg: 'flex' }}>
                {navLinks.map((item) => {
                  const active = navView === item.key;

                  // 资源中心 → 二级下拉
                  if (item.key === 'resources') {
                    const subItems = [
                      { label: c.nav.help || (lang === 'zh' ? '帮助中心' : 'Help Center'), key: 'help' },
                      { label: c.nav.blog || (lang === 'zh' ? '资讯中心' : 'Insights'), key: 'blog' },
                    ];
                    return (
                      <Menu key={item.key} offset={[0, 8]}>
                        <MenuButton
                          as={Link}
                          fontWeight="medium"
                          fontSize="md"
                          color="gray.900"
                          _hover={{ color: 'blue.500' }}
                          _focus={{ outline: 'none', boxShadow: 'none' }}
                          _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                          _active={{ outline: 'none', boxShadow: 'none' }}
                          whiteSpace="nowrap"
                          px={1}
                          cursor="pointer"
                          sx={{ transition: 'color 0.2s ease' }}
                        >
                          <HStack spacing={1}>
                            <Text>{item.label}</Text>
                            <Icon as={FiChevronDown} boxSize={3.5} />
                          </HStack>
                        </MenuButton>
                        <MenuList
                          minW="180px"
                          py={2}
                          px={1}
                          shadow="xl"
                          borderColor="blue.100"
                          borderWidth="1px"
                          rounded="xl"
                          bg="white"
                          sx={{
                            animation: 'fadeInDown 0.18s ease-out',
                            '@keyframes fadeInDown': {
                              from: { opacity: 0, transform: 'translateY(-6px)' },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                          }}
                        >
                          {subItems.map((sub) => (
                            <MenuItem
                              key={sub.key}
                              onClick={() => showView(sub.key)}
                              _hover={{ bg: 'blue.50', color: 'blue.600' }}
                              _focus={{ bg: 'blue.50' }}
                              _focusVisible={{ outline: 'none' }}
                              fontSize="sm"
                              py={2.5}
                              px={3}
                              rounded="lg"
                              transition="all 0.15s ease"
                            >
                              {sub.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    );
                  }

                  return (
                    <Link
                      key={item.key}
                      as="button"
                      onClick={() => showView(item.key)}
                      fontWeight="medium"
                      fontSize="md"
                      color="gray.900"
                      _hover={{ color: 'blue.500' }}
                      _focus={{ outline: 'none', boxShadow: 'none' }}
                      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                      _active={{ outline: 'none', boxShadow: 'none' }}
                      whiteSpace="nowrap"
                      px={1}
                      className={active ? 'link-underline is-active' : 'link-underline'}
                      sx={{ transition: 'color 0.2s ease' }}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </HStack>
            </Flex>
            {/* Right: actions (固定右对齐，宽度不随中间变化) */}
            <Flex align="center" gap={{ base: 2, lg: 3 }} justifySelf="end">
              {langButton}
              <Box display={{ base: 'none', lg: 'block' }}>{contactButton}</Box>
              {openButton}
              <IconButton
                display={{ base: 'inline-flex', lg: 'none' }}
                aria-label="Open navigation menu"
                icon={<FiMenu />}
                variant="ghost"
                onClick={mobileNav.onOpen}
              />
            </Flex>
          </Grid>
        </Container>
      </Box>

      {/* ============ Mobile navigation drawer ============ */}
      <Drawer isOpen={mobileNav.isOpen} placement="right" onClose={mobileNav.onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor="gray.200" fontSize="md" color="gray.500">
            {lang === 'zh' ? '导航菜单' : 'Navigation'}
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={1} mt={2} mb={6}>
              {navLinks.map((item) => {
                // 资源中心 → 展开子项
                if (item.key === 'resources') {
                  const subItems = [
                    { label: c.nav.help || (lang === 'zh' ? '帮助中心' : 'Help Center'), key: 'help' },
                    { label: c.nav.blog || (lang === 'zh' ? '资讯中心' : 'Insights'), key: 'blog' },
                  ];
                  return (
                    <Box key={item.key}>
                      <Text px={2} py={2} fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
                        {item.label}
                      </Text>
                      {subItems.map((sub) => (
                        <Link
                          key={sub.key}
                          as="button"
                          onClick={() => {
                            mobileNav.onClose();
                            showView(sub.key);
                          }}
                          py={2.5}
                          px={4}
                          fontSize="md"
                          fontWeight={navView === sub.key ? 'bold' : 'medium'}
                          color={navView === sub.key ? 'blue.600' : 'gray.800'}
                          textAlign="left"
                          rounded="lg"
                          _hover={{ bg: 'blue.50', color: 'blue.600' }}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </Box>
                  );
                }
                return (
                  <Link
                    key={item.key}
                    as="button"
                    onClick={() => {
                      mobileNav.onClose();
                      showView(item.key);
                    }}
                    py={3}
                    px={2}
                    fontSize="md"
                    fontWeight={navView === item.key ? 'bold' : 'medium'}
                    color={navView === item.key ? 'blue.600' : 'gray.800'}
                    textAlign="left"
                    rounded="lg"
                    _hover={{ bg: 'blue.50', color: 'blue.600' }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </VStack>
            <Divider mb={6} />
            <VStack spacing={3}>
              <Button variant="outline" w="full" onClick={() => { mobileNav.onClose(); showView('contact'); }}>
                {c.nav.contact}
              </Button>
              <Button as="a" href={PARTNER_PAGE_URL} target="_blank" rel="noopener noreferrer" variant="amber" w="full">
                {c.nav.openAccount}
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* ============ 首页内容（view === 'home' 时显示） ============ */}
      {view === 'home' && (
      <>
      {/* ============ Hero ============ */}
      <Box id="hero" position="relative" overflow="hidden" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} className="gradient-mesh" sx={{ scrollMarginTop: '80px' }}>
        {/* animated gradient blobs */}
        <Box aria-hidden="true" position="absolute" w="480px" h="480px" rounded="full" bg="blue.300" opacity={0.18} filter="blur(80px)" top="-180px" right="-100px" className="animate-float" />
        <Box aria-hidden="true" position="absolute" w="380px" h="380px" rounded="full" bg="amber.300" opacity={0.14} filter="blur(70px)" bottom="-140px" left="-80px" className="animate-float-slow" sx={{ animationDelay: '-4s' }} />
        <Box aria-hidden="true" position="absolute" w="260px" h="260px" rounded="full" bg="blue.200" opacity={0.20} filter="blur(60px)" top="25%" left="8%" className="animate-float" sx={{ animationDelay: '-2s' }} />

        <Container maxW="7xl" position="relative" zIndex={1}>
          <Flex direction={{ base: 'column', lg: 'row' }} align="center" gap={{ base: 10, lg: 14 }} py={{ base: 6, md: 8 }}>
            <MotionBox flex="1" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" boxShadow="sm" fontSize="sm">
                {c.hero.badge}
              </Badge>
              {/* Chakra 的 Heading 默认是 h2，首页此前整页没有 h1 —— 这里显式指定 */}
              <Heading as="h1" mt={6} lineHeight={1.1}>
                <Text as="span" display="block" fontSize={{ base: 'xl', md: '2xl', xl: '3xl' }} color="gray.700" fontWeight="500" mb={2}>
                  {c.hero.titleLine1} <Text as="span" color="gray.900" fontWeight="800">{c.hero.titleBold1}</Text>
                </Text>
                <Text as="span" display="block" fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }} fontWeight="800" lineHeight={1.15} className="gradient-text">
                  {c.hero.titleLine2} {c.hero.titleBold2}
                </Text>
              </Heading>
              <Text mt={6} color="gray.800" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.8} maxW="2xl">
                {c.hero.subtitle}
              </Text>
              <HStack spacing={4} mt={9} wrap="wrap">
                {/* 单按钮（按新稿）：文案「了解解决方案｜体验 AI 服务」，沿用主按钮样式，跳到通话演示页。 */}
                <Button variant="primary" rightIcon={<FiArrowRight />} onClick={() => showView('cases')} className="animate-pulse-glow">
                  {c.hero.ctaPrimary}
                </Button>
              </HStack>
              <HStack spacing={{ base: 6, md: 9 }} mt={10} align="stretch" wrap="wrap">
                {c.hero.stats.map((s, idx) => (
                  <Stack key={s.label} spacing={0.5} align="flex-start">
                    <Flex align="center" gap={2}>
                      <Box w="8px" h="8px" rounded="full" bg={idx === 1 ? 'amber.500' : 'blue.500'} />
                      <Text fontWeight="extrabold" fontSize="xl" color="blue.600" lineHeight={1.2}>{s.value}</Text>
                    </Flex>
                    <Text fontWeight="medium" fontSize="sm" color="gray.700" pl={3}>{s.label}</Text>
                  </Stack>
                ))}
              </HStack>
            </MotionBox>

            {/* Hero carousel —— 四张卡轮播：开通流程 / 部署配置 / 价格 / 服务保障 */}
            <MotionBox
              flex="1"
              w="full"
              maxW="560px"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <HeroCarousel
                c={c.hero}
                p={c.pricing}
                onContactSales={() => showView('contact')}
                onViewPricing={() => showView('pricing')}
              />
            </MotionBox>
          </Flex>

          {/* ============ Value cards —— 收在 hero 的渐变带里，渐变不再在卡片上方断掉 ============ */}
          <Box pt={{ base: 7, md: 9 }}>
          {/* 通栏标题 —— 原来是 maxW="3xl" + mx="auto" 居中。
              在一个 1248px 宽的内容带里居中一个 768px 的块，两侧各留 240px 空白；
              再加上上面 hero 是左对齐、这里是居中，两套基准线并存，标题就成了孤岛。
              改为「左侧标题 + 右侧副标题」的通栏排布：基准线与 hero 对齐，横向宽度用满。
              lg 以下自动堆叠、副标题左对齐，仍是通栏。 */}
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            align={{ base: 'flex-start', lg: 'flex-end' }}
            justify="space-between"
            gap={{ base: 2, lg: 10 }}
          >
            <VStack align="flex-start" spacing={3}>
              <Badge colorScheme="blue" variant="subtle" px={4} py={1.5} rounded="full" fontWeight="semibold" fontSize="sm">
                {c.valueCards.eyebrow}
              </Badge>
              <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} lineHeight={1.35} color="gray.900">
                {c.valueCards.title}
              </Heading>
            </VStack>
            <Text fontSize="md" color="gray.700" lineHeight="1.8" maxW={{ base: 'full', lg: 'md' }} pb={{ base: 0, lg: 1 }}>
              {c.valueCards.subtitle}
            </Text>
          </Flex>
          {/* 四张卡：夜班漏接 / 重复咨询 / 售后资料 / 逐句纠错。
              纠错原本只在产品页当「尚未确认的能力」提了一句，现已确认，提到首页当卖点。
              列数从 md:3 改为 sm:2 lg:4，四张卡在桌面一行排开、平板两行。
              竖向节奏收紧：区块上内边距 40/56 → 28/36，标题到卡片 40/48 → 28/32，
              卡片内边距 28 → 24，卡片高度 245px → 约 215px。 */}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 5, lg: 6 }} mt={{ base: 7, md: 8 }}>
            {[
              { icon: FiMoon, tone: 'blue' },
              { icon: FiMessageSquare, tone: 'amber' },
              { icon: FiClipboard, tone: 'blue' },
              // 图标与产品后台里「纠错」按钮的铅笔一致
              { icon: FiEdit3, tone: 'amber' },
            ].map((card, i) => (
              <MotionBox
                key={c.valueCards.items[i].title}
                {...VALUE_CARD}
                p={{ base: 6, md: 6 }}
                whileHover={{ y: -4, boxShadow: '0 14px 30px rgba(11,61,92,0.09)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.32, delay: i * 0.08 }}
              >
                {/* 图标块 44px → 36px，图标 20px → 18px；底色改白，在淡色卡上才立得住 */}
                <Flex {...softIconBox(card.tone, 9)} bg="white" mb={4}>
                  <Icon as={card.icon} boxSize="18px" />
                </Flex>
                <Heading fontSize="lg" mb={2} color="gray.900" lineHeight={1.35}>{c.valueCards.items[i].title}</Heading>
                <Text fontSize="sm" color="gray.700" lineHeight="1.8">{c.valueCards.items[i].text}</Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Box>
        </Container>
      </Box>

      {/* ============ Home flow (touchpoints + map + SMS) ============ */}
      <Box id="home-flow" bg="white" py={{ base: 16, md: 22 }} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="7xl">
          <SectionHeader
            eyebrow={c.homeFlow.eyebrow}
            title={c.homeFlow.title}
            subtitle={c.homeFlow.subtitle}
          />

          {/* Top row: 3 sources + map + next-step callout */}
          <Flex direction={{ base: 'column', lg: 'row' }} gap={6} mt={12} align="stretch">
            {/* Left: 3 input sources */}
            <VStack flex="1" spacing={4} align="stretch" justify="center">
              {c.homeFlow.sources.map((source, i) => (
                <MotionBox
                  key={source.name}
                  p={5}
                  rounded="xl"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.35, delay: i * 0.1 }}
                  whileHover={{ x: 4 }}
                  sx={{ transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease' }}
                  _hover={{ boxShadow: 'md', borderColor: 'blue.300' }}
                >
                  <HStack spacing={3} align="flex-start">
                    <Flex w={9} h={9} rounded="full" bg="blue.500" color="white" align="center" justify="center" flexShrink={0} boxShadow="sm">
                      <Icon as={FiPhone} boxSize={4} />
                    </Flex>
                    <Box>
                      <Text fontWeight="bold" color="gray.900" fontSize="sm">{source.name}</Text>
                      <Text fontSize="xs" color="gray.600" mt={0.5}>{source.text}</Text>
                    </Box>
                  </HStack>
                </MotionBox>
              ))}
            </VStack>

            {/* Center: Map */}
            <MotionBox
              flex="1.2"
              position="relative"
              bg="blue.50"
              border="1px solid"
              borderColor="blue.100"
              rounded="2xl"
              p={{ base: 4, md: 6 }}
              overflow="hidden"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <Badge
                position="absolute"
                top={4}
                left="50%"
                transform="translateX(-50%)"
                colorScheme="blue"
                variant="solid"
                rounded="full"
                px={3}
                py={1}
                fontSize="xs"
                boxShadow="md"
                zIndex={2}
              >
                {c.homeFlow.mapBadge}
              </Badge>
              <Image src={NORTH_AMERICA_MAP_SRC} alt="North America coverage" w="100%" objectFit="contain" />
              {/* small inline callout (anchored to map, not floating below) */}
              <HStack
                position="absolute"
                bottom={4}
                right={4}
                bg="white"
                border="1px solid"
                borderColor="blue.200"
                rounded="full"
                px={3}
                py={1.5}
                boxShadow="md"
                spacing={2}
                zIndex={2}
                maxW="220px"
              >
                <Flex w={6} h={6} rounded="full" bg="blue.500" color="white" align="center" justify="center" flexShrink={0}>
                  <Icon as={FiPhone} boxSize={3} />
                </Flex>
                <Box>
                  <Text fontWeight="bold" color="gray.900" fontSize="xs" lineHeight={1.2}>
                    {c.homeFlow.mapCalloutTitle}
                  </Text>
                  <Text fontSize="2xs" color="gray.500" lineHeight={1.2} mt={0.5}>
                    {c.homeFlow.mapCalloutSub}
                  </Text>
                </Box>
              </HStack>
            </MotionBox>

            {/* Right: 2 stacked next-step cards (fills the column) */}
            <VStack flex="1" spacing={4} align="stretch" justify="center">
              <MotionBox
                p={5}
                rounded="xl"
                bg="white"
                border="1px solid"
                borderColor="blue.100"
                boxShadow="sm"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                whileHover={{ y: -3 }}
                sx={{ transition: 'box-shadow 0.25s ease' }}
                _hover={{ boxShadow: 'md' }}
              >
                <Flex
                  w={10}
                  h={10}
                  rounded="full"
                  bg="blue.500"
                  color="white"
                  align="center"
                  justify="center"
                  mb={4}
                  boxShadow="sm"
                >
                  <Icon as={FiMessageSquare} boxSize={4} />
                </Flex>
                <Text fontWeight="bold" color="gray.900" fontSize="md" mb={2} lineHeight={1.4}>
                  {c.homeFlow.nextStep1Title}
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight={1.7}>
                  {c.homeFlow.nextStep1Text}
                </Text>
              </MotionBox>

              <MotionBox
                p={5}
                rounded="xl"
                bg="white"
                border="1px solid"
                borderColor="amber.100"
                boxShadow="sm"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                whileHover={{ y: -3 }}
                sx={{ transition: 'box-shadow 0.25s ease' }}
                _hover={{ boxShadow: 'md' }}
              >
                <Flex
                  w={10}
                  h={10}
                  rounded="full"
                  bg="amber.500"
                  color="white"
                  align="center"
                  justify="center"
                  mb={4}
                  boxShadow="sm"
                >
                  <Icon as={FiSend} boxSize={4} />
                </Flex>
                <Text fontWeight="bold" color="gray.900" fontSize="md" mb={2} lineHeight={1.4}>
                  {c.homeFlow.nextStep2Title}
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight={1.7}>
                  {c.homeFlow.nextStep2Text}
                </Text>
              </MotionBox>
            </VStack>
          </Flex>

          {/* Bottom row: 2 SMS flow cards */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={10}>
            {[1, 2].map((n) => (
              <MotionBox
                key={n}
                p={6}
                bg="white"
                rounded="xl"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.3, delay: (n - 1) * 0.1 }}
                whileHover={{ y: -3 }}
                sx={{ transition: 'box-shadow 0.25s ease' }}
                _hover={{ boxShadow: 'md' }}
              >
                <Text fontSize="xs" fontWeight="bold" color="blue.600" textTransform="uppercase" letterSpacing="wider" mb={2}>
                  {c.homeFlow[`flow${n}Label`]}
                </Text>
                <Heading fontSize="md" color="gray.900" mb={3} lineHeight={1.5}>
                  {c.homeFlow[`flow${n}Title`]}
                </Heading>
                <Text fontSize="sm" color="gray.700" mb={4} lineHeight={1.7}>
                  {c.homeFlow[`flow${n}Sub`]}
                </Text>
                <HStack spacing={2} wrap="wrap">
                  {c.homeFlow[`flow${n}Steps`].map((step) => (
                    <Tag key={step} size="sm" rounded="full" colorScheme="blue" variant="subtle" fontWeight="semibold">
                      {step}
                    </Tag>
                  ))}
                </HStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ============ Product teaser (asymmetric) ============ */}
      <Box id="product" className="gradient-mesh" py={{ base: 16, md: 22 }} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="7xl">
          <SectionHeader
            eyebrow={c.homeTeaser.productEyebrow}
            title={c.homeTeaser.productTitle}
            subtitle={c.homeTeaser.productSubtitle}
          />
          <Flex direction={{ base: 'column', lg: 'row' }} gap={6} mt={12} align="stretch">
            {/* Featured card: Number + AI */}
            <MotionBox
              flex={{ base: '1', lg: '1.6' }}
              position="relative"
              overflow="hidden"
              p={{ base: 7, md: 9 }}
              rounded="2xl"
              bgGradient="linear(135deg, blue.600 0%, blue.800 100%)"
              color="white"
              boxShadow="2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -4 }}
              sx={{ transition: 'box-shadow 0.3s ease' }}
              _hover={{ boxShadow: '0 30px 60px rgba(41,171,226,0.35)' }}
            >
              <Box aria-hidden position="absolute" w="220px" h="220px" rounded="full" bg="amber.300" opacity={0.18} filter="blur(60px)" top="-50px" right="-30px" className="animate-float" />
              <Box aria-hidden position="absolute" w="200px" h="200px" rounded="full" bg="blue.300" opacity={0.22} filter="blur(50px)" bottom="-40px" left="-20px" className="animate-float-slow" />

              <Flex direction="column" h="full" position="relative" zIndex={1}>
                <HStack mb={5} spacing={3}>
                  <Flex w={14} h={14} rounded="full" bg="white" color="blue.700" align="center" justify="center" boxShadow="lg">
                    <Icon as={FiZap} boxSize={6} />
                  </Flex>
                  <Badge colorScheme="amber" variant="solid" rounded="full" px={3} fontWeight="bold" fontSize="xs">
                    {c.homeTeaser.productBadge}
                  </Badge>
                </HStack>
                <Heading fontSize={{ base: 'xl', md: '2xl' }} mb={3} lineHeight={1.25}>
                  {c.products.cards[1].name}
                </Heading>
                <Text fontSize="md" opacity={0.92} mb={6} maxW="lg" lineHeight={1.7}>
                  {c.products.cards[1].tagline}
                </Text>
                <VStack align="stretch" spacing={2.5} mb={6}>
                  {c.homeTeaser.aiFeatures.map((feat, i) => (
                    <HStack key={i} spacing={2.5} align="flex-start">
                      <Flex w={5} h={5} rounded="full" bg="amber.400" color="white" align="center" justify="center" flexShrink={0} mt="2px">
                        <Icon as={FiCheckCircle} boxSize={3} />
                      </Flex>
                      <Text fontSize="sm" lineHeight={1.6} opacity={0.96}>{feat}</Text>
                    </HStack>
                  ))}
                </VStack>
                <HStack spacing={3} mt="auto" wrap="wrap">
                  <Button variant="amber" rightIcon={<FiArrowRight />} onClick={() => showView('products')}>
                    {c.homeTeaser.standaloneCta}
                  </Button>
                  <Button
                    bg="whiteAlpha.200"
                    color="white"
                    border="1px solid"
                    borderColor="whiteAlpha.500"
                    fontWeight="semibold"
                    _hover={{ bg: 'whiteAlpha.300', borderColor: 'white', transform: 'translateY(-1px)' }}
                    leftIcon={<FiPlay />}
                    onClick={() => openDemo()}
                  >
                    {c.homeTeaser.standaloneCtaAlt}
                  </Button>
                </HStack>
              </Flex>
            </MotionBox>

            {/* Right column: Standalone Number + workflow callout */}
            <VStack flex="1" spacing={6} align="stretch">
              <MotionBox
                flex="1"
                p={6}
                rounded="2xl"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{ y: -3 }}
                sx={{ transition: 'box-shadow 0.3s ease' }}
                _hover={{ boxShadow: 'lg' }}
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex w="44px" h="44px" rounded="full" bg="blue.50" color="blue.600" align="center" justify="center">
                    <Icon as={FiPhone} boxSize={5} />
                  </Flex>
                  <Heading fontSize="md" color="gray.900">
                    {c.products.cards[0].name}
                  </Heading>
                </Flex>
                <Text fontSize="sm" color="gray.700" lineHeight={1.7} mb={4}>
                  {c.products.cards[0].tagline}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  rightIcon={<FiArrowRight />}
                  onClick={() => showView('products')}
                >
                  {c.products.cards[0].cta}
                </Button>
              </MotionBox>

              <MotionBox
                flex="1"
                p={6}
                rounded="2xl"
                bgGradient="linear(135deg, amber.50 0%, white 100%)"
                border="1px solid"
                borderColor="amber.100"
                boxShadow="md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ y: -3 }}
                sx={{ transition: 'box-shadow 0.3s ease' }}
                _hover={{ boxShadow: 'lg' }}
              >
                <Flex align="center" gap={2} mb={4}>
                  <Flex w="44px" h="44px" rounded="full" bg="amber.500" color="white" align="center" justify="center">
                    <Icon as={FiZap} boxSize={5} />
                  </Flex>
                  <Heading fontSize="md" color="gray.900">
                    {c.homeTeaser.flowTitle}
                  </Heading>
                </Flex>
                {/* 4 步之间插箭头：原来 4 个方块并排，看不出是「先后顺序」 */}
                <Flex align="center" gap={1} mb={3}>
                  {[
                    { icon: FiPhone, color: 'blue' },
                    { icon: FiMessageSquare, color: 'amber' },
                    { icon: FiClipboard, color: 'blue' },
                    { icon: FiSettings, color: 'amber' },
                  ].map((step, i) => (
                    <React.Fragment key={i}>
                      <VStack flex="1" minW={0} spacing={1.5} p={2} rounded="lg" bg="white" border="1px solid" borderColor="gray.200">
                        <Flex w={7} h={7} rounded="full" bg={step.color === 'amber' ? 'amber.500' : 'blue.500'} color="white" align="center" justify="center">
                          <Icon as={step.icon} boxSize={3.5} />
                        </Flex>
                        <Text fontSize="xs" fontWeight="bold" color="gray.700">
                          {c.homeTeaser.flowSteps[i]}
                        </Text>
                      </VStack>
                      {i < 3 && (
                        <Icon as={FiChevronRight} boxSize={4} color="gray.400" flexShrink={0} aria-hidden="true" />
                      )}
                    </React.Fragment>
                  ))}
                </Flex>
                <Text fontSize="xs" color="gray.600" lineHeight={1.6}>
                  {c.homeTeaser.flowNote}
                </Text>
              </MotionBox>
            </VStack>
          </Flex>
        </Container>
      </Box>

      {/* ============ Cases preview (3 cards) — 点卡即播对应 demo，不再跳页 ============ */}
      <Box id="cases-preview" bg="white" py={{ base: 16, md: 22 }} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="7xl">
          <SectionHeader
            eyebrow={c.homeTeaser.casesEyebrow}
            title={c.homeTeaser.casesTitle}
            subtitle={c.homeTeaser.casesSubtitle}
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={12}>
            {c.scenarios.items.map((item, i) => {
              // 这三张卡各自对应「通话演示」里的一通真实通话，点开就地播放
              const demoCase = c.demo.cases.find((d) => d.id === item.demoId);
              const accent = (demoCase && demoCase.accent) || 'blue.500';
              const accentKey = accent.split('.')[0];
              const wave = WAVE_HEIGHTS[i % WAVE_HEIGHTS.length];
              const played = Math.floor(wave.length * 0.35);
              return (
                <MotionBox
                  key={item.name}
                  as="button"
                  type="button"
                  textAlign="left"
                  onClick={() => openDemo(item.demoId)}
                  p={6}
                  rounded="2xl"
                  bg="white"
                  border="1px solid"
                  borderColor="blue.100"
                  boxShadow="sm"
                  position="relative"
                  overflow="hidden"
                  cursor="pointer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.3, delay: i * 0.08 }}
                  whileHover={{ y: -4 }}
                  sx={{ transition: 'box-shadow 0.25s ease, border-color 0.25s ease' }}
                  _hover={{ borderColor: `${accentKey}.300`, boxShadow: 'lg' }}
                  _focusVisible={{ outline: 'none', boxShadow: '0 0 0 3px rgba(41,171,226,0.35)' }}
                >
                  {/* 顶部主题色细条 —— 与通话演示页的强调色同源 */}
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bg={accent} />

                  <Flex justify="space-between" align="center" mb={5}>
                    <Flex w="44px" h="44px" rounded="xl" bg={`${accentKey}.50`} color={accent} align="center" justify="center">
                      <Icon as={SCENARIO_ICONS[item.icon] || FiSettings} boxSize={5} />
                    </Flex>
                    <Text fontSize="xs" fontWeight="bold" color={accent} letterSpacing="0.1em" textTransform="uppercase">
                      {c.demo.caseLabel} {String(i + 1).padStart(2, '0')}
                    </Text>
                  </Flex>

                  <Heading fontSize="md" color="gray.900" mb={3} lineHeight={1.4}>{item.name}</Heading>
                  <Text fontSize="sm" color="gray.700" lineHeight={1.7} mb={5}>{item.problem}</Text>

                  {/* 迷你声波 + 播放提示：跟通话演示页同一套视觉语言 */}
                  <Flex align="center" gap={1} h="26px" mb={4} aria-hidden="true">
                    {wave.slice(0, 22).map((hh, k) => (
                      <Box
                        key={k}
                        className="wave-live-bar"
                        w="3px"
                        flex="1"
                        maxW="4px"
                        bg={k < played ? accent : 'gray.200'}
                        rounded="full"
                        h={`${hh}%`}
                        sx={{ animationDelay: `${(k % 10) * 0.09}s` }}
                      />
                    ))}
                  </Flex>

                  <HStack spacing={2} color={accent} fontWeight="semibold" fontSize="sm">
                    <Icon as={FiPlay} boxSize="13px" />
                    <Text>{c.homeTeaser.caseLink}</Text>
                    <Icon as={FiArrowRight} boxSize="13px" />
                  </HStack>
                </MotionBox>
              );
            })}
          </SimpleGrid>
          <Flex justify="center" mt={10} gap={4} wrap="wrap">
            <Button variant="outline" rightIcon={<FiArrowRight />} onClick={() => showView('cases')}>
              {c.homeTeaser.casesMoreCta}
            </Button>
            <VoiceTrialButton lang={lang} />
          </Flex>
        </Container>
      </Box>

      {/* ============ FAQ ============ */}
      <Box id="faq" bg="white" py={{ base: 16, md: 22 }} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="6xl">
          <SectionHeader title={c.faq.title} subtitle={c.faq.subtitle} />
          <Accordion allowMultiple mt={10} border="none">
            {c.faq.items.map((item) => (
              <AccordionItem key={item.q} border="1px solid" borderColor="gray.300" rounded="lg" mb={4} overflow="hidden">
                <h3>
                  <AccordionButton px={5} py={4} _expanded={{ bg: 'blue.50' }} transition="background 0.2s ease">
                    <Box flex="1" textAlign="left" fontWeight="semibold" color="gray.900">{item.q}</Box>
                    <AccordionIcon color="blue.500" />
                  </AccordionButton>
                </h3>
                <AccordionPanel pb={4} bg="gray.50" color="gray.800" lineHeight={1.8} fontSize="sm">
                  {item.a}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* ============ Contact ============ */}
      <Box id="contact" className="gradient-mesh-dark" color="white" py={{ base: 16, md: 20 }} sx={{ scrollMarginTop: '80px' }}>
        <Container maxW="5xl">
          <VStack spacing={6} textAlign="center">
            <Heading fontSize={{ base: '2xl', md: '3xl' }} lineHeight={1.3}>{c.contact.title}</Heading>
            <Text fontSize="lg" opacity={0.9}>{c.contact.subtitle}</Text>
            {/* 「立即开通」直接跳合作方开通页，跟顶部导航那颗保持一致 */}
            <Button
              as="a"
              href={PARTNER_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              bg="white"
              color="brand.navy"
              fontWeight="bold"
              px={10}
              rounded="full"
              boxShadow="0 12px 30px rgba(0,0,0,0.22)"
              _hover={{ bg: 'blue.50', transform: 'translateY(-2px)', boxShadow: '0 16px 36px rgba(0,0,0,0.26)' }}
              _active={{ transform: 'translateY(0)' }}
            >
              {c.contact.cta}
            </Button>
            <Divider borderColor="whiteAlpha.400" />
            <Stack spacing={3} fontSize="md" align="flex-start" mx="auto" w="fit-content" textAlign="left">
              <HStack spacing={3}>
                <Icon as={FiMail} boxSize={5} flexShrink={0} />
                <Link href={`mailto:${c.contact.email}`} color="white" _hover={{ textDecoration: 'underline' }}>{c.contact.email}</Link>
              </HStack>
              <HStack spacing={3}>
                <Icon as={FiPhone} boxSize={5} flexShrink={0} />
                <Text>{c.contact.phone}</Text>
              </HStack>
              <HStack spacing={3}>
                <Icon as={FiMapPin} boxSize={5} flexShrink={0} />
                <Text>{c.contact.location}</Text>
              </HStack>
            </Stack>
          </VStack>
        </Container>
      </Box>

      </>
      )}

      {/* ============ 定价视图 ============ */}
      {view === 'pricing' && (
        <PricingView lang={lang} onContactSales={() => showView('contact')} onHelp={() => showView('help')} />
      )}

      {/* ============ 关于我们视图 ============ */}
      {view === 'about' && (
        <AboutView lang={lang} onCta={() => showView('products')} />
      )}

      {/* ============ 资讯中心视图 ============ */}
      {view === 'blog' && (
        <BlogView lang={lang} onCta={() => showView('contact')} onOpenPost={openPost} />
      )}

      {view === 'blog-post' && (
        <BlogPostView
          lang={lang}
          post={activePost}
          onBack={() => showView('blog')}
          onOpenPost={openPost}
          onCta={() => showView('contact')}
        />
      )}

      {/* ============ 产品视图 ============ */}
      {view === 'products' && (
        <ProductsView lang={lang} onCases={() => showView('cases')} onPricing={() => showView('pricing')} />
      )}

      {/* ============ 通话演示视图 ============ */}
      {view === 'cases' && (
        <CasesView lang={lang} onContact={() => showView('contact')} openDemo={openDemo} />
      )}

      {/* ============ 帮助中心视图 ============ */}
      {view === 'help' && (
        <HelpCenterView
          lang={lang}
          onPricing={() => showView('pricing')}
          articleId={route.view === 'help-article' ? route.id : null}
          onArticle={(id) => navigate(pathFor(id ? 'help-article' : 'help', lang, id))}
        />
      )}

      {/* ============ 联系我们视图 ============ */}
      {view === 'contact' && <LeadPage lang={lang} />}

      {/* ============ 占位视图（其他未开发栏目） ============ */}
      {view !== 'home' && view !== 'pricing' && view !== 'about' && view !== 'products' && view !== 'cases' && view !== 'blog' && view !== 'blog-post' && view !== 'help' && view !== 'contact' && (
        <Container maxW="md" pt="64px">
          <Flex
            as="main"
            minH="calc(100vh - 64px)"
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            gap={5}
            py={16}
          >
            <Heading as="h1" fontSize={{ base: '3xl', md: '4xl' }} color="gray.900">
              {PLACEHOLDER_META[view].zh}
              <Text as="span" color="blue.600"> · {PLACEHOLDER_META[view].en}</Text>
            </Heading>
            <Divider w="16" borderColor="blue.200" />
            <Text color="gray.600" fontSize="md">页面建设中，敬请期待</Text>
            <Text color="gray.500" fontSize="sm">This page is under construction. Coming soon.</Text>
            <Button variant="outline" size="sm" mt={4} leftIcon={<FiArrowRight />} onClick={goHome}>
              {lang === 'zh' ? '返回首页' : 'Back to home'}
            </Button>
          </Flex>
        </Container>
      )}

      {/* ============ Footer（所有视图共用） ============ */}
      <Box bg="gray.900" color="gray.600" py={16}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={10} mb={10}>
            <Stack spacing={4}>
              <Box bg="white" borderRadius="md" px={3} py={2} w="fit-content">
                <Text as="span" display="block" fontSize="24px" fontWeight="bold" color="black" lineHeight={1} letterSpacing="-0.01em">
                  Bizsprite
                </Text>
              </Box>
              <Text color="gray.500" fontSize="sm" lineHeight={1.8}>{c.footer.tagline}</Text>
            </Stack>
            <Stack spacing={3}>
              <Text fontWeight="semibold" color="white">{c.footer.company}</Text>
              <Link _hover={{ color: 'white' }} onClick={() => showView('about')}>{c.footer.about}</Link>
            </Stack>
            <Stack spacing={3}>
              <Text fontWeight="semibold" color="white">{c.footer.support}</Text>
              {/* 与导航栏「帮助中心」「联系我们」落到完全相同的视图 */}
              <Link _hover={{ color: 'white' }} onClick={() => showView('help')}>{c.footer.help}</Link>
              <Link _hover={{ color: 'white' }} onClick={() => showView('contact')}>{c.footer.contactUs}</Link>
              <Link as={RouterLink} to="/privacy-policy" _hover={{ color: 'white' }}>{c.footer.privacy}</Link>
              <Link as={RouterLink} to="/terms" _hover={{ color: 'white' }}>{c.footer.terms}</Link>
            </Stack>
          </SimpleGrid>
          <Divider borderColor="gray.700" mb={6} />
          <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
            <Stack spacing={1}>
              <Text>{c.footer.rights}</Text>
              <Text fontSize="xs" color="gray.600">{c.footer.powered}</Text>
            </Stack>
            <HStack spacing={6}>
              <Link href="mailto:support@bizsprite.com" _hover={{ color: 'white' }} color="gray.500" fontSize="sm">support@bizsprite.com</Link>
            </HStack>
          </Flex>
          <Text mt={8} pt={6} borderTop="1px solid" borderColor="gray.800" maxW="6xl" fontSize="xs" lineHeight={1.8} color="gray.600">
            {c.footer.disclaimer}
          </Text>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
